import React, { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { lensStatus, bandOf } from './verdictModel';
import { prepareChartData } from './prepareChartData';
import { startColumns, grainBoundaries } from './gridPlacement';
import { createCustomData } from './useSunburstChart';
import NodePanel from './NodePanel';
import RollupPanel from './RollupPanel';
import SynthesisPanel from './SynthesisPanel';
import AxisInspector from './AxisInspector';
import BetaFeedback from './BetaFeedback';
import Thumbs, { thumbKey } from './Thumbs';
import ShapeTab from './FieldStanding';
import AxisChat, { chatKeyOf } from './AxisChat';
import TableView from './TableView';
import SunburstLegend from './SunburstLegend';
import { METRIC_LABELS, fmtScore, SCORE_SCALE, softFill, scoreHex, LENSES, LENS_HEADLINE } from './Constants';
import { unitDisplayLabel, unitSortKey, sequenceCellLabel, sequenceCellCode } from './unitLabels';
import { grainTitle } from './grainNouns';
import { buildIcicleTour, shouldAutoStartIcicleTour } from './icicleTour';
import { WikiDrawer } from './InfoUI';
import HowItWorksVideo from './HowItWorksVideo';
import { wikiArticle } from './wikiContent';
import { tip } from './Tip';
import { parseLrTarget, resolveLrUnit } from './deepLink';

// Icicle / "unfurled sunburst" — the same DER nodes as the wheel, as 4 stacked rows (Script · Acts ·
// Sequences · Scenes). A shared CSS-GRID of (total-scene) columns makes every row align exactly: each
// unit spans its scene count, so a scene sits directly under its sequence under its act. Strong vertical
// rules mark act boundaries (down every row), medium ones mark sequence boundaries. Standalone (/icicle).

const NAVY = '#14536e';
const INK = '#0f172a';

// Interaction tracking (ANALYSIS_INTERACTION_TRACKING_SPEC): semantic events for the timeline.
// window.srTrack is provided by partials/tracking/sr_track.blade.php (dual-emits to Mixpanel +
// batches to POST /track → user_actions); no-op when the partial isn't on the page.
// NEW-FEATURE RULE: every new interactive control added to this page gets either a track() call
// (semantic, preferred) or lives inside the data-sr-group container (generic click coverage).
const track = (action, metadata) => { try { window.srTrack?.(action, 'icicle', metadata); } catch (e) { /* tracking must never break the UI */ } };
const ZOOM_MS = 340;   // zoom tween; long enough to read the motion, short enough not to wait on it
const LABEL_W = 84;    // layer-name tab on the left of each timeline row; the row starts flush against it
const SLIVER_PX = 26;
const ASK_TEAL = '#14b8a6';   // the site-wide Ask AI colour (.btn-ai-chat); chat marks share it
// "You have a conversation here" — a small teal speech bubble next to an axis in the Axes list.
const ChatMark = ({ size = 16 }) => (
  <span role="img" aria-label="You have a conversation here" title="You have a conversation here"
    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: 999,
      background: ASK_TEAL, color: '#fff', flexShrink: 0 }}>
    <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 4v-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /></svg>
  </span>
);  // width of a squeezed act/sequence while another is zoomed — room for its name on its side
// Magnifier with + or −: the zoom glyph every map and image viewer uses (owner, 2026-10-14: "⤢ Scenes"
// named the result, not the action, and nobody read it as zoom).
// ── The "Layered Read" wordmark (owner, 2026-10-14: "LR needs to stand out in some way so people
// remember it as the tool name … Heathers is as important if not more"). It stands out by FACE, not
// size: Instrument Serif italic (the face the guides page already uses) against the all-sans UI, plus a
// tiny copy of the map (one bar, two, four, in the triage colours) that says what "layered" means.
// The script title stays the bold heading beside it.
const LR_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@1&display=swap';
function useWordmarkFont() {
  useEffect(() => {
    if (typeof document === 'undefined' || document.querySelector(`link[href="${LR_FONT_HREF}"]`)) return;
    const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = LR_FONT_HREF; document.head.appendChild(l);
  }, []);
}
const LR_MARK_ROWS = [['#22c55e'], ['#22c55e', '#eab308'], ['#22c55e', '#f97316', '#22c55e', '#22c55e']];
const LayeredReadMark = ({ w = 26 }) => (
  <span aria-hidden="true" style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, width: w, flexShrink: 0 }}>
    {LR_MARK_ROWS.map((row, i) => (
      <span key={i} style={{ display: 'flex', gap: 2, height: 4 }}>
        {row.map((c, j) => <span key={j} style={{ flex: 1, background: c, borderRadius: 1.5 }} />)}
      </span>
    ))}
  </span>
);
const LayeredReadWordmark = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', flexShrink: 0 }}>
    <LayeredReadMark />
    <span style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontStyle: 'italic', fontWeight: 400, fontSize: 25, lineHeight: 1, color: '#0f766e', letterSpacing: '.005em' }}>Layered Read</span>
  </span>
);

const MagIcon = ({ minus = false }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
    <circle cx="6.5" cy="6.5" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M10.4 10.4 L14.8 14.8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M4 6.5h5" stroke="currentColor" strokeWidth="1.8" />
    {!minus && <path d="M6.5 4v5" stroke="currentColor" strokeWidth="1.8" />}
  </svg>
);
const AXES = ['contract', 'pressure', 'development', 'movement', 'consequence', 'economy', 'assembly', 'clarity', 'freshness', 'unpredictability', 'charge', 'grip'];
// ⚠️ THE KEY AND THE HEIGHT ARE STRUCTURE; THE LABEL IS COPY, AND COPY IS PER-FORMAT.
// `label` is gone from this table on purpose — it used to say 'Sequences' and 'Scenes' for every
// script, including a stage play whose middle grain IS the playwright's printed scene and whose
// bottom grain is a unit the splitter cut. The row order, the grain keys and the pixel heights are
// the same for every format, so they stay here; the noun comes from grainNouns() per render.
const GRAINS = [
  { key: 'movie', h: 46 },
  { key: 'act', h: 40 },
  { key: 'sequence', h: 34 },
  { key: 'scene', h: 30 },
];

// The score scale and its remap now live in Constants.js so the landing-page snapshot gets them
// too — it was rendering the raw pale gradeColors ramp, i.e. exactly the problem this scale was
// written to fix (2026-09-01). `s10` keeps its local name; it is `scoreHex` upstream.
const s10 = scoreHex;
// metric 'de' = the two-channel read. The wheel encoded it border/fill (subtle); the timeline can
// SPLIT the cell — top half Design, bottom half Execution — which reads at a glance.
const deFill = (dc) => (dc && typeof dc.design === 'number' && typeof dc.execution === 'number')
  ? `linear-gradient(180deg, ${s10(dc.design)} 0%, ${s10(dc.design)} 50%, ${s10(dc.execution)} 50%, ${s10(dc.execution)} 100%)`
  : null;

// Pick the higher-contrast text colour for a fill (white wins on the deep reds, ink on the light/greens)
// — keeps the fills RED while making the numbers legible (black on #E11D48 is only 3.8:1).
function textOn(bg) {
  if (!bg || bg[0] !== '#' || bg.length < 7) return INK;
  const lin = (h) => { const c = parseInt(h, 16) / 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  const L = 0.2126 * lin(bg.slice(1, 3)) + 0.7152 * lin(bg.slice(3, 5)) + 0.0722 * lin(bg.slice(5, 7));
  return (1.05 / (L + 0.05)) >= ((L + 0.05) / 0.05) ? '#fff' : INK;   // white-vs-bg  ≥  black-vs-bg
}

// Axes overview — the list side of the Axes tab (master-detail INSIDE the pane, like the axis list
// in a unit's reading). Full-width mode = the headline sentences; when an axis is selected the list
// COMPACTS (chip + name) so hopping between axes is one click and the inspector owns the right side.
// In addition to (not instead of) the Colour-by dropdown.
//
// ── GROUPED BY LENS, NOT RANKED BY SCORE (owner, 2026-09-04) ─────────────────────────────────────
// This list used to be twelve flat rows `.sort((a,b) => a.score - b.score)` — worst first. Two
// problems, and the second is the serious one:
//
//   1. It taught the wrong shape. The framework is three lenses over twelve axes, and the one
//      screen devoted to the axes rendered them as an undifferentiated list of twelve, so a reader
//      met the parts without ever meeting the structure. Design/Execution/Read appeared only as
//      <optgroup> labels inside the Colour-by dropdown.
//   2. ⚠️ THE RANKING ITSELF IS NOT DEFENSIBLE. CODEBASE_MAP §13.14: axis raw scores are not on a
//      common scale (mean pairwise r 0.63-0.80; one cell's bootstrap CI exceeds the whole map's
//      range), so ordering all twelve against each other implies a precision the instrument does
//      not have. On Amelie the whole spread was 6→9 across twelve axes, six of them tied at 7.
//
// So: canonical order within a lens, lenses in framework order, no cross-axis sort anywhere. The
// question the sort was trying to answer — "where do I start?" — is answered properly by the START
// HERE card above the list, which uses `axis_priority` (a material owed-weak pattern), a criterion
// that has nothing to do with raw rank. That mismatch is exactly why the badge used to look
// arbitrary: it sat seventh in a score-sorted list, on an 8, below six lower scores.
function AxesOverview({ movieNode, inspectors, onOpenAxis, selected, onOpenWiki, priorityAxis = null, priorityBasis = null, chatted = null, view = 'timeline' }) {
  const compact = !!selected;
  const scoreChip = (sc) => {
    if (sc == null) return { bg: '#eef2f5', fg: '#8fa3ad' };
    const bg = SCORE_SCALE[Math.max(0, Math.min(9, Math.round(sc) - 1))];
    return { bg, fg: textOn(bg) };
  };
  const rowFor = (ax) => {
    const insp = inspectors?.[ax] || null;
    const qr = insp?.merged?.quick_read || {};
    const nodeScore = movieNode?.detailedContent?.scores?.[ax]?.score;
    const score = qr.score != null ? +qr.score : (nodeScore != null ? +nodeScore : null);
    return { ax, score, headline: qr.hover_summary || null, has: !!insp };
  };
  const lensScoreOf = (k) => {
    const v = movieNode?.detailedContent?.scores?.[k]?.score;
    return v == null ? null : +v;
  };
  // `axis_priority.basis` is a machine string like "material_pattern:severity=broken,
  // lens_order_tiebreak". Render only the part we can state honestly — the severity — and fall
  // back to the generic sentence rather than inventing a reason from a token we don't recognise.
  const prioritySeverity = (() => {
    const m = /severity=([a-z_]+)/.exec(priorityBasis || '');
    return m ? m[1] : null;
  })();
  const PRIORITY_WHY = {
    broken: 'a broken pattern shows up here',
    soft: 'a soft pattern shows up here',
    weak: 'a weak pattern shows up here',
  };
  const priorityLens = priorityAxis ? LENSES.find(L => L.axes.includes(priorityAxis)) : null;
  return (
    <div style={{ padding: compact ? '.85rem .6rem 1rem' : '1.1rem 1.15rem 1.3rem' }}>
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ color: NAVY, fontWeight: 800, fontSize: '1.2rem', margin: 0 }}>The 12 axes</h3>
          <span style={{ color: '#5b7a8c', fontSize: '.85rem' }}>{view === 'table' ? 'click one to see what it found' : `click one to see what it found and colour the ${view === 'list' ? 'list' : 'map'} by it`}</span>
          {onOpenWiki && (
            <button type="button" onClick={onOpenWiki}
              style={{ all: 'unset', cursor: 'pointer', marginLeft: 'auto', color: NAVY, fontWeight: 600, fontSize: '.85rem', textDecoration: 'underline', textUnderlineOffset: 3 }}>
              ⓘ how we read these
            </button>
          )}
        </div>
      )}
      {/* START HERE, promoted out of the list. It answers "where do I begin?", which is what the
          old score-sort was pretending to answer — and it says WHY, because the basis is a material
          owed-weak pattern rather than a low number, and a badge with no stated reason on a
          middling score reads as noise. */}
      {!compact && priorityAxis && (
        <button type="button" onClick={() => onOpenAxis(priorityAxis)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', cursor: 'pointer',
            marginTop: '.9rem', padding: '.6rem .8rem', borderRadius: 10,
            border: '1px solid #cddbe4', borderLeft: `4px solid ${NAVY}`, background: '#f2f7fa' }}>
          <span style={{ flexShrink: 0, fontSize: '.64rem', fontWeight: 800, letterSpacing: '.03em',
            color: '#fff', background: NAVY, borderRadius: 999, padding: '.12rem .5rem' }}>START HERE</span>
          <span style={{ flex: 1, minWidth: 0, color: '#16242c', fontSize: '.95rem', lineHeight: 1.4 }}>
            <strong style={{ fontWeight: 700 }}>{METRIC_LABELS[priorityAxis] || priorityAxis}</strong>
            {priorityLens ? <span style={{ color: '#5b7a8c' }}> · {priorityLens.label}</span> : null}
            {' — '}
            {(prioritySeverity && PRIORITY_WHY[prioritySeverity]) || 'a materially owed-weak pattern'}.
          </span>
          <span style={{ color: NAVY, fontWeight: 700, flexShrink: 0 }}>›</span>
        </button>
      )}
      <div style={{ marginTop: compact ? 0 : '.8rem' }}>
        {LENSES.map(L => {
        const lensSc = lensScoreOf(L.key);
        const rows = L.axes.map(rowFor);
        return (
        <div key={L.key} style={{ marginBottom: compact ? '.5rem' : '.9rem' }}>
          {/* ⚠️ THE LENS HAS TO DOMINATE — this is a TEACHING surface (owner, 2026-09-04).
              A first pass set the lens to 1.02rem against the axis names' .95rem and its score pill
              to .86rem against the axis chips' .95rem. That is not a hierarchy: a 0.07rem edge on
              the name is invisible, and the lens SCORE was still smaller than the twelve axis scores
              underneath it — the same parent-quieter-than-child inversion this pass exists to kill,
              reintroduced one screen over.
              Now: name 1.45rem, score 1.3rem in a solid pill, and the plain-English verdict beside
              them. Axis names drop to .9rem and their chips to .85rem. The gap has to be obvious at
              a glance, because "there are three of these, and twelve of those underneath" is the
              single thing this screen is for. */}
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 10, margin: compact ? '.5rem 0 .35rem' : '1.1rem 0 .5rem' }}>
            <span style={{ color: '#0f172a', fontWeight: 700, fontSize: compact ? '1.05rem' : '1.45rem', letterSpacing: '-.02em', lineHeight: 1.1 }}>{L.label}</span>
            {lensSc != null && (
              <span style={{ fontWeight: 800, fontSize: compact ? '.9rem' : '1.3rem', borderRadius: 999,
                padding: compact ? '.06rem .5rem' : '.1rem .7rem', lineHeight: 1.25,
                background: scoreChip(lensSc).bg, color: scoreChip(lensSc).fg }}>{fmtScore(lensSc)}</span>
            )}
            {/* The verdict, not just the number — a reader has no scale for "7.6" but knows what
                "Built and working" means. This is the same string the per-unit rail prints. */}
            {!compact && lensSc != null && LENS_HEADLINE[L.key]?.[lensStatus(lensSc)] && (
              <span style={{ color: '#5b7a8c', fontSize: '1rem', fontWeight: 600 }}>
                {LENS_HEADLINE[L.key][lensStatus(lensSc)]}
              </span>
            )}
            <span style={{ flex: 1, minWidth: 20, height: 2, background: '#e7edf1', borderRadius: 2 }} />
          </div>
          {rows.map(r => {
          const chip = scoreChip(r.score);
          const isSel = selected === r.ax;
          return (
            <button key={r.ax} type="button" onClick={() => onOpenAxis(r.ax)}
              style={{ display: 'flex', alignItems: 'center', gap: compact ? 8 : 12, width: '100%', textAlign: 'left', cursor: 'pointer',
                background: isSel ? '#eef4f8' : '#fff', border: '1px solid #e7edf1', borderRadius: 8,
                boxShadow: isSel ? `inset 3px 0 0 ${NAVY}` : 'none',
                padding: compact ? '.45rem .55rem' : '.55rem .75rem', marginBottom: 6 }}>
              <span style={{ width: compact ? 32 : 34, flexShrink: 0, textAlign: 'center', fontWeight: 800, fontSize: compact ? '.8rem' : '.85rem',
                background: chip.bg, color: chip.fg, borderRadius: 6, padding: '3px 0' }}>
                {r.score != null ? fmtScore(r.score) : '—'}
              </span>
              {/* longhand only — mixing the `flex` shorthand with `flexShrink` makes React warn on
                  every re-render once `compact` flips, and the shorthand silently resets the other
                  two sub-properties. */}
              <span style={{ flexGrow: compact ? 1 : 0, flexBasis: 'auto', flexShrink: 0,
                width: compact ? 'auto' : 126, fontWeight: 600,
                color: isSel ? NAVY : '#33454f', fontSize: compact ? '.84rem' : '.9rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {METRIC_LABELS[r.ax] || r.ax}{chatted && chatted.has(r.ax) && <ChatMark size={15} />}</span>
              {/* Only in COMPACT mode. In full mode the lead card above the list already carries
                  this badge and states its reason, and two identical navy pills a few rows apart
                  read as two different claims. Compact mode has no lead card, so the row keeps it. */}
              {compact && r.ax === priorityAxis && (
                <span {...tip('A materially owed-weak pattern — a C8-safe place to start')} style={{ flexShrink: 0, fontSize: '.64rem', fontWeight: 800, letterSpacing: '.03em', color: '#fff', background: NAVY, borderRadius: 999, padding: '.08rem .5rem' }}>START HERE</span>
              )}
              {!compact && (
                <>
                  <span style={{ flex: 1, minWidth: 0, color: r.headline ? '#33454f' : '#94a3b8', fontSize: '.88rem', lineHeight: 1.35,
                    fontStyle: r.headline ? 'normal' : 'italic' }}>
                    {r.headline || 'No inspector yet — still colours the timeline.'}
                  </span>
                  <span style={{ color: NAVY, fontWeight: 700, flexShrink: 0 }}>›</span>
                </>
              )}
            </button>
          );
          })}
        </div>
        );
        })}
      </div>
    </div>
  );
}
AxesOverview.propTypes = { chatted: PropTypes.object, movieNode: PropTypes.object, inspectors: PropTypes.object, onOpenAxis: PropTypes.func, selected: PropTypes.string, onOpenWiki: PropTypes.func, priorityAxis: PropTypes.string, priorityBasis: PropTypes.string };

export default function Icicle({ movie, embedded = false, publicView = false, initialMetric = null, initialSelection = null, initialTarget = null, listenNavigate = false }) {
  useWordmarkFont();
  // initialMetric lets embedders (e.g. the LP compact demo) preset Colour-by to a specific axis
  // like 'pressure' so the visitor lands on the intended demonstration. Falls back to 'overall'
  // (Triage) — the standalone /icicle page never sets it.
  const [metric, setMetric] = useState(() => {
    if (initialMetric && (initialMetric === 'overall' || initialMetric === 'design' || initialMetric === 'execution' || initialMetric === 'read' || AXES.includes(initialMetric))) {
      return initialMetric;
    }
    return 'overall';
  });
  // initialSelection: { grain, label } — an LP-demo preselect. Resolved to a real cell in
  // an effect below and openNode()'d once on mount so the reading pane lands on a
  // deliberately chosen unit instead of the generic Findings fallback.
  // Landing: ORIENTATION on the very first visit only (the tour's stage); every visit after lands
  // straight on the READ (findings, first-pass footprint auto-lit) — a permanent how-to card would
  // make the product feel like it hasn't started. 'ic_tour_seen' doubles as the first-visit flag.
  // {type:'orient'} | {type:'findings'} | {type:'axes'} | {type:'node',node} | {type:'rollup',grain} | {type:'axis',axis}
  const [right, setRight] = useState(() => {
    try { return localStorage.getItem('ic_tour_seen') ? { type: 'findings' } : { type: 'orient' }; }
    catch (e) { return { type: 'findings' }; }
  });
  const [activeFindingIdx, setActiveFindingIdx] = useState(null); // which finding's footprint is lit
  const [selectedId, setSelectedId] = useState(null);
  const [hover, setHover] = useState(null);          // {node, x, y} → floating tooltip pinned to the cell
  // Measured tooltip width, so it can be kept inside the viewport. See the tooltip render for why
  // this is measured rather than assumed.
  const tipRef = useRef(null);
  const [tipW, setTipW] = useState(0);
  const [tipH, setTipH] = useState(0);
  // 'timeline' (the horizontal icicle) | 'list' (the vertical map — story order runs DOWN, scenes
  // carry their TITLES) | 'table' (axis × unit matrix). List is the same map rotated 90°, not a
  // different product: same colouring, same click target, same reading pane. It exists because the
  // two answer different questions — the timeline shows a script's SHAPE at a glance and cannot
  // show a scene's name; the list shows every name and cannot show the shape. See verticalMap().
  const [view, setView] = useState('timeline');
  const [pinned, setPinned] = useState(null);        // Set<node id> lit for the current Axis-Inspector issue
  const [pinnedLabel, setPinnedLabel] = useState(null); // caption for the pinned issue (what the timeline is showing)
  const [pinFlash, setPinFlash] = useState(false);      // brief pulse on the lit units when a pin is set
  const pinFlashT = useRef(null);
  const pinColor = '#b3261e';   // the Patterns cards' rose (RollupPanel ROSE): outline, chip and pulse share it
  // LP-demo pulse: transient set of cell IDs to draw a fading outline around. Fed by a
  // `layered-read:pulse-units` CustomEvent the landing page fires when the widget scrolls
  // into view, so the visitor's eye catches the cells the LP copy is pointing at.
  const [pulsedIds, setPulsedIds] = useState(() => new Set());
  // Docked chat: ALWAYS starts collapsed so every landing is read-first — the reading is the single
  // focus, the chat is a tool you reach for (via a chip or the launcher), not a competing pane.
  const [chatOpen, setChatOpen] = useState(false);
  const setChatOpenPersist = (open, from) => {
    setChatOpen(open);
    try { localStorage.setItem('ic_chat_open', open ? '1' : '0'); } catch (e) { /* private mode */ }
    track('ic_chat_toggle', { open, from });
  };
  const [chatSubject, setChatSubject] = useState(null); // { axis, axisLabel, candidateId, unitId, title } | null = whole script
  // CHAT SESSION STORE (owner, 2026-10-14). Lives here, above every place AxisChat mounts, so a transcript,
  // its chat id and an unsent draft survive the chat being collapsed or unmounted (closing the reading,
  // stepping to the next scene, toggling the docked chat). One conversation per unit, one for the script.
  const [chatConvos, setChatConvos] = useState({});
  const [chatDrafts, setChatDrafts] = useState({});
  const chatStore = useMemo(() => ({ convos: chatConvos, setConvos: setChatConvos, drafts: chatDrafts, setDrafts: setChatDrafts }), [chatConvos, chatDrafts]);
  // SAVED CHATS RELOAD WITH THE PAGE (owner, 2026-10-14). Every thread is stored server-side; on load the
  // writer's own threads for this script are fetched and rebuilt into the SAME keys AxisChat uses
  // (chatKeyOf), so a question asked last week is back under its scene, axis or pattern. Anything already
  // in the session store wins (a reply streaming while this loads is never overwritten). Oldest first,
  // so when an old per-unit+axis thread and a newer per-unit thread share a key, the newer one wins.
  // chatMeta: key → { updatedAt, subject } — for the Conversations list and the "has a chat" marks.
  const [chatMeta, setChatMeta] = useState({});
  useEffect(() => {
    if ((publicView && embedded) || !movie?.hash || typeof fetch === 'undefined') return undefined;
    let dead = false;
    fetch(`/assistant/axis-inspector-chats?movie_hash=${encodeURIComponent(movie.hash)}`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (dead || !j || !Array.isArray(j.chats)) return;
        const convos = {}; const meta = {}; const saved = {};
        j.chats.forEach(c => {
          const subject = { axis: c.axis, candidateId: c.candidate_id || null, unitId: c.unit_id || null };
          const key = chatKeyOf(subject);
          // Several saved threads can share one key (older per-unit+axis threads, or a pattern asked twice
          // before threads were remembered). Show them as ONE conversation in time order; new questions
          // continue in the newest thread (its chatId), which the server seeded most recently.
          const msgs = (c.messages || []).map((m, i) => (m.me
            ? { id: `saved-${c.chat_id}-${i}`, me: true, text: m.text }
            : { id: `saved-${c.chat_id}-${i}`, me: false, markdown: m.text, streaming: false }));
          convos[key] = { chatId: c.chat_id, followUps: [], msgs: [...(convos[key]?.msgs || []), ...msgs] };
          meta[key] = { updatedAt: Date.parse(c.updated_at) || 0, subject };
          // returning to an axis reopens its latest saved thread (see axisThreadRef) — unless this session
          // already chose or used a thread for that axis, which wins (Codex G_hydration_axis_selection)
          if (!subject.unitId && subject.axis && subject.axis !== '_script' && !sessionAxesRef.current.has(subject.axis)) {
            const full = { ...subject, axisLabel: METRIC_LABELS[subject.axis] || subject.axis, title: subject.candidateId ? patternTitleOf(subject.axis, subject.candidateId) : null };
            axisThreadRef.current[subject.axis] = full;
            saved[subject.axis] = full;
          }
        });
        // An axis that was ALREADY open when the fetch resolved is showing its empty bare-axis thread;
        // switch it to the saved one (only if the session hasn't picked a thread for it meanwhile).
        setChatSubject(cur => ((cur && !cur.unitId && cur.axis && !cur.candidateId && saved[cur.axis] && !sessionAxesRef.current.has(cur.axis))
          ? saved[cur.axis] : cur));
        setChatConvos(prev => ({ ...convos, ...prev }));
        setChatMeta(prev => ({ ...meta, ...prev }));
      })
      .catch(() => { /* saved chats are a convenience; the page works without them */ });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movie?.hash]);
  const patternTitleOf = (axis, cid) => {
    const m = movie?.axis_inspectors?.[axis]?.merged || {};
    const p = [...(m.problem_patterns || []), ...(m.strength_patterns || [])].find(x => x && x.candidate_id === cid);
    return p ? (p.title || p.name || null) : null;
  };
  // UNIT CHAT DOCK — a collapsible bar at the bottom of a unit's reading (desktop modal + phone sheet).
  // Expanded it SHRINKS the reading (never covers it). It ALWAYS starts closed and costs no height
  // when closed — the way in is the "💬 Ask about …" button in the reading's header (owner, 2026-10-14:
  // "needs to be obvious it's available, but shouldn't take up vertical space").
  const [dockOpen, setDockOpenRaw] = useState(false);
  const dockOpenRef = useRef(dockOpen);
  dockOpenRef.current = dockOpen;
  const setDockOpen = (open, from) => {
    setDockOpenRaw(open);
    track('lr_unit_dock', { open, from: from || null });
  };
  const [dockAxis, setDockAxis] = useState(null);     // null = the whole unit; else the axis the writer asked about
  // Questions queued for the dock, each BOUND to its unit + axis ({ id, question, subject }). Not cleared by
  // navigation: a question asked about Scene 6 is still sent to Scene 6's conversation (review I1).
  const [dockQueue, setDockQueue] = useState([]);
  const dockQSeq = useRef(0);
  const [dockTyping, setDockTyping] = useState(false); // phone: input focused → chat takes the full height
  // ---- MOBILE (phones <700px): the horizontal grid can't work for interaction, so the map
  // rotates 90° (story order runs DOWN, native scroll), readings open as full-screen modals
  // (the SoS2 pattern), and the chat is a full-screen sheet. Desktop path is untouched.
  // ?phone=1 forces the phone layout at any width (preview/testing on desktop)
  const phoneNow = () => typeof window !== 'undefined'
    && (window.innerWidth < 700 || new URLSearchParams(window.location.search).has('phone'));
  const [isPhone, setIsPhone] = useState(phoneNow);
  // A viewport too SHORT for the desktop "fixed map + scrolling detail" split (e.g. a landscape phone).
  // Below this, the desktop container scrolls as one page so the reading isn't trapped under the map.
  const shortNow = () => typeof window !== 'undefined' && window.innerHeight < 640;
  const [shortView, setShortView] = useState(shortNow);
  // ⚠️ Viewport WIDTH has to be state, not a live `window.innerWidth` read during render. The List
  // view's side-by-side split keys on it, and a render-time read only re-evaluates when something
  // ELSE re-renders — so dragging the window narrower kept the two-column layout until an unrelated
  // state change happened to fix it. `shortView` above is height-only and does not cover this.
  const widthNow = () => (typeof window !== 'undefined' ? window.innerWidth : 0);
  const [viewportW, setViewportW] = useState(widthNow);
  useEffect(() => {
    const f = () => { setIsPhone(phoneNow()); setShortView(shortNow()); setViewportW(widthNow()); };
    window.addEventListener('resize', f);
    window.addEventListener('orientationchange', f);
    return () => { window.removeEventListener('resize', f); window.removeEventListener('orientationchange', f); };
  }, []);
  // ── Desktop unit-reading MODAL (owner, 2026-10-14) ───────────────────────────────────────────────
  // On the desktop TIMELINE a unit's reading opens in a modal over the map instead of the pane under it.
  // A writer reported the pane showed ~3 lines at a time inside a nested scroller that fought the page's
  // scene rail. `right` stays the single source of truth (in-app history, chat subject, deep links all
  // read it); `paneRight` remembers what the pane showed before the unit opened, so the pane keeps
  // showing Findings / Axes / Patterns underneath and closing the modal restores it.
  // List keeps its side-by-side split, Table keeps its own overlay, phones keep the full-screen sheet.
  // A node opened by the host's PRESELECTION (the landing-page demo's `initialSelection`) stays in the pane —
  // it is a preview, not a writer's click, and must not open a page-covering modal or lock scroll (review I3).
  // The analysis page is also `embedded`, so `embedded` alone is not the test.
  const [nodeInPane, setNodeInPane] = useState(false);
  const paneOpenRef = useRef(false);
  // TABLE uses the same modal (owner, 2026-10-15: "let's fix the other views"): its own overlay had no chat,
  // no prev/next and no title bar. The table overlay now only hosts the script-level panels.
  const unitModal = !isPhone && !nodeInPane && (view === 'timeline' || view === 'table') && right?.type === 'node';
  const [paneRight, setPaneRight] = useState(null);
  // The unit last opened (with its map selection), for List view's unit tab.
  const [lastUnit, setLastUnit] = useState(null);
  useEffect(() => { if (right?.type === 'node' && right.node) setLastUnit({ right, selectedId }); }, [right]);   // eslint-disable-line react-hooks/exhaustive-deps
  // UNIT HISTORY — what the reading window's Back / Forward walk (owner, 2026-10-20: "clicking back always
  // closes the modal … when I click back I want to see the history of modals I viewed"). Only readings
  // are recorded; closing the window, switching tabs or recolouring the map are not steps here, so Back
  // goes from the unit you are reading to the unit you read before it, however you got between them.
  // The page-wide history (navStack) still drives the top-bar arrows in List / Table.
  const [unitHist, setUnitHist] = useState({ items: [], idx: -1 });
  const unitMoveRef = useRef(false);
  const unitKeyOf = (r) => { const d = r?.node; return d ? String(d.unitId || d.unit_id || d.id || `${d.type}-${d.number}`) : null; };
  useEffect(() => {
    if (right?.type !== 'node' || !right.node) return;
    if (unitMoveRef.current) { unitMoveRef.current = false; return; }
    const entry = { right, selectedId, key: unitKeyOf(right) };
    setUnitHist(h => {
      if (h.idx >= 0 && h.items[h.idx]?.key === entry.key) return h;   // same unit (e.g. a different axis in it)
      const items = h.items.slice(0, h.idx + 1).concat([entry]).slice(-50);
      return { items, idx: items.length - 1 };
    });
  }, [right]);   // eslint-disable-line react-hooks/exhaustive-deps
  const stepUnitHist = (d) => {
    const t = unitHist.items[unitHist.idx + d];
    if (!t) return;
    track(d < 0 ? 'ic_unit_back' : 'ic_unit_forward', { depth: unitHist.idx });
    unitMoveRef.current = true;
    setUnitHist(h => ({ ...h, idx: h.idx + d }));
    setSelectedId(t.selectedId);
    setRight(t.right);
  };
  // A map hover tip must not survive a view switch or a reading opening over it: the mouse never "left"
  // the cell, so the tip stayed pinned on top of the Table modal (found 2026-10-15).
  useEffect(() => { setHover(null); }, [view, unitModal]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (right?.type !== 'node') setPaneRight(right); }, [right]);
  useEffect(() => {
    if (!unitModal || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [unitModal]);
  // Move focus into the dialog when it opens (and on prev/next): keyboard users land in the reading, and
  // the clicked map cell blurs, which hides the hover tip its onFocus would otherwise keep showing.
  const unitDialogRef = useRef(null);
  useEffect(() => {
    if (unitModal && unitDialogRef.current) { try { unitDialogRef.current.focus({ preventScroll: true }); } catch (e) { /* old browsers */ } }
  }, [unitModal, right]);
  const unitOpenerRef = useRef(null);   // the map cell (or link) that opened the reading — focus returns there
  const closeUnitModal = (via) => {
    track('ic_unit_modal_close', { via: via || null });
    setRight(paneRight);
    setSelectedId(null);
    const opener = unitOpenerRef.current;
    unitOpenerRef.current = null;
    if (opener && opener.isConnected && typeof opener.focus === 'function') {
      requestAnimationFrame(() => { try { opener.focus({ preventScroll: true }); } catch (e) { /* ignore */ } });
    }
  };
  // Tab stays inside the reading while it is open (aria-modal is a promise, not a mechanism).
  const trapTab = (e) => {
    if (e.key !== 'Tab') return;
    // A nested dialog (a repair-options modal) owns Tab while it is open (review I5).
    const nested = [...e.currentTarget.querySelectorAll('[role="dialog"]')].filter(d => d !== e.currentTarget && d.offsetParent !== null).pop();
    const root = nested || e.currentTarget;
    const f = [...root.querySelectorAll('button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
      .filter(el => el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (nested && !nested.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && (document.activeElement === first || document.activeElement === root)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  // Land on the MAP on a phone — the timeline IS the Layered Read, and it was hidden behind a tap before.
  const [mView, setMView] = useState(() => (phoneNow() ? 'map' : 'read'));   // 'read' (the pane) | 'map' (vertical icicle)
  const [mUnit, setMUnit] = useState(null);     // detailedContent for the full-screen reading modal
  const [mAxis, setMAxis] = useState(null);     // axis key for the full-screen inspector modal
  // wiki drawer (same InfoUI drawer as the SoS wheel + the unit readings' own dots) — page-level
  // so findings' "D·E·R behind it" links and the axis ⓘ affordances share one drawer.
  const [wikiArt, setWikiArt] = useState(null);
  const [howVideo, setHowVideo] = useState(false); // the 5-min explainer, from the orientation card (2026-10-23)
  // …and from the LR tab's top button (view_icicle_react.blade.php), which sits outside this React root.
  useEffect(() => {
    const open = () => { track('ic_rail_nav', { item: 'how_video', via: 'tab_top' }); setHowVideo(true); };
    window.addEventListener('lr:howvideo', open);
    return () => window.removeEventListener('lr:howvideo', open);
  }, []);
  // "Take a tour" from the analysis page's Layered Read heading (outside this root): window event 'lr:tour'.
  useEffect(() => {
    const go = () => startTour('heading');
    window.addEventListener('lr:tour', go);
    return () => window.removeEventListener('lr:tour', go);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ACTIVE PANEL = wherever the answer just landed: ask the chat → chat; open a reading → pane.
  // Shown as a 3px navy top accent (transparent when inactive, so nothing reflows). The rail is an
  // index, never "active". Any click inside a panel claims it; actions that redirect attention
  // (e.g. "Ask the AI" inside the pane) claim explicitly.
  const [activePanel, setActivePanel] = useState('pane');
  const [chatSeed, setChatSeed] = useState(null);    // { question, nonce } → auto-send on "ask →"

  // ── In-app Back / Forward history (independent of the browser Back/Forward) ─────────────────────────
  // A history of the reading states the writer moved through — Findings → an axis → a scene → Patterns →
  // a grain → a colour-by, and so on. Each entry snapshots the nav-defining state. A back/forward pair in
  // the top-left (browser convention) walks it: Back pops the previous state, Forward re-applies one we
  // backed out of. Any NEW navigation truncates the forward history (standard browser branch behaviour).
  // Transitions are RECORDED PASSIVELY in the effect below — so we never wrap the ~40 scattered setRight/
  // setMetric/… call sites — and navMoveRef stops a programmatic back/forward from recording itself.
  // Scoped to the desktop shell; the phone layout keeps its own modal/back behaviour untouched.
  // ⚠️ DECLARED HERE, not down with the rest of the zoom logic, because the nav-history effect
  // below lists `focusAct` in its dependency array — which is evaluated DURING RENDER. Babel
  // compiles const to var, so a later declaration would silently read `undefined` every render
  // instead of throwing (same trap as NodePanel.js:1105). Keep this above the history block.
  const [focusAct, setFocusAct] = useState(null);   // act OR sequence node id, or null (whole script)
  const [navStack, setNavStack] = useState([]);   // states we can go BACK to
  const [fwdStack, setFwdStack] = useState([]);   // states we can go FORWARD to (filled by Back)
  const navMoveRef = useRef(false);    // true while applying a programmatic back/forward → skip recording
  const displayedRef = useRef(null);   // { snap, key } of the state currently on screen (the previous-value idiom)
  // `focusAct` is part of the navigation state (owner, 2026-09-04). It used to be missing from the
  // key AND from the snapshot, so a reader who zoomed in and reached for the Back arrow — the
  // obvious thing to reach for — was thrown to some unrelated earlier pane while the map stayed
  // zoomed. Zoom is a navigation; the history has to know about it.
  const navKeyOf = (r, m, sel, v, fa) => JSON.stringify([r?.type || null, r?.axis || null, r?.grain || null, r?.fromAxis || null, sel || null, m, v, fa || null]);
  useEffect(() => {
    const snap = { right, metric, selectedId, view, focusAct, pinned: pinned ? [...pinned] : null, pinnedLabel };
    const key = navKeyOf(right, metric, selectedId, view, focusAct);
    const prev = displayedRef.current;      // the state we're LEAVING (captured before we overwrite it)
    displayedRef.current = { snap, key };
    if (prev === null || key === prev.key) return;   // first mount, or a side-only change (e.g. a highlight)
    if (navMoveRef.current) { navMoveRef.current = false; return; }   // this change was Back/Forward — don't record
    setNavStack(s => (s.length >= 50 ? s.slice(1) : s).concat([prev.snap]));   // push the state we just left
    setFwdStack([]);   // a genuine new navigation invalidates the forward history
  }, [right, metric, selectedId, view, focusAct, pinned, pinnedLabel]);
  const applySnap = (snap) => {
    navMoveRef.current = true;
    setRight(snap.right);
    setMetric(snap.metric);
    setSelectedId(snap.selectedId);
    setView(snap.view);
    setFocusAct(snap.focusAct && canZoom(byId.get(snap.focusAct)) ? snap.focusAct : null);   // only a zoomable block (acts-only rule)
    setPinned(snap.pinned ? new Set(snap.pinned) : null);
    setPinnedLabel(snap.pinnedLabel);
    setActivePanel('pane');
  };
  const goBack = () => {
    if (!navStack.length) return;
    const target = navStack[navStack.length - 1];
    const current = displayedRef.current?.snap;
    applySnap(target);
    setNavStack(s => s.slice(0, -1));
    if (current) setFwdStack(f => f.concat([current]));   // where we were becomes a Forward step
    track('ic_nav_back', { depth: navStack.length });
  };
  const goForward = () => {
    if (!fwdStack.length) return;
    const target = fwdStack[fwdStack.length - 1];
    const current = displayedRef.current?.snap;
    applySnap(target);
    setFwdStack(f => f.slice(0, -1));
    if (current) setNavStack(s => s.concat([current]));   // where we were goes back onto the Back stack
    track('ic_nav_forward', { depth: fwdStack.length });
  };
  // The Back / Forward pair. Enabled only when there is somewhere to go (disabled, not hidden, so it
  // never shifts). On the TIMELINE it lives in the reading modal only (owner, 2026-10-19: "place
  // back/forward navigation in timeline view in the modal only, not at the top level").
  const renderUnitHistory = () => {
    const canB = unitHist.idx > 0, canF = unitHist.idx >= 0 && unitHist.idx < unitHist.items.length - 1;
    const nameOf = (e) => (e ? (unitDisplayLabel(e.right.node, 'list') || e.right.node.title || 'reading') : '');
    return (
      <div className="d-flex" role="group" aria-label="Readings you opened" style={{ gap: 2, flexShrink: 0 }}>
        {[['‹', 'Back', -1, canB], ['›', 'Forward', 1, canF]].map(([glyph, label, d, on]) => (
          <button key={label} type="button" onClick={on ? () => stepUnitHist(d) : undefined} disabled={!on}
            {...tip(on ? `${label} to ${nameOf(unitHist.items[unitHist.idx + d])}` : (d < 0 ? 'No earlier reading' : 'No later reading'))} aria-label={label}
            style={{ width: 30, height: 28, lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1.5px solid ${on ? NAVY : '#e2e8f0'}`, borderRadius: 7, background: '#fff',
              color: on ? NAVY : '#c3ccd4', fontSize: 18, fontWeight: 700, cursor: on ? 'pointer' : 'default', padding: 0 }}>
            {glyph}
          </button>
        ))}
      </div>
    );
  };
  const renderHistory = () => (
    <div className="d-flex" role="group" aria-label="Navigation history" style={{ gap: 2, flexShrink: 0 }}>
      {[['‹', 'Back', goBack, navStack.length > 0], ['›', 'Forward', goForward, fwdStack.length > 0]].map(([glyph, label, fn, on]) => (
        <button key={label} type="button" onClick={on ? fn : undefined} disabled={!on} {...tip(on ? label : `No ${label.toLowerCase()} history`)} aria-label={label}
          style={{ width: 30, height: 28, lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1.5px solid ${on ? NAVY : '#e2e8f0'}`, borderRadius: 7, background: '#fff',
            color: on ? NAVY : '#c3ccd4', fontSize: 18, fontWeight: 700, cursor: on ? 'pointer' : 'default', padding: 0 }}>
          {glyph}
        </button>
      ))}
    </div>
  );

  // Measure the scene-row grid width so we can show a bare scene number ("11") only when a cell is wide
  // enough to fit one; otherwise the strip stays colour-only and hover/click carry the detail.
  const leftRef = useRef(null);
  const contentRef = useRef(null);
  const cardRef = useRef(null);
  const drawerRef = useRef(null);   // table-mode right-hand detail drawer (its own scroll container)
  const railRef = useRef(null);     // the boundary-line overlay; its measured px width lets us snap lines
  const [gridW, setGridW] = useState(0);
  const [railW, setRailW] = useState(0);
  const paneRowRef = useRef(null);   // pane+chat flex row — measured so the chat docks or overlays by width
  const [rowW, setRowW] = useState(0);
  useEffect(() => {
    const measure = () => {
      // gridW = the GRID's own content width. The boundary overlay sits exactly over it, so measure that;
      // the old "map width minus the label" also counted the map's 1rem padding on both sides.
      if (railRef.current) { const w = railRef.current.getBoundingClientRect().width; setRailW(w); setGridW(w); }
      else if (leftRef.current) setGridW(Math.max(0, leftRef.current.clientWidth - 32 - LABEL_W));
      if (paneRowRef.current) setRowW(paneRowRef.current.getBoundingClientRect().width);
    };
    // measure after first PAINT (double rAF), and re-measure late (fonts/CSS settling) — a single
    // rAF proved flaky on first load once the pane mounted heavy content (labels stayed hidden
    // because gridW never left 0 until a manual resize).
    requestAnimationFrame(() => requestAnimationFrame(measure));
    const late = setTimeout(measure, 350);
    window.addEventListener('load', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;

    // ⚠️ ATTACH BY RETRY, not once (fixed 2026-08-21). These three observes used to run a single
    // time in this effect body. Whenever a ref was still null at that moment — the tree had not
    // mounted yet on that pass — NOTHING was observed, the effect never re-ran (deps are `[view]`),
    // and `rowW` stayed frozen at its first measured value for the life of the view.
    //
    // The failure is invisible until the layout changes WITHOUT a window resize (a sidebar
    // collapsing, a scrollbar appearing, the embedded full-bleed effect widening `#content-pcr`),
    // because `measure` is also bound to `window.resize` and that path masks it. Measured before the
    // fix: the pane row went 1400 -> 1100 -> 900 -> 780 while the chat stayed pinned at its stale
    // 460px dock and never flipped to overlay at 780; dispatching a synthetic `resize` corrected it
    // immediately, which is what isolated the observer rather than `measure` as the culprit.
    //
    // `ro.observe()` on an already-observed element is a no-op, so re-running attach is free.
    const attach = () => {
      if (!ro) return;
      if (leftRef.current) ro.observe(leftRef.current);
      if (railRef.current) ro.observe(railRef.current);       // the rail itself → always measured
      if (paneRowRef.current) ro.observe(paneRowRef.current);
    };
    attach();
    const attachTimers = [0, 120, 400, 1000].map(ms => setTimeout(() => { attach(); measure(); }, ms));

    // ⚠️ POLL, because the ResizeObserver above demonstrably does not fire here and I could not
    // isolate why. Control test on this page: observing the pane row with a FRESH ResizeObserver and
    // then changing its width produced ZERO callbacks, while dispatching a synthetic `window.resize`
    // — which runs the very same `measure` — corrected the layout immediately. So the fault is the
    // observer notification, not the measurement. Until that is understood, the observer stays
    // (harmless, and correct where it does fire) and this backstop guarantees `rowW` cannot sit
    // frozen: a stale `rowW` pins the chat at a dock width the row cannot afford and stops it ever
    // falling back to the overlay, which is a visible layout break rather than a cosmetic one.
    // Cost is three getBoundingClientRect calls per tick; `measure` no-ops when nothing changed
    // because React bails out on identical state. Same pattern as the full-bleed effect's poll.
    const poll = setInterval(measure, 700);

    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      clearTimeout(late);
      clearInterval(poll);
      attachTimers.forEach(clearTimeout);
      window.removeEventListener('load', measure);
      window.removeEventListener('resize', measure);
    };
  }, [view]);

  // Chat docking math: below ~900px the pane's two-column axis view and the chat cannot coexist, so the
  // chat OVERLAYS instead of docking. Docked: chat ~1/3 (clamped 320–460), pane takes the rest. rowW===0
  // (first paint) is treated as dockable so the initial layout isn't an overlay flash.
  //
  // ⚠️ DO NOT "FIX" A CRAMPED READING BY OVERLAYING THE CHAT MORE OFTEN — IT MAKES IT NARROWER.
  // Tried and measured 2026-09-04 at rowW=964: DOCKED leaves the reading card 644px (chat 320);
  // OVERLAY leaves it 544px, because the left column must RESERVE the overlay's 420px as padding
  // or its content hides underneath (see the note at the pane row). Overlay exists to protect the
  // CHAT from becoming an unusable slice on a narrow row, not to protect the pane. The threshold
  // below is therefore about the chat's own minimum, and stays as it was.
  const CHAT_DOCK_MIN = 900;
  const chatDockable = rowW === 0 || rowW >= CHAT_DOCK_MIN;
  const chatDockW = Math.max(320, Math.min(460, Math.round((rowW || 1000) * 0.33)));
  // Overlay-mode width, mirroring AxisChat's own `min(420px, 94%)`. The left column RESERVES this
  // as padding while the chat is overlaying — see the note at the pane row.
  const chatOverlayW = Math.min(420, Math.round((rowW || 1000) * 0.94));

  // FULL-BLEED (analysis-page embed only): the Layered Read lives in a narrow Bootstrap col-sm-9
  // (#mainanal, overflow-x:hidden). Break the section out to span the whole width between the left nav
  // and the scenes rail so timeline + pane + chat get real room. Guarded, revertible, and a no-op on the
  // LP demo, the standalone icicle, mobile (<1024), or while the section is hidden. Observes the COLUMN
  // (viewport-driven) not the section, so our own width writes can't feed back into a resize loop.
  useEffect(() => {
    if (!embedded) return undefined;
    const section = document.getElementById('content-pcr');
    const col = document.getElementById('mainanal');
    if (!section || !col || !col.contains(section)) return undefined;
    const prevOverflowX = col.style.overflowX;
    const prevOverflowPriority = col.style.getPropertyPriority('overflow-x');
    // ⚠️ TURF WAR over #mainanal's overflow-x — do not "simplify" this away.
    // The Scenes / D&E overview injects a PERSISTENT author rule to make its sticky headers work:
    //   <style id="sov-mainanal-overflow-patch">#mainanal { overflow-x: clip !important }</style>
    // (script_design_execution_overview.blade.php:5703, injected once from bootView(), never removed).
    // An author !important rule BEATS a non-important inline style, so our `col.style.overflowX =
    // 'visible'` below silently lost the moment that view had booted even once — and because `clip`
    // creates no scrollable overflow, the full-bleed section was cut with NO scrollbar and nothing to
    // scroll to. Measured on staging 2026-08-20: 138px hidden off the left, 102px off the right
    // ("BETA" -124px, "Layered Read is an early previ…" -69px, "Colour by" +17px, "Script" +84px).
    // The two views are never visible at once, so they take turns: suspend the rule while the Layered
    // Read is actually on screen, hand it straight back the moment it isn't (or on unmount).
    const patchEl = () => document.getElementById('sov-mainanal-overflow-patch');
    const suspendPatch = () => { const p = patchEl(); if (p && !p.disabled) p.disabled = true; };
    const restorePatch = () => { const p = patchEl(); if (p && p.disabled) p.disabled = false; };
    const restoreOverflow = () => {
      col.style.removeProperty('overflow-x');                        // removeProperty, so the !important we set can't linger
      if (prevOverflowX) col.style.setProperty('overflow-x', prevOverflowX, prevOverflowPriority);
    };
    let applying = false;
    const clear = () => { section.style.width = ''; section.style.marginLeft = ''; section.style.maxWidth = ''; };
    const apply = () => {
      if (applying) return;
      if (!section.offsetParent) { restorePatch(); return; }          // LR hidden → the Scenes view gets its sticky rule back
      const vw = document.documentElement.clientWidth;
      if (vw < 1024) { if (section.style.width) { clear(); restorePatch(); restoreOverflow(); } return; }  // mobile → normal layout
      // Measure the dark left icon rail and the right scenes rail. Both are stable and unaffected by our own
      // width/margin writes on the section, so this is safe to run repeatedly.
      let navRight = 60, railLeft = vw;
      // ⚠️ ASK THE SIDEBAR DIRECTLY. The heuristic below caps at `width <= 120`, which only ever
      // matched the COLLAPSED icon rail. Expanded, `#sidebar` is ~220px, matched nothing, and
      // navRight silently fell back to the hard-coded 60 — so the full-bleed positioned itself for
      // a collapsed rail and the Layered Read sat UNDER the expanded menu. Measured on localhost
      // 2026-08-21 (movie 9179): #sidebar.col-sm-3 width 220, right 220, bg rgb(8,58,84);
      // matches for the heuristic: 0. The element has an id — use it, and keep the scan as a
      // fallback for layouts that don't.
      const sidebarEl = document.getElementById('sidebar');
      if (sidebarEl) {
        const sb = sidebarEl.getBoundingClientRect();
        if (sb.left <= 2 && sb.width > 20 && sb.height > 200) navRight = Math.max(navRight, sb.right);
      }
      document.querySelectorAll('body *').forEach((e) => {
        const r = e.getBoundingClientRect();
        if (r.left <= 2 && r.height > 400 && r.width >= 40 && r.width <= 120) {
          const bg = getComputedStyle(e).backgroundColor;
          if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') navRight = Math.max(navRight, r.right);
        } else if (r.right >= vw - 3 && r.width > 20 && r.width < 120 && r.height > 300 && r.left < railLeft) railLeft = r.left;
      });
      const M = 16, leftEdge = navRight + M, targetW = Math.round((railLeft - M) - leftEdge);
      if (targetW <= 200) return;
      // Pull left relative to the COLUMN's content edge (a child's negative margin can't move the column),
      // so repeated applies are idempotent and can't compound the way measuring the section's own moved left did.
      const colPadL = parseFloat(getComputedStyle(col).paddingLeft) || 0;
      const naturalLeft = Math.round(col.getBoundingClientRect().left + colPadL);
      const wPx = targetW + 'px', mPx = (leftEdge - naturalLeft) + 'px';
      // Re-suspend on EVERY pass, not only when the geometry changes: the Scenes view can inject its
      // rule AFTER we've applied (user opens Scenes, comes back), and the early-return below would
      // otherwise skip straight past the re-suspend and leave us clipped again.
      suspendPatch();
      if (section.style.width === wPx && section.style.marginLeft === mPx && col.style.overflowX === 'visible') return;  // already correct
      applying = true;
      col.style.setProperty('overflow-x', 'visible', 'important');   // 'important' — see the turf-war note above
      section.style.maxWidth = 'none';
      section.style.width = wPx;
      section.style.marginLeft = mPx;
      requestAnimationFrame(() => { applying = false; });
    };
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro) {
      ro.observe(col);
      ro.observe(section);                                  // catches the late showContent('pcr') reveal
      // ⚠️ AND the sidebar: collapsing/expanding it does NOT resize #mainanal (which is full-width
      // on this page), so without this the offset never recomputes and the LR stays positioned for
      // whichever state the sidebar happened to be in at mount.
      const sb = document.getElementById('sidebar');
      if (sb) ro.observe(sb);
    }
    window.addEventListener('resize', apply);
    // The LR section is revealed by showContent('pcr') several seconds after load and RO doesn't reliably
    // fire on that display flip — so poll until the section is actually visible and full-bleed has applied,
    // then stop (bounded to ~12s so it never runs forever).
    let tries = 0;
    const poll = setInterval(() => { apply(); if ((section.style.width && section.offsetParent) || ++tries > 30) clearInterval(poll); }, 400);
    return () => { if (ro) ro.disconnect(); clearInterval(poll); window.removeEventListener('resize', apply); clear(); restorePatch(); restoreOverflow(); };
  }, [embedded]);

  const nodes = useMemo(() => prepareChartData(movie, true, metric).nodes, [movie, metric]);
  const byId = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);
  const movieNode = useMemo(() => nodes.find(n => n.nodeType === 'movie') || null, [nodes]);
  const sceneNodes = useMemo(() => nodes.filter(n => n.nodeType === 'scene'), [nodes]);
  const totalScenes = sceneNodes.length || 1;
  // ZOOM: when set, the timeline widens ONE UNIT in place (see "ZOOM IN PLACE" below). The unit may
  // be an ACT or a SEQUENCE (owner, 2026-09-04; it used to be acts only).
  //
  // Sequence zoom is not "act zoom but sparser" — it is the level at which the map can finally show
  // scene TITLES. A typical act holds ~4 scenes per sequence, so at sequence zoom each scene block is
  // several hundred px wide and can carry its name; at act zoom it carries its number; at script
  // level it carries nothing but its shape. Three zoom levels, three densities, one gesture. That is
  // what earns the sparseness — see cellLabel().
  const isInFocusedAct = (n) => { if (!n) return false; if (n.id === focusAct) return true; let p = n.parent, g = 0; while (p && byId.has(p) && g++ < 8) { if (p === focusAct) return true; p = byId.get(p).parent; } return false; };
  // ZOOM IN PLACE (owner, 2026-10-14). Zooming widens the chosen act's or sequence's COLUMN: every
  // layer under it widens with it, so the rows stay aligned (and any layer added later comes along
  // for free). Everything else is squeezed into slivers that stay on screen and clickable. Nothing is
  // hidden, so there is no mode to find your way out of — the squeezed acts ARE the "you are zoomed"
  // cue, and "Zoom out" sits on the zoomed cell exactly where "Zoom in" was. The old version dropped
  // the other acts and the rows above, and left a breadcrumb + "Jump to" strip as the only way back.
  // focusAnc = the zoomed unit and everything above it; all of them are drawn at full height.
  const focusAnc = useMemo(() => {
    const s = new Set(); let cur = focusAct ? byId.get(focusAct) : null, g = 0;
    while (cur && g++ < 9) { s.add(cur.id); cur = cur.parent ? byId.get(cur.parent) : null; }
    return s;
  }, [focusAct, byId]);
  // Every unit OUTSIDE the zoomed one → the sliver it is squeezed into: its highest ancestor that does
  // not contain the zoomed unit (a sibling act, or a sibling sequence in the same act). Empty unzoomed.
  const groupOf = useMemo(() => {
    const m = {};
    if (!focusAct) return m;
    nodes.forEach(n => {
      if (focusAnc.has(n.id) || isInFocusedAct(n)) return;
      let cur = n, g = 0;
      while (cur.parent && byId.has(cur.parent) && !focusAnc.has(cur.parent) && g++ < 9) cur = byId.get(cur.parent);
      m[n.id] = cur.id;
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, focusAct, focusAnc, byId]);
  // The grid ALWAYS holds every scene in story order; zooming changes column widths, never the set.
  const orderedScenes = useMemo(() => sceneNodes.slice().sort((a, b) => unitSortKey(a) - unitSortKey(b)), [sceneNodes]);

  // leaf-scene span + max scene number per node (walk each scene up its parent chain)
  const { span, maxScene, pageSpan } = useMemo(() => {
    const sp = {}; const mx = {}; const pg = {};
    nodes.forEach(n => { sp[n.id] = 0; pg[n.id] = 0; });
    sceneNodes.forEach(s => {
      const num = s.detailedContent?.number ?? 0;
      const pc = Math.max(0.1, (+s.detailedContent?.page_count) || 1);
      sp[s.id] = 1; mx[s.id] = num; pg[s.id] = pc;
      let p = s.parent, g = 0;
      while (p && byId.has(p) && g++ < 8) { sp[p] = (sp[p] || 0) + 1; pg[p] = (pg[p] || 0) + pc; mx[p] = Math.max(mx[p] || 0, num); p = byId.get(p).parent; }
    });
    return { span: sp, maxScene: mx, pageSpan: pg };
  }, [nodes, byId, sceneNodes]);

  // Measure the tooltip so it can be clamped inside the viewport (see the tooltip render). Keyed on
  // the hovered node rather than on `hover`, because `hover` is a fresh object on every mouseenter
  // and the width only changes when the CONTENT does. Bails when the width is unchanged so moving
  // along a row of cells does not re-render on every pixel.
  useLayoutEffect(() => {
    if (!hover || !tipRef.current) return;
    const rb = tipRef.current.getBoundingClientRect();
    const w = rb.width, h = rb.height;
    setTipW(prev => (Math.abs(prev - w) < 1 ? prev : w));
    setTipH(prev => (Math.abs(prev - h) < 1 ? prev : h));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hover?.node?.id, metric]);

  // Width of the List column, so its optional columns can degrade rather than crush the title.
  const listRef = useRef(null);
  const [listW, setListW] = useState(0);
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setListW(el.clientWidth));
    ro.observe(el); setListW(el.clientWidth);
    return () => ro.disconnect();
  }, [view]);

  // Side by side only when there is genuinely room for two columns and the shell is the normal
  // full-height one. `shortView` is the landscape-phone flow layout, where stacking is correct,
  // and an embedded widget has no height to give away.
  // ⚠️ AND NOT WHILE THE CHAT IS OPEN, unless the window is genuinely huge (owner, 2026-09-04:
  // "chat needs to be an overlay to avoid the scrunching"). Three columns — list, reading, chat —
  // do not fit in 1440: it leaves the reading ~490px and its two-column layout collapses into
  // narrow columns of wrapped words.
  // A true floating overlay does NOT solve it, which is why this is a layout fallback instead: the
  // chat already reserves its width as padding on the left column (see the note at the pane row,
  // owner 2026-08-24), because without that reservation the pane's content simply hides underneath
  // it. So "overlay" costs 420px where docking costs 320 — strictly worse for the scrunch.
  // Falling back to STACKED gives the reading the full row (1,404px measured) and the chat docks
  // into it comfortably; the list keeps 48vh, which is the right trade while you are typing rather
  // than scanning. Above ~1800px all three genuinely fit, so the split survives.
  // ⚠️ EMBEDDED IS NO LONGER EXCLUDED (owner, 2026-09-30: "the goal of using list view is to see
  // more. so split should be vertical, with content on the right"). The analysis-page embed has a
  // bounded 82vh / min 540px box — plenty of height — and stacking there spent half of it on a
  // 48vh list, which defeated the point of List. Only the LP demo (publicView && embedded) keeps
  // stacking. And the gate is now the SHELL's own width, not the viewport's: an embed sits between
  // the nav and the scenes rail, so viewport width over-reports what the split actually gets.
  // Measured on the shell, not the pane row, because the pane row's width depends on this flag.
  const shellRef = useRef(null);
  const [shellW, setShellW] = useState(0);
  useEffect(() => {
    const el = shellRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setShellW(el.clientWidth));
    ro.observe(el); setShellW(el.clientWidth);
    return () => ro.disconnect();
  }, [isPhone]);
  const splitW = shellW || viewportW;
  // ⚠️ 2026-10-15 (owner: "fix the other views — chat in list view"): stacking under the chat left the
  // reading ~270px tall with the chat beside it at the same height. The reading now measures ITSELF
  // (NodePanel drops to one column when narrow), so the Sep-04 "words wrapping in narrow columns" reason
  // for stacking is gone: from 1200px the split survives and the LIST narrows instead (listNarrow).
  const sideBySide = view === 'list' && !shortView && !(publicView && embedded) && splitW >= 800
    && (!chatOpen || splitW >= 1200);
  const listNarrow = sideBySide && chatOpen && splitW < 1700;

  // Column widths as FRACTIONS of the map (they sum to 1). Unzoomed: page length. Zoomed: each sliver
  // gets SLIVER_PX (less if there are many), split across its scenes by page length, and the zoomed
  // unit's scenes share the rest by page length — so widths inside the zoom stay honest.
  const targetWeights = useMemo(() => {
    const pages = orderedScenes.map(s => Math.max(0.1, (+s.detailedContent?.page_count) || 1));
    const tot = pages.reduce((a, b) => a + b, 0) || 1;
    if (!focusAct) return pages.map(x => x / tot);
    const W = gridW || 1000;
    const groupPages = {}; let inside = 0;
    orderedScenes.forEach((s, i) => { const g = groupOf[s.id]; if (g) groupPages[g] = (groupPages[g] || 0) + pages[i]; else inside += pages[i]; });
    const G = Object.keys(groupPages).length;
    const px = G ? Math.max(6, Math.min(SLIVER_PX, (W * 0.3) / G)) : 0;   // slivers never take more than 30%
    const share = G ? Math.min(0.3, (G * px) / W) : 0;
    return orderedScenes.map((s, i) => {
      const g = groupOf[s.id];
      return g ? (share / G) * (pages[i] / groupPages[g]) : (1 - share) * (pages[i] / (inside || 1));
    });
  }, [orderedScenes, focusAct, groupOf, gridW]);
  // The zoom ANIMATES THE WIDTHS, not a transform: every row, every boundary line and the selection
  // frame are computed from the same weights each frame, so nothing can drift out of line mid-tween
  // and no text is ever stretched. Only a change of zoom animates; a resize just snaps.
  // ⚠️ rAF does not fire in a hidden tab, so a timer finishes the tween regardless (an earlier
  // rAF-only zoom froze mid-transform when the tab was backgrounded).
  const [shownWeights, setShownWeights] = useState(null);
  const shownRef = useRef(null);
  const zoomFocusRef = useRef(focusAct);
  const [zooming, setZooming] = useState(false);
  const zoomFrames = useRef([]);
  const zoomTimers = useRef([]);
  useLayoutEffect(() => {
    const stop = () => {
      zoomFrames.current.forEach(cancelAnimationFrame); zoomFrames.current = [];
      zoomTimers.current.forEach(clearTimeout); zoomTimers.current = [];
    };
    stop();
    const from = shownRef.current, to = targetWeights;
    const focusChanged = zoomFocusRef.current !== focusAct; zoomFocusRef.current = focusAct;
    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const finish = () => { stop(); shownRef.current = to; setShownWeights(to); setZooming(false); };
    if (!focusChanged || !from || from.length !== to.length || reduce || hidden || !gridW) { finish(); return undefined; }
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    setZooming(true);
    const step = (now) => {
      const t = Math.min(1, (now - t0) / ZOOM_MS);
      const e = 1 - Math.pow(1 - t, 3);   // ease-out
      if (t >= 1) { finish(); return; }
      const w = from.map((a, i) => a + (to[i] - a) * e);
      shownRef.current = w; setShownWeights(w);
      zoomFrames.current.push(requestAnimationFrame(step));
    };
    zoomFrames.current.push(requestAnimationFrame(step));
    zoomTimers.current.push(setTimeout(finish, ZOOM_MS + 150));
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetWeights]);
  const colWeights = (shownWeights && shownWeights.length === targetWeights.length) ? shownWeights : targetWeights;

  const actEnds = useMemo(() => new Set(nodes.filter(n => n.nodeType === 'act').map(n => maxScene[n.id])), [nodes, maxScene]);
  const seqEnds = useMemo(() => new Set(nodes.filter(n => n.nodeType === 'sequence').map(n => maxScene[n.id])), [nodes, maxScene]);

  // Scene widths are proportional to PAGE COUNT (a 3-page scene is ~3× a 1-page scene), not equal. Build
  // one grid column per scene in story order sized by its page_count; every row shares this template so
  // acts/sequences (which span whole scenes) inherit the right proportional width and stay aligned.
  // cumPages[k] = pages before boundary k → the fraction where the k-th vertical line sits.
  // ⚠️ WHERE EACH CELL STARTS, READ OFF ITS OWN CHILDREN — not inferred by packing.
  // `gridColumn: span N` with no start lets CSS Grid auto-pack a row from column 1, which is only
  // correct while every row's cells cover the whole grid. Movie 36644 broke that: its PROLOGUE is a
  // sequence parented to the ROOT, so the Acts row had no cell for it and both acts were drawn two
  // columns left of the scenes they contain, with the last two blank. See gridPlacement.js.
  const startCol = useMemo(() => startColumns(orderedScenes, byId), [orderedScenes, byId]);

  // cumPages/totalPages are now in map FRACTIONS (zoom-aware), not pages — every consumer only ever
  // divides one by the other. ⚠️ fr values are scaled ×1000: a track list whose fr sum is below 1
  // only fills that fraction of the row.
  const { colTemplate, cumPages, totalPages, gridTotal } = useMemo(() => {
    const w = colWeights;
    const cum = [0];
    w.forEach(x => cum.push(cum[cum.length - 1] + x));
    return { colTemplate: w.map(x => `minmax(0, ${(x * 1000).toFixed(3)}fr)`).join(' '), cumPages: cum, totalPages: cum[cum.length - 1] || 1, gridTotal: w.length || 1 };
  }, [colWeights]);

  // 'overall' (triage-coloured) nodes for the Table view — independent of the timeline's Colour-by metric.
  const tableNodes = useMemo(() => prepareChartData(movie, true, 'overall').nodes, [movie]);
  // The Script row's own triage band (same rule as prepareChartData's root) — the Findings headline chip
  // shows this, so the map and the headline never name two different verdicts (owner, 2026-10-26).
  const scriptBand = useMemo(() => { const m = (movie && movie.movie) || {}; return bandOf({ design: m.design, execution: m.execution, read: m.read }); }, [movie]);

  // Every vertical boundary as a scene index (0..totalScenes) + the deepest level it separates. Drawn as
  // one overlay in the grid's own coordinate space so they align perfectly; level only sets how far DOWN
  // the line runs. k=0 and k=totalScenes are the script's own edges (left & right of the whole timeline).
  const boundaryLines = useMemo(() => {
    // Units squeezed into a sliver cut no lines of their own — 15 hairlines in a 26px column is a
    // black bar. The sliver's own edges still come from its head (an act or sequence boundary).
    const vis = (n) => !groupOf[n.id] || groupOf[n.id] === n.id;
    const inside = new Set();
    new Set(Object.values(groupOf)).forEach(h => {
      const c0 = (startCol[h] || 1) - 1, c1 = c0 + (span[h] || 1);
      for (let k = c0 + 1; k < c1; k++) inside.add(k);
    });
    // ⚠️ A CELL'S OWN END, NOT A RUNNING TOTAL. This accumulated spans in sort order, which carries
    // the same error as auto-packing: on movie 36644 it drew the act rule at column 21 instead of
    // 23, inside Act 1's own Scene Three. gridPlacement.js.
    const cuts = (type) => grainBoundaries(nodes.filter(n => n.nodeType === type && vis(n)), startCol, span);
    const actSet = cuts('act');
    const seqSet = cuts('sequence');
    const lines = [];
    for (let k = 0; k <= gridTotal; k++) {
      if (inside.has(k)) continue;
      let level = 'scene';
      if (k === 0 || k === gridTotal) level = 'movie';         // the script's outer edges
      else if (actSet.has(k)) level = 'act';
      else if (seqSet.has(k)) level = 'sequence';
      lines.push({ k, level, pos: (cumPages[k] ?? k) / totalPages });   // fraction across (by page count)
    }
    return lines;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, span, gridTotal, cumPages, totalPages, groupOf, startCol]);

  // Every row is always drawn, zoomed or not; squeezed units are handled at render (see groupOf).
  const rows = GRAINS
    .map(gr => ({ ...gr, label: grainTitle(movie && movie.movie, gr.key),
      cells: nodes.filter(n => n.nodeType === gr.key).sort((a, b) => unitSortKey(a) - unitSortKey(b)) }))
    .filter(r => r.cells.length);
  // y-offset (px, within the rows wrapper) of each row's top, so a boundary line can start at its own
  // level and run DOWN — an act line from the Acts row, a sequence line from the Sequences row.
  const rowTop = {}; { let acc = 0; rows.forEach(r => { rowTop[r.key] = acc; acc += r.h + 6; }); }
  // exact y (px, within the wrapper) of the bottom of the last (scenes) row — every boundary line runs
  // from its own level's row down to here, so they all reach the very bottom.
  const lastRow = rows[rows.length - 1] || { h: 30 };
  const bottomY = (rowTop[lastRow.key] || 0) + lastRow.h;
  // Where the selected unit sits, in the boundary overlay's pixel space (same page-fraction coordinates
  // as the boundary lines). null when nothing is selected, the unit's row is not drawn (an ancestor
  // hidden by focus mode), or the overlay is not measured yet.
  const selFrame = (() => {
    const n = selectedId ? byId.get(selectedId) : null;
    if (!n || !railW || rowTop[n.nodeType] == null || !startCol[n.id]) return null;
    if (groupOf[n.id] && groupOf[n.id] !== n.id) return null;   // squeezed into a sliver: nothing to frame
    const row = rows.find(r => r.key === n.nodeType);
    if (!row || !row.cells.some(c => c.id === n.id)) return null;
    const c0 = startCol[n.id] - 1, c1 = c0 + (span[n.id] || 1);
    if (cumPages[c0] == null || cumPages[c1] == null) return null;
    const left = Math.round((cumPages[c0] / totalPages) * railW);
    const right = Math.round((cumPages[c1] / totalPages) * railW);
    return { left, width: Math.max(4, right - left), top: rowTop[n.nodeType], h: row.h };
  })();
  const rollups = movie.rollups || {};
  const availableGrains = rows.map(r => (r.key === 'movie' ? 'overview' : r.key));

  // LP-demo pulse listener: rows-via-ref so the mount-only effect always sees the current
  // cell layout. The LP section dispatches `layered-read:pulse-units` with `{units: [{grain,label}, ...]}`
  // when the widget scrolls into view; we resolve those to concrete cell IDs and flash them
  // for ~1.7s so the visitor's eye finds the parts the LP copy references.
  const rowsRef = useRef(null); rowsRef.current = rows;
  useEffect(() => {
    const handler = (e) => {
      const units = e.detail?.units || [];
      if (!units.length || !rowsRef.current) return;
      const matched = new Set();
      for (const row of rowsRef.current) {
        for (const cell of (row.cells || [])) {
          for (const u of units) {
            if (u.grain && cell.nodeType !== u.grain) continue;
            if (u.label && String(cell.label) !== String(u.label)) continue;
            matched.add(cell.id);
          }
        }
      }
      if (matched.size === 0) return;
      setPulsedIds(matched);
      const t = setTimeout(() => setPulsedIds(new Set()), 1800);
      return () => clearTimeout(t);
    };
    window.addEventListener('layered-read:pulse-units', handler);
    return () => window.removeEventListener('layered-read:pulse-units', handler);
  }, []);

  // Preselect a cell on mount (LP demo `initialSelection` prop). Same {grain, label}
  // shape as pulse units. Runs once — the ref guard prevents re-firing on later renders,
  // so if the visitor navigates away from the preselected node their choice sticks.
  const preselectedRef = useRef(false);
  useEffect(() => {
    if (preselectedRef.current) return;
    if (!initialSelection || !initialSelection.grain || !rowsRef.current) return;
    const { grain, label } = initialSelection;
    for (const row of rowsRef.current) {
      for (const cell of (row.cells || [])) {
        if (cell.nodeType !== grain) continue;
        if (label && String(cell.label) !== String(label)) continue;
        preselectedRef.current = true;
        paneOpenRef.current = true;   // a preview: show it in the pane, not the modal
        openNode(cell);
        return;
      }
    }
    // If nothing matched, still flip the guard so we don't keep hunting on every render.
    preselectedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelection]);

  // The "· 7.3/10" suffix on the hover label. Returns null whenever the selected metric has no
  // honest single number, and the caller then prints no suffix at all.
  const scoreOf = (dc) => {
    if (!dc) return null;
    // 'overall' is TRIAGE — a categorical gate over the three lens statuses, not a score
    // (verdictModel.js:1-16). This used to return the unweighted mean of design/execution/read: a
    // fourth, invented quantity that exists nowhere else in the system and that the verdict model
    // exists specifically to repudiate ("NOT an average and uses NO invented weights"). The band
    // itself is already shown in the hover panel, so the right suffix here is none.
    // Fixed 2026-08-13, landmine 76.
    if (metric === 'overall') return null;
    // 'de' is a two-channel look (Design border + Execution fill), also not one number.
    if (metric === 'de') return null;
    if (['design', 'execution', 'read'].includes(metric)) return typeof dc[metric] === 'number' ? dc[metric] : null;
    const s = dc.scores?.[metric]; return s && s.score != null ? +s.score : null;
  };
  // All vertical lines (incl. scene ticks) are drawn by the single boundary overlay below, so cells carry
  // no side borders — that's what keeps every line pixel-aligned across the rows.
  const peersFor = (grain) => nodes.filter(n => n.nodeType === grain).map(n => n.detailedContent?.scores).filter(Boolean);
  const openNode = (cell) => {
    if (right?.type !== 'node' && typeof document !== 'undefined') unitOpenerRef.current = document.activeElement;   // focus returns here on close
    setNodeInPane(paneOpenRef.current); paneOpenRef.current = false;
    track('ic_unit_open', { grain: cell.nodeType, unit: cell.detailedContent?.number ?? null,
      label: cell.formattedLabel || cell.label || null, axis: AXES.includes(metric) ? metric : null, from: 'timeline' });
    setActivePanel('pane');
    setSelectedId(cell.id);
    clearPin();   // clicking any unit overturns the pinned-issue highlight so the unit is easy to see
    // when the timeline is coloured by a specific axis, open the reading focused on that axis so it
    // scrolls straight to it (matches the table's cell → axis behaviour). fromAxis lets the back button
    // return to that axis's Inspector.
    const ax = AXES.includes(metric) ? metric : null;
    const dc = cell.detailedContent;
    setRight({ type: 'node', node: ax && dc ? { ...dc, focusAxis: ax, targetAxis: ax } : dc, fromAxis: ax });
  };
  // ZOOM in/out of an act OR a sequence (zoom in place — see focusAnc/groupOf). clearPin so a stale
  // highlight doesn't dangle across the rescale.
  const enterFocus = (unitId, from) => { const n = byId.get(unitId); if (!canZoom(n)) return; track('ic_act_focus', { act: unitId, grain: n?.nodeType || null, from: from || null }); clearPin(); setHover(null); setFocusAct(unitId); };
  // Zoom out goes straight back to the whole script, never one level up: two levels of "out" is
  // where people lose their place (owner, 2026-10-14).
  const exitFocus  = (via) => { track('ic_act_unfocus', { via: via || null }); setHover(null); setFocusAct(null); };
  // ← / → while zoomed: the previous / next ZOOMABLE block in story order — acts and any root-level
  // prologue sequence, as ONE list ordered by where they start on the map. (Codex Z1: stepping through
  // "every unit of the same kind" let a prologue sequence step into a sequence inside Act 1.)
  const stepFocus = (dir) => {
    if (!focusAct) return;
    const blocks = nodes.filter(canZoom).sort((a, b) => (startCol[a.id] || 0) - (startCol[b.id] || 0));
    const nx = blocks[blocks.findIndex(n => n.id === focusAct) + dir];
    if (nx) enterFocus(nx.id, dir < 0 ? 'key_left' : 'key_right');
  };
  // ZOOM IS FOR ACTS ONLY (owner, 2026-10-14: "we probably don't want zoom on sequences"). The one
  // exception is a sequence that sits directly under the script with no act (a prologue, e.g. movie
  // 36644): there it IS a top-level block, so it zooms like an act.
  const canZoom = (n) => !!n && (n.nodeType === 'act'
    || (n.nodeType === 'sequence' && byId.get(n.parent)?.nodeType === 'movie'));
  // A sliver zooms to the unit it stands for (a lone squeezed scene just opens).
  const sliverClick = (g) => {
    if (!g) return;
    if (canZoom(g)) enterFocus(g.id, 'sliver');
    else openNode(g);
  };
  // (openAndZoom removed 2026-10-14: a click opens the reading; zoom is the "⤢ Scenes" pill / "See scenes" button.)
  // Table cell → same detail card, focused on the clicked axis (matches SoS2's handleSelectAxis contract).
  const openAxis = (n, axis) => {
    if (!n || !n.detailedContent) return;
    track('ic_unit_open', { grain: n.nodeType, unit: n.detailedContent?.number ?? null, axis: axis || null, from: 'table' });
    if (right?.type !== 'node' && typeof document !== 'undefined') unitOpenerRef.current = document.activeElement;   // focus returns here on close
    setNodeInPane(false);
    setSelectedId(n.id);
    // (no setTableDetail: a unit opens in the reading modal; an open Findings overlay stays underneath)
    // fromAxis null: a cell click did not come FROM an inspector, so the reading gets no "‹ X Inspector"
    // back bar (in the modal it would lead to a panel the table does not show).
    setRight({ type: 'node', node: { ...n.detailedContent, focusAxis: axis || null, targetAxis: axis || null }, fromAxis: null });
  };
  // DEEP LINK (`?lr=…`, grammar in deepLink.js): land on one exact unit / tab / pattern so an email,
  // a help page or the report's own "Fix first" chips can point at it instead of describing the clicks.
  // Two sources feed ONE apply path: the URL at mount (`initialTarget`) and, for the page's own widget
  // (`listenNavigate`), `sr:navigate` events from window.srNavigate (view_analysis) — in-page jumps with
  // no reload. A request waits until the nodes exist. An unresolvable target leaves the view as it is,
  // so a stale link is never worse than no link. Units mirror the click: a scene opens its reading, an
  // act or sequence also zooms in. Each request applies once; a repeat click is a new request.
  const [navReq, setNavReq] = useState(() => (initialTarget ? { raw: String(initialTarget), seq: 0 } : null));
  const navSeqRef = useRef(0);
  useEffect(() => {
    if (!listenNavigate) return undefined;
    const onNav = (e) => {
      const raw = e && e.detail && e.detail.lr;
      if (!raw) return;
      navSeqRef.current += 1;
      setNavReq({ raw: String(raw), seq: navSeqRef.current });
    };
    window.addEventListener('sr:navigate', onNav);
    return () => window.removeEventListener('sr:navigate', onNav);
  }, [listenNavigate]);
  const appliedNavRef = useRef(null);
  useEffect(() => {
    if (!navReq || !nodes.length || appliedNavRef.current === navReq) return;
    appliedNavRef.current = navReq;
    const t = parseLrTarget(navReq.raw);
    let ok = false;
    if (t) {
      const go = () => { setActivePanel('pane'); if (phoneNow()) setMView('read'); };
      if (t.kind === 'findings') { go(); setRight({ type: 'findings' }); ok = true; }
      else if (t.kind === 'axes') { go(); setRight({ type: 'axes', axis: AXES.includes(t.axis) ? t.axis : null }); ok = true; }
      else if (t.kind === 'patterns') { go(); setRight({ type: 'rollup', grain: t.grain, focus: t.focus }); ok = true; }
      else if (t.kind === 'unit') {
        const n = resolveLrUnit(nodes, t.id);
        if (n && n.detailedContent) {
          go();
          const ax = AXES.includes(t.axis) ? t.axis : null;
          setSelectedId(n.id);
          clearPin();
          setNodeInPane(false);   // a deep link is a writer's request — open the reading
          setRight({ type: 'node', node: ax ? { ...n.detailedContent, focusAxis: ax, targetAxis: ax } : n.detailedContent, fromAxis: ax });
          // (no zoom: a click opens, it does not zoom — the link mirrors the click)
          ok = true;
        }
      }
    }
    track('ic_deeplink', { target: navReq.raw.slice(0, 60), kind: t ? t.kind : 'invalid', ok, source: navReq.seq ? 'nav' : 'url' });
    // Tell window.srNavigate whether the target resolved (it scrolls only to something that opened).
    try { window.dispatchEvent(new CustomEvent('sr:navigated', { detail: { lr: navReq.raw, ok } })); } catch (err) { /* old browsers */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navReq, nodes]);
  // ⚠️ The table overlay's open/closed state is its OWN flag, not `right?.type === 'node'`.
  // Deriving it from `right` was a regression (caught by the owner 2026-08-21): the old table layout
  // had a permanently-open right drawer, and that drawer was the ONLY place Findings / Axes /
  // Patterns / Shape lived in Table view. Showing the overlay only for a selected unit deleted them
  // from this view entirely. With an explicit flag the overlay can host ANY `pane` content —
  // a unit reading or the script-level panels — and closing it means "closed", not "show findings"
  // (which would just re-open it).
  const [tableDetail, setTableDetail] = useState(false);
  const closeTableDetail = () => { setTableDetail(false); setSelectedId(null); };
  // Toolbar entry point: open the script-level panels without having to pick a unit first.
  const openTablePane = () => {
    setRight(prev => (prev && prev.type !== 'node') ? prev : { type: 'findings' });
    setSelectedId(null);
    setTableDetail(true);
  };
  // Escape closes the table overlay. Bound only while it is open so it cannot swallow Escape from
  // the wiki drawer, the chat, or anything else that wants it.
  useEffect(() => {
    if (view !== 'table' || !tableDetail) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); closeTableDetail(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, tableDetail]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Table column-header → open that axis's Inspector in the drawer (parity with the timeline).
  const openAxisInspector = (axis) => { if (AXES.includes(axis)) { track('ic_axis_inspector_open', { axis, from: 'table_header' }); setRight({ type: 'axes', axis }); clearPin(); } };
  // NO DEAD ENDS: from any axis in a unit's reading, open the docked AI chat seeded with THIS unit + axis
  // so a weak axis always has a live next step (talk it through), even when no fixes were pre-written.
  // `labelOverride` lets a NON-DER subject use this same path — Craft Read criteria are not in
  // METRIC_LABELS (they are the traditional battery, not the twelve axes), so without it the chat
  // header would show a raw key like `emotional_impact`.
  // ONE explicit-unit handler for the page chat (List, Table, the pane): the caller says WHICH unit, so a
  // question can never pick up a stale `right` (review M3/I6). The dock has its own bound queue (askInDock).
  const discussAxis = (axis, seed, labelOverride = null) => discussUnit(right?.type === 'node' ? right.node : null, axis, seed, labelOverride);
  const discussUnit = (dc, axis, seed, labelOverride = null) => {
    const title = dc ? (unitDisplayLabel(dc, 'list') || dc.title || dc.label || '') : '';
    const axisLabel = labelOverride || METRIC_LABELS[axis] || axis;
    track('ic_chat_open', { axis, from: 'unit_axis', unit: dc?.number ?? null });
    markExplicitSubject(dc ? `unit:${uidOfDc(dc)}` : 'script');
    setActivePanel('chat');
    setChatSubject({
      axis,
      axisLabel,
      candidateId: null,
      unitId: dc?.unitId || dc?.unit_id || dc?.id || null,
      title,
    });
    // seed === false → "ask your own": open the chat blank (no auto-sent question).
    if (seed === false) setChatSeed(null);
    else setChatSeed({ question: seed || `How can I strengthen ${axisLabel}${title ? ` in ${title}` : ''}? Give concrete, page-level moves.`, nonce: Date.now() });
    setChatOpenPersist(true, 'unit_axis');
  };
  // When the detail opens/changes. If a specific axis is focused, NodePanel scrolls its own container to
  // that axis (block:'start'), so we DON'T also scroll here (that would fight it). Otherwise: TABLE resets
  // the drawer to the top; TIMELINE tweens the below-card into view (native smooth no-ops in this nested
  // scroller, so we animate scrollTop).
  const focusedAxis = right?.type === 'node' && (right.node?.focusAxis || right.node?.targetAxis);
  useEffect(() => {
    if (!right || focusedAxis || unitModal) return undefined;   // the modal has its own scroller
    if (view === 'table') { if (drawerRef.current) drawerRef.current.scrollTop = 0; return undefined; }
    if (!cardRef.current || !contentRef.current) return undefined;
    const scroller = contentRef.current;
    const start = scroller.scrollTop;
    const target = start + (cardRef.current.getBoundingClientRect().top - scroller.getBoundingClientRect().top) - 8;
    const dist = target - start;
    if (Math.abs(dist) < 4) return undefined;
    const t0 = performance.now(); const dur = 340; let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      scroller.scrollTop = start + dist * (1 - Math.pow(1 - p, 3)); // easeOutCubic
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [right, view]);

  // Cross-row hover sync: a cell stays lit if it's the hovered cell, an ANCESTOR of it (its sequence/
  // act/script), or a DESCENDANT of it (its sequences/scenes); everything else dims.
  const ancestorsOf = (id) => { const s = new Set(); let p = byId.get(id)?.parent, g = 0; while (p && g++ < 8) { s.add(p); p = byId.get(p)?.parent; } return s; };
  // scene number → scene-node id (der refs encode SCENE numbers at every grain, so a pattern's unit_refs
  // map to a scene set; light those scenes + their ancestor sequences/acts so a pattern's cross-grain
  // footprint lights up — a contiguous run vs a scatter reads straight off the timeline).
  const sceneNumToId = useMemo(() => { const m = {}; sceneNodes.forEach(s => { const n = s.detailedContent?.number; if (n != null) m[n] = s.id; }); return m; }, [sceneNodes]);
  // metaOverride: the caller's own coverage phrase (e.g. the card's "17 of 42 scenes") — the refs
  // often name only EXEMPLAR units (spec caps them), so computed counts can contradict the card.
  const showOnTimeline = (refs, cid, label, metaOverride, kind) => {
    const parsed = []; const named = { scene: new Set(), sequence: new Set(), act: new Set() };
    (refs || []).forEach(ref => {
      // inspector issues pass refs as strings; der_synthesis footprints pass objects {ref, intensity, …}
      const str = typeof ref === 'string' ? ref : (ref && ref.ref) || '';
      const m = /der:unit:(scene|sequence|act):(\d+)_(\d+)/.exec(str); if (!m) return;
      named[m[1]].add(m[2] + '_' + m[3]);
      parsed.push({ grain: m[1], a: +m[2], b: +m[3] });
    });
    // Light only the FINEST grain the refs name: act-grain refs on a script-wide pattern expand to
    // every cell, which reads as "nothing pinned". Scene/sequence exemplars give the footprint pop.
    const finest = named.scene.size ? 'scene' : named.sequence.size ? 'sequence' : 'act';
    const lit = new Set(); const sceneSet = new Set();
    parsed.filter(p => p.grain === finest).forEach(p => {
      for (let s = p.a; s <= p.b; s++) { const id = sceneNumToId[s]; if (id) { lit.add(id); sceneSet.add(id); ancestorsOf(id).forEach(x => lit.add(x)); } }
    });
    const meta = metaOverride
      || (finest === 'scene' ? `${sceneSet.size} scene${sceneSet.size === 1 ? '' : 's'}`
        : `${named[finest].size} ${finest}${named[finest].size === 1 ? '' : 's'}`);
    if (lit.size) track('ic_issue_pin', { axis: AXES.includes(metric) ? metric : (right?.type === 'axis' ? right.axis : null), candidate_id: cid || null, label: label || null, scenes: sceneSet.size, meta });
    setPinned(lit.size ? lit : null);
    setPinnedLabel(lit.size ? { text: label || 'selected pattern', meta, kind: kind || null } : null);
    // one pulse on the lit units each time something new is pinned, so the eye finds them
    if (lit.size) { setPinFlash(false); requestAnimationFrame(() => setPinFlash(true)); clearTimeout(pinFlashT.current); pinFlashT.current = setTimeout(() => setPinFlash(false), 1600); }
  };
  const clearPin = () => { setPinned(null); setPinnedLabel(null); setActiveFindingIdx(null); };
  // The chat SUBJECT FOLLOWS THE SELECTION: axis open → that axis's thread; back to findings/patterns
  // → the whole-script thread. AxisChat keys threads per subject, so switching loses nothing.
  // (discussAxis/onAsk still set a more specific subject at ask time — this only reacts to axisKey.)
  // WHAT THE PAGE CHAT FOLLOWS: what the pane is showing — a unit, an axis, or the script. Under the
  // desktop unit modal the pane keeps showing its previous content (paneRight), and the modal has its own
  // dock, so the page chat follows the pane, not the modal. Units are part of the identity (Codex
  // I_unit_navigation_context): two different units used to both read as "no axis", so moving from one
  // to the next left the chat on the first.
  const followTarget = unitModal ? paneRight : right;
  const followAxis = followTarget?.type === 'axis' ? followTarget.axis : (followTarget?.type === 'axes' ? followTarget.axis || null : null);
  const uidOfDc = (dc) => (dc ? String(dc.unitId || dc.unit_id || dc.id || '') : '');
  const followUnit = followTarget?.type === 'node' ? followTarget.node : null;
  const followKey = followUnit ? `unit:${uidOfDc(followUnit)}` : (followAxis ? `axis:${followAxis}` : 'script');
  // ⚠️ RETURN TO THE CONVERSATION YOU HAD (owner, 2026-10-14: "selected Economy, asked a question, it
  // answered; clicked away and back and it was gone"). A question asked from an Inspector pattern lives
  // in that PATTERN's thread (pattern:<axis>:<candidate>, seeded server-side with the pattern's context),
  // but coming back to the axis used to reset the subject to the bare axis thread, which was empty. The
  // transcript was still in the session store; the chat was just looking at a different thread. Now the
  // axis reopens on whichever thread you last SENT in for that axis.
  const axisThreadRef = useRef({});   // axis → the subject last sent in, at script level
  const noteAxisSend = useCallback((sj) => {
    if (!sj) return;
    if (sj.axis && !sj.unitId && sj.axis !== '_script') { axisThreadRef.current[sj.axis] = sj; sessionAxesRef.current.add(sj.axis); }
    const key = chatKeyOf(sj);
    setChatMeta(m => ({ ...m, [key]: { updatedAt: Date.now(), subject: { axis: sj.axis, candidateId: sj.candidateId || null, unitId: sj.unitId || null } } }));
  }, []);
  // An EXPLICIT choice (picking a unit conversation, "ask about this unit") must survive the navigation
  // it causes: opening a unit from an axis flips followAxis to null, and this effect used to clear the
  // unit subject it had just been given (Codex G_unit_subject_overwritten). The choice records the ONE
  // followAxis value its own navigation will produce; the effect skips exactly that transition and
  // forgets the token on any other, so the writer's next navigation follows at once, however soon it
  // comes (Codex H_follow_window_blocks_navigation — a time window swallowed genuine navigation).
  const pendingFollowRef = useRef(undefined);   // undefined = nothing pending; else the expected followKey
  const markExplicitSubject = (expectedKey) => { pendingFollowRef.current = expectedKey || 'script'; };
  const sessionAxesRef = useRef(new Set());   // axes whose thread was chosen or used THIS session (beat saved ones)
  useEffect(() => {
    const pending = pendingFollowRef.current;
    pendingFollowRef.current = undefined;
    if (pending !== undefined && pending === followKey) return;   // the explicit choice's own transition
    if (followUnit) {
      // a unit in the pane → that unit's one conversation (the axis is per-question context)
      const dc = followUnit;
      const grain = dc.type === 'movie' ? 'script' : (dc.type || 'unit');
      setChatSubject({ axis: '_unit', axisLabel: `The whole ${grain}`, candidateId: null, unitId: uidOfDc(dc) || null,
        title: unitDisplayLabel(dc, 'list') || dc.title || '' });
    } else if (followAxis) setChatSubject(axisThreadRef.current[followAxis]
      || { axis: followAxis, axisLabel: METRIC_LABELS[followAxis] || followAxis, candidateId: null, title: null });
    else setChatSubject(null);   // null = the whole-script subject
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followKey]);
  const scriptSubject = useMemo(() => ({ axis: '_script', axisLabel: 'this script', candidateId: null, title: movie.movie?.title || null }), [movie]);
  const effectiveSubject = chatSubject || scriptSubject;
  // static starter chips for the whole-script thread, derived from the synthesis — no tokens until asked
  const chatStarters = useMemo(() => {
    if (effectiveSubject.axis !== '_script') return null;
    const g = movie.der_synthesis?.writer_view?.tier_1_glance || null;
    const primary = (g?.pattern_cards || []).find(c => c.role === 'primary_rewrite_pattern');
    const qs = ['Where should I start, and why?'];
    if (primary?.title) qs.push(`What would fixing “${primary.title}” change downstream?`);
    qs.push('Which three scenes should I rewrite first?');
    return qs;
  }, [effectiveSubject.axis, movie]);

  // Esc = back to Findings (home) from anywhere — unless the writer is typing (chat textarea).
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      // Only when the MAP owns the keyboard: nothing focused, or focus inside the map. A focused chat
      // chip, tab or any other control keeps its own arrow keys (Codex Z2).
      const t = e.target;
      const mapOwnsKeys = !t || t === document.body || t === document.documentElement
        || (typeof t.closest === 'function' && !!t.closest('#ic-map'));
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && focusAct && view === 'timeline' && !unitModal && mapOwnsKeys
        && !e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
        e.preventDefault(); stepFocus(e.key === 'ArrowLeft' ? -1 : 1); return;
      }
      if (e.key !== 'Escape') return;
      if (unitModal && dockOpen) { setDockOpen(false, 'esc'); return; }   // order: nested dialog (own handler) → chat dock → reading → zoom → home
      if (unitModal) { closeUnitModal('esc'); return; }
      if (focusAct) { exitFocus('esc'); return; }   // zoom out (all the way: one level of out)
      if (right?.type === 'axes' && right.axis) { setRight({ type: 'axes', axis: null }); return; }   // collapse detail first
      goFindings('esc');   // home = the read
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Pulse-highlight a target element (the orientation card's "clicking something shows it on
  // the screen" behaviour). Re-triggers cleanly by forcing a reflow between class toggles.
  const pulse = (sel) => {
    const el = document.querySelector(sel);
    if (!el) return;
    el.classList.remove('ic-pulse'); void el.offsetWidth; el.classList.add('ic-pulse');
    track('ic_orient_pulse', { target: sel });
  };

  // Echo a REMOTE colour-by change on the dropdown. Clicking an axis (or Back/Forward) recolours the map by
  // calling setMetric from elsewhere — the writer's eye is on the axis list or the map, not the header — so
  // the "Colour by" control silently changes value. A one-shot pulse ties the action to the control that
  // moved. We DON'T pulse when the writer used the dropdown themselves (metricSelfRef) — that's redundant.
  const metricSelfRef = useRef(false);   // set by the select's own onChange → suppress the echo pulse
  const metricMountRef = useRef(true);
  useEffect(() => {
    if (metricMountRef.current) { metricMountRef.current = false; return; }   // ignore the initial value
    if (metricSelfRef.current) { metricSelfRef.current = false; return; }     // the writer just used the dropdown
    if (view !== 'timeline') return;   // the select only exists in timeline view
    const id = requestAnimationFrame(() => pulse('#ic-metric-select'));       // after the value has repainted
    return () => cancelAnimationFrame(id);
  }, [metric, view]);

  // Guided tour (Shepherd, mirrors the SoS wheel): auto-runs once on first visit; always
  // re-launchable from the orientation card. Fresh tour per start; timeline view is its stage.
  const startTour = (from) => {
    setView('timeline');
    track('ic_tour_start', { from });
    setTimeout(() => buildIcicleTour({ onDone: (status) => track('ic_tour_end', { status }) }).start(), 180);
  };
  useEffect(() => {
    // publicView also blocks auto-tour: anonymous / showcase / share-link viewers didn't ask
    // for onboarding — an unsolicited Shepherd popup on a demo/LP surface reads as an ad. The
    // tour is still available via the orientation-card link and #ic-tour-btn.
    if (embedded || publicView || typeof window === 'undefined' || window.innerWidth < 900 || !shouldAutoStartIcicleTour()) return undefined;
    const t = setTimeout(() => startTour('auto'), 700);   // after first paint + measure
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // RETURNING visitors don't get the auto-tour, so the pill can go unnoticed. Draw the eye to it ONCE per
  // session with a single pulse (same navy glow, reduced-motion aware) — a nudge, not a nag.
  // Also skip the pulse on publicView — demo visitors shouldn't be led into the tour at all.
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth < 900) return undefined;
    if (publicView) return undefined;                    // no pulse on public/demo surfaces
    if (shouldAutoStartIcicleTour()) return undefined;   // first-timers get the full tour instead
    try { if (sessionStorage.getItem('ic_tour_hint')) return undefined; } catch (e) { /* private mode */ }
    const t = setTimeout(() => { pulse('#ic-tour-btn'); try { sessionStorage.setItem('ic_tour_hint', '1'); } catch (e) { /* ignore */ } }, 1100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hNode = hover?.node || null;
  const hAnc = hNode ? ancestorsOf(hNode.id) : null;
  const related = (cell) => !hNode || cell.id === hNode.id || (hAnc && hAnc.has(cell.id)) || ancestorsOf(cell.id).has(hNode.id);
  // a cell's on-screen width in px = its share of total pages × the grid width (widths are page-weighted).
  const cellWidth = (cell) => {
    const c0 = (startCol[cell.id] || 1) - 1, c1 = c0 + (span[cell.id] || 1);
    return (((cumPages[c1] ?? 0) - (cumPages[c0] ?? 0)) / totalPages) * gridW;
  };

  // sequence numbers reset per act; show the within-act ordinal ("Seq 4") — the act row + alignment give
  // the act context, and hover/right-pane carry the full label.
  const seqOrdinal = useMemo(() => {
    const m = {}; const byParent = {};
    nodes.filter(n => n.nodeType === 'sequence').forEach(s => { (byParent[s.parent] = byParent[s.parent] || []).push(s); });
    Object.values(byParent).forEach(list => list.sort((a, b) => unitSortKey(a) - unitSortKey(b)).forEach((s, i) => { m[s.id] = i + 1; }));
    return m;
  }, [nodes]);

  // ⚠️ seqOrdinal ABOVE IS POSITIONAL, NOT THE UNIT'S OWN NUMBER — a 1-based index within the parent
  // act, recomputed from sort order. The two helpers below prefer a server-supplied label over it;
  // see unitLabels.js for why, and unitLabels.selftest.mjs for the cases. They live there rather
  // than here because a closure inside this component cannot be tested.
  const dcOf = (n) => (n && n.detailedContent) || {};
  const seqLabelFull  = (n) => sequenceCellLabel(n, seqOrdinal[n.id]);
  const seqLabelShort = (n) => sequenceCellCode(n, seqOrdinal[n.id]);

  // ── THE VERTICAL MAP ────────────────────────────────────────────────────────────────────────
  // Story order runs DOWN; acts and sequences are the left-hand columns their scenes span, with
  // sticky labels; every scene carries its NUMBER AND TITLE. ONE implementation, two callers: the
  // phone branch (which has always had it) and the desktop `view === 'list'` (added 2026-09-04).
  // A plain function rather than a component, so it closes over nodes/span/metric/pinned/selectedId
  // and cannot remount on every render.
  //
  // ⚠️ THIS IS THE TIMELINE, ROTATED — same colours, same click target, same reading pane. The two
  // exist because they answer different questions and each is bad at the other's: the timeline
  // shows the whole script's SHAPE at a glance and physically cannot show a scene's name (median
  // cell 20px); the list names every scene and cannot show the shape (60 scenes ≈ 2,000px of
  // scrolling). Do not "unify" them by deleting one.
  //
  // `rowH` is the caller's, and it is the one real difference between the two. On a phone the floor
  // IS the tap target (44px), which costs the page-length metaphor — a scene must exceed 3.7 pages
  // before its height means anything. On desktop the floor drops and the multiplier rises, so
  // height reads as length again.
  // ⚠️ Returns the GRID ONLY — the caller owns the scroll container, because the phone branch
  // already has one (with its own sticky pinned-footprint header inside it) and nesting a second
  // scroller inside it would trap the scroll.
  // `rich` is a LEVEL, not a flag: 2 = page count + the three lens scores, 1 = lens scores only,
  // 0 = neither. The extras are worth having but never at the cost of the TITLE, which is the
  // whole reason this view exists — so they drop off as the column narrows, the same 'as much as
  // fits' ladder cellLabel() uses. Side-by-side (list beside the reading) lands on 1; full-width
  // (list above the reading) lands on 2; the phone gets 0.
  const verticalMap = ({ rowH, stickyTop, pad, onOpen, rich = 0, markSelected = rich > 0 }) => {
    const ordered = sceneNodes.slice().sort((a, b) => unitSortKey(a) - unitSortKey(b));
    const heights = ordered.map(rowH);
    const starts = {};   // 1-based grid row where each act/sequence begins, from its first scene
    ordered.forEach((sc, i) => {
      let p = sc.parent, g = 0;
      while (p && byId.has(p) && g++ < 8) { if (starts[p] == null) starts[p] = i + 1; p = byId.get(p).parent; }
    });
    // TV pilots and shorts often have NO acts; reserving the column leaves a dead strip (reported).
    const hasActs = nodes.some(n => n.nodeType === 'act');
    const seqCol = hasActs ? 2 : 1, sceneCol = hasActs ? 3 : 2;
    const dim = (id) => (pinned ? (pinned.has(id) ? 1 : 0.25) : 1);
    // the same "you have a conversation here" dot as the timeline
    const chatted = (n) => { const d = n && n.detailedContent; const u = d && (d.unitId || d.unit_id || d.id); return !!(u && chattedUnits.has(String(u))); };
    const vDot = (n) => (chatted(n) ? <span aria-hidden title="You have a conversation here" style={{ position: 'absolute', top: 4, left: 4, width: 8, height: 8, borderRadius: 999, background: ASK_TEAL, boxShadow: '0 0 0 1.5px #fff' }} /> : null);
    const spanLabel = (n, label, size) => (
      <span style={{ position: 'sticky', top: stickyTop, display: 'block', textAlign: 'center', padding: '6px 2px',
        color: textOn(softFill(n.color)), fontWeight: 700, fontSize: size, lineHeight: 1.15 }}>{label}</span>
    );
    return (
        <div style={{ display: 'grid', gridTemplateColumns: hasActs ? '50px 46px 1fr' : '46px 1fr', gap: 2, padding: pad,
          gridTemplateRows: heights.map(h => `${h}px`).join(' ') }}>
          {hasActs && nodes.filter(n => n.nodeType === 'act').map(a => (
            <button key={a.id} type="button" onClick={() => onOpen(a)}
              style={{ gridColumn: 1, gridRow: `${starts[a.id]} / span ${span[a.id] || 1}`, borderRadius: 4,
                border: 'none', padding: 0, cursor: 'pointer', width: '100%', position: 'relative',
                background: softFill(a.color), overflow: 'hidden', opacity: dim(a.id) }}>
              {vDot(a)}{spanLabel(a, a.formattedLabel || a.label, 11)}
            </button>
          ))}
          {nodes.filter(n => n.nodeType === 'sequence').map(sq => (
            <button key={sq.id} type="button" onClick={() => onOpen(sq)}
              style={{ gridColumn: seqCol, gridRow: `${starts[sq.id]} / span ${span[sq.id] || 1}`, borderRadius: 4,
                border: 'none', padding: 0, cursor: 'pointer', width: '100%', position: 'relative',
                background: softFill(sq.color), overflow: 'hidden', opacity: dim(sq.id) }}>
              {vDot(sq)}{spanLabel(sq, seqLabelFull(sq), 10.5)}
            </button>
          ))}
          {ordered.map((sc, i) => {
            const de = metric === 'de' ? deFill(sc.detailedContent) : null;
            const bg = de ? s10(sc.detailedContent.execution) : softFill(sc.color);
            const title = sc.detailedContent?.title || '';
            const pages = +(sc.detailedContent?.page_count) || null;
            // ⚠️ Selection is its own switch, not `rich > 0`. The side-by-side list runs at rich 0
            // (titles only), which silently dropped the outline on the scene you had just opened.
            const sel = markSelected && selectedId === sc.id;
            return (
              <button key={sc.id} type="button" onClick={() => onOpen(sc)}
                style={{ gridColumn: sceneCol, gridRow: i + 1, border: 'none', borderRadius: 4, background: de || bg,
                  color: textOn(bg), textAlign: 'left', padding: '0 10px', cursor: 'pointer', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, opacity: dim(sc.id),
                  outline: sel ? `3px solid ${NAVY}` : (pinned && pinned.has(sc.id) ? `2px solid ${NAVY}` : 'none'),
                  outlineOffset: -2 }}>
                <span style={{ fontWeight: 800 }}>{sc.detailedContent?.number}</span>
                {chatted(sc) && <ChatMark size={14} />}
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{title}</span>
                {/* `rich` = the desktop list, whose row is ~1,300px wide for a ~40-character title.
                    The spare width earns its keep: page length (which the row height can only
                    approximate) and the three lens scores — the framework restated once per scene,
                    which is the cheapest teaching there is. Off on a phone, where there is no room. */}
                {rich >= 2 && pages != null && (
                  <span style={{ flexShrink: 0, opacity: .75, fontSize: 11.5, fontWeight: 600 }}>{fmtScore(pages)}pg</span>
                )}
                {rich >= 1 && LENSES.map(L => {
                  const v = sc.detailedContent?.scores?.[L.key]?.score;
                  if (v == null) return null;
                  return (
                    <span key={L.key} {...tip(`${L.label} ${fmtScore(v)} / 10`)}
                      style={{ flexShrink: 0, width: 26, textAlign: 'center', fontSize: 11, fontWeight: 800,
                        borderRadius: 4, padding: '1px 0', background: 'rgba(255,255,255,.72)', color: '#0f172a' }}>
                      {fmtScore(v)}
                    </span>
                  );
                })}
              </button>
            );
          })}
        </div>
    );
  };

  // Width-adaptive label: as much as fits, then less, then nothing (colour + hover carry the rest).
  //
  // THE SCENE TITLE IS THE POINT OF ZOOMING (owner, 2026-09-04). Zoom is not magnification for its
  // own sake — each level buys a denser label, because each level gives every scene more width:
  //   whole script (~20px/scene) → nothing, just shape
  //   act          (~45px/scene) → the scene number
  //   sequence    (~300px/scene) → the number AND its title
  // That is what answers "isn't a sequence zoom too sparse?" — the space is spent on the one thing
  // the desktop map could never show and the phone layout always could. The thresholds are measured
  // against the real string, not guessed: ~7px per character at this size, plus the number and a gap.
  const cellLabel = (cell) => {
    const w = cellWidth(cell);
    // The LEFT RAIL already says "Script"; repeating it in the band is a label naming its own row.
    // The title is the one thing about the whole-script row the rail cannot say.
    if (cell.nodeType === 'movie') return (dcOf(cell).title || '').trim() || 'Script';
    if (cell.nodeType === 'act') return cell.formattedLabel || cell.label;
    if (cell.nodeType === 'sequence') return w >= 46 ? seqLabelFull(cell) : (w >= 15 ? seqLabelShort(cell) : '');
    if (cell.nodeType === 'scene') {
      const num = cell.detailedContent?.number;
      if (w < 14) return '';
      const title = (cell.detailedContent?.title || '').trim();
      // Only when the WHOLE title fits. A clipped title is worse than none — it reads as a bug, and
      // `overflow: hidden` on the cell would cut it mid-word with no ellipsis.
      if (title && w >= (title.length * 7 + 34)) return `${num}  ${title}`;
      return `${num}`;
    }
    return '';
  };

  // contextual readout: unit · pages · score (earns its header space; idle = empty, not a nag)
  // ---- mobile derived data: vertical map rows (story order DOWN), unit grid positions ----
  const mOrdered = useMemo(() => sceneNodes.slice().sort((a, b) => unitSortKey(a) - unitSortKey(b)), [sceneNodes]);
  // 44px minimum: the tap target IS the argument for the vertical map (GPT review caught 34 contradicting it)
  const mHeights = useMemo(() => mOrdered.map(s => Math.max(44, (+(s.detailedContent?.page_count) || 1) * 12)), [mOrdered]);
  const mCum = useMemo(() => { const c = [0]; mHeights.forEach(h => c.push(c[c.length - 1] + h + 2)); return c; }, [mHeights]);
  const [mCtx, setMCtx] = useState(null);   // sticky "where am I" while scrolling: {act, seq}
  const onMapScroll = (e) => {
    const y = e.target.scrollTop + 70;
    let i = 0; while (i < mCum.length - 2 && mCum[i + 1] < y) i++;
    const sc = mOrdered[i]; if (!sc) return;
    const seq = byId.get(sc.parent); const act = seq ? byId.get(seq.parent) : null;
    const next = { act: act ? (act.formattedLabel || act.label) : null, seq: seq ? seqLabelFull(seq) : null };
    setMCtx(prev => (prev && prev.act === next.act && prev.seq === next.seq) ? prev : next);
  };
  const mStarts = useMemo(() => {
    const st = {};
    ['act', 'sequence'].forEach(type => {
      let acc = 0;
      nodes.filter(n => n.nodeType === type).sort((a, b) => unitSortKey(a) - unitSortKey(b))
        .forEach(u => { st[u.id] = acc + 1; acc += span[u.id] || 0; });
    });
    return st;
  }, [nodes, span]);
  const mMapRef = useRef(null);        // the vertical map's scroll container
  const mSavedScroll = useRef(null);   // saved on modal open; restored on close (browser heuristics drift)
  const mRestoreScroll = () => requestAnimationFrame(() => {
    if (mMapRef.current && mSavedScroll.current != null) mMapRef.current.scrollTop = mSavedScroll.current;
  });
  const mOpenUnit = (cell) => {
    track('ic_unit_open', { grain: cell.nodeType, unit: cell.detailedContent?.number ?? null, from: 'mobile_map' });
    if (mMapRef.current) mSavedScroll.current = mMapRef.current.scrollTop;
    if (cell.detailedContent) setMUnit(cell.detailedContent);
  };
  // Browser Back closes the full-screen reading (a route that LOOKS like a modal). The map never
  // unmounts underneath, so scroll position survives for free.
  const mPushedRef = useRef(false);
  useEffect(() => {
    if (!isPhone) return;
    const open = !!(mUnit || mAxis);
    if (open && !mPushedRef.current) { mPushedRef.current = true; try { window.history.pushState({ icModal: 1 }, ''); } catch (e) { /* ignore */ } }
    if (!open) mPushedRef.current = false;
  }, [mUnit, mAxis, isPhone]);
  // Phone Back collapses an open chat dock BEFORE it closes the reading: the dock gets its own history
  // entry while open. Collapsing it any other way (▾, Esc, next scene) pops that entry silently.
  const dockPushedRef = useRef(false);
  const ignorePopRef = useRef(false);
  useEffect(() => {
    if (!isPhone || !mUnit) return;
    if (dockOpen && !dockPushedRef.current) { dockPushedRef.current = true; try { window.history.pushState({ icDock: 1 }, ''); } catch (e) { /* ignore */ } }
    if (!dockOpen && dockPushedRef.current) { dockPushedRef.current = false; ignorePopRef.current = true; try { window.history.back(); } catch (e) { ignorePopRef.current = false; } }
  }, [dockOpen, isPhone, mUnit]);
  useEffect(() => {
    if (!isPhone) return undefined;
    const onPop = () => {
      if (ignorePopRef.current) { ignorePopRef.current = false; return; }
      if (dockPushedRef.current) { dockPushedRef.current = false; setDockOpenRaw(false); setDockTyping(false); return; }
      setMUnit(null); setMAxis(null); mRestoreScroll();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhone]);
  const mCloseModal = () => {
    setMUnit(null); setMAxis(null);
    if (mPushedRef.current && dockPushedRef.current) {
      // both the reading and its open dock have history entries — drop both in one step
      mPushedRef.current = false; dockPushedRef.current = false; ignorePopRef.current = true;
      try { window.history.go(-2); } catch (e) { ignorePopRef.current = false; }
    } else if (mPushedRef.current) { mPushedRef.current = false; try { window.history.back(); } catch (e) { /* ignore */ } }
    mRestoreScroll();
  };
  // prev/next scene inside the reading — close-hunt-reopen gets old fast on a phone
  const mSceneIdx = mUnit && mUnit.type === 'scene' ? mOrdered.findIndex(s => s.detailedContent?.number === mUnit.number) : -1;
  const mStepScene = (d) => {
    const nx = mOrdered[mSceneIdx + d];
    if (nx && nx.detailedContent) { track('ic_unit_open', { grain: 'scene', unit: nx.detailedContent.number, from: 'mobile_prevnext' }); setMUnit(nx.detailedContent); }
  };

  // ── UNIT CHAT DOCK (owner, 2026-10-14) ───────────────────────────────────────────────────────────
  // The unit currently open in a reading (desktop modal or phone sheet).
  const dockUnit = unitModal ? (right?.node || null) : (isPhone && mUnit ? mUnit : null);
  const dockUnitKey = dockUnit ? (dockUnit.unitId || dockUnit.unit_id || dockUnit.id || `${dockUnit.type}:${dockUnit.number}`) : null;
  // Topic follows the reading: a different unit starts on the whole unit, with no queued question.
  useEffect(() => { setDockAxis(null); setDockTyping(false); }, [dockUnitKey]);
  // A reading always opens with the chat closed (the reading comes first). Prev/next keeps it open.
  const readingOpen = !!dockUnit;
  useEffect(() => { if (!readingOpen) setDockOpenRaw(false); }, [readingOpen]);
  // Ask from inside a reading: open the dock on this unit, topic = that axis, question queued (or the
  // input focused for "Ask your own"). Replaces the old close-the-reading-then-open-the-page-chat path,
  // which on the phone also dropped the unit and the question.
  const askInDock = (axis, seed, labelOverride = null) => {
    const ax = axis && axis !== '_unit' ? axis : null;
    setDockAxis(ax);
    if (!dockOpenRef.current) setDockOpen(true, 'ask');
    if (seed === false || !dockUnit) return;   // "Ask your own": open on the topic, no question sent
    const title = unitDisplayLabel(dockUnit, 'list') || dockUnit.title || '';
    const grain = dockUnit.type === 'movie' ? 'script' : (dockUnit.type || 'unit');
    const label = labelOverride || (ax ? (METRIC_LABELS[ax] || ax) : null);
    const q = seed || (label ? `How can I strengthen ${label}${title ? ` in ${title}` : ''}? Give concrete, page-level moves.` : null);
    if (!q) return;
    dockQSeq.current += 1;
    const subject = { axis: ax || '_unit', axisLabel: ax ? (METRIC_LABELS[ax] || ax) : `The whole ${grain}`,
      unitId: dockUnit.unitId || dockUnit.unit_id || dockUnit.id || null, title };
    setDockQueue(qu => [...qu, { id: `dq${dockQSeq.current}`, question: q, subject }]);
  };
  // Starter chips, built from what is on screen — free until tapped.
  const dockStarters = (dc) => {
    const grain = dc?.type === 'movie' ? 'script' : (dc?.type || 'unit');
    const mis = (Array.isArray(dc?.prose?.the_read?.misfiring) ? dc.prose.the_read.misfiring : [])
      .map(t => (typeof t === 'string' ? t : (t && (t.text || t.point)) || '')).filter(Boolean).slice(0, 2);
    return [...mis.map(t => `How could I fix this: “${t.replace(/[.\s]+$/, '')}”?`), `What single change would most improve this ${grain}?`];
  };
  // vh of the phone's VISIBLE viewport (shrinks when the keyboard opens) so the chat input never hides.
  const [vvH, setVvH] = useState(null);
  useEffect(() => {
    if (!isPhone || typeof window === 'undefined' || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    const f = () => setVvH(Math.round(vv.height));
    f(); vv.addEventListener('resize', f);
    return () => vv.removeEventListener('resize', f);
  }, [isPhone]);
  // The way into the chat: a labelled button in the reading's HEADER, next to prev/next and ✕ — always
  // visible, costs no reading height. Pressed state while the dock is open (it toggles).
  // no chat on the public LP demo, nor without a script to talk about: the series report's season read has no
  // hash (one season, five scripts), and /assistant/axis-inspector-chat needs one (2026-10-31)
  const chatAllowed = !(publicView && embedded) && !!movie?.hash;
  // ── WHERE YOU HAVE CONVERSATIONS ──────────────────────────────────────────────────────────────
  // Units map to their node by the same id the unit chat sends (dc.unitId || dc.unit_id || dc.id).
  const unitNodeByUid = useMemo(() => {
    const m = new Map();
    nodes.forEach(n => { const d = n.detailedContent; const u = d && (d.unitId || d.unit_id || d.id); if (u) m.set(String(u), n); });
    return m;
  }, [nodes]);
  const subjectOfKey = (k) => {
    if (chatMeta[k]?.subject) return chatMeta[k].subject;
    if (k.startsWith('unit:')) return { unitId: k.slice(5), axis: '_unit' };
    if (k.startsWith('pattern:')) { const [, ax, ...rest] = k.split(':'); return { axis: ax, candidateId: rest.join(':') }; }
    return { axis: k.replace(/^axis:/, '') };
  };
  const chatList = useMemo(() => Object.entries(chatConvos)
    .map(([k, c]) => {
      const count = (c?.msgs || []).filter(m => m.me).length;
      if (!count) return null;
      const sj = subjectOfKey(k);
      let label; let detail = null;
      if (sj.unitId) {
        const n = unitNodeByUid.get(String(sj.unitId));
        const dc = n?.detailedContent || {};
        label = n ? (unitDisplayLabel(dc, 'list') || dc.title || n.label) : sj.unitId;
        detail = dc.title && dc.title !== label ? dc.title : null;
      } else if (sj.axis === '_script') {
        label = 'Whole script';
      } else {
        label = METRIC_LABELS[sj.axis] || sj.axis;
        detail = sj.candidateId ? patternTitleOf(sj.axis, sj.candidateId) : 'The whole axis';
      }
      return { key: k, label, detail, count, updatedAt: chatMeta[k]?.updatedAt || 0, subject: sj };
    })
    .filter(Boolean)
    .sort((a, b) => b.updatedAt - a.updatedAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  [chatConvos, chatMeta, unitNodeByUid]);
  const chattedUnits = useMemo(() => new Set(chatList.filter(c => c.subject.unitId).map(c => String(c.subject.unitId))), [chatList]);
  const chattedAxes = useMemo(() => new Set(chatList.filter(c => !c.subject.unitId && c.subject.axis && c.subject.axis !== '_script').map(c => c.subject.axis)), [chatList]);
  const unitChatCount = (dc) => { const u = dc && (dc.unitId || dc.unit_id || dc.id); const c = u ? chatConvos[`unit:${u}`] : null; return c ? (c.msgs || []).filter(m => m.me).length : 0; };
  // Go to a conversation: a unit opens its reading with the chat open; an axis or pattern opens the Axes
  // panel on that axis with that thread; the whole-script thread opens Findings with the chat.
  const openConversation = (key) => {
    const it = chatList.find(c => c.key === key);
    if (!it) return;
    const sj = it.subject;
    track('lr_conversation_open', { kind: sj.unitId ? 'unit' : (sj.axis === '_script' ? 'script' : (sj.candidateId ? 'pattern' : 'axis')) });
    if (sj.unitId) {
      const n = unitNodeByUid.get(String(sj.unitId));
      if (!n) return;
      if (view === 'timeline' && !isPhone) { openNode(n); setDockAxis(null); setDockOpen(true, 'conversations'); }
      else { openNode(n); discussUnit(n.detailedContent, '_unit', false, `The whole ${n.detailedContent?.type === 'movie' ? 'script' : (n.detailedContent?.type || 'unit')}`); }
      return;
    }
    if (unitModal) closeUnitModal('conversations');
    if (sj.axis === '_script') { markExplicitSubject('script'); goFindings('conversations'); setChatSubject(null); setChatOpenPersist(true, 'conversations'); return; }
    const subj = { axis: sj.axis, axisLabel: METRIC_LABELS[sj.axis] || sj.axis, candidateId: sj.candidateId || null, title: sj.candidateId ? patternTitleOf(sj.axis, sj.candidateId) : null };
    axisThreadRef.current[sj.axis] = subj;
    sessionAxesRef.current.add(sj.axis);
    markExplicitSubject(`axis:${sj.axis}`);
    setActivePanel('pane');
    setRight({ type: 'axes', axis: sj.axis });
    setChatSubject(subj);
    setChatOpenPersist(true, 'conversations');
  };
  // The way into the unit chat. SITE CONVENTION (owner, 2026-10-14: "in rest of site the chat buttons look
  // unique"): the same filled teal "Ask AI" pill with a speech-bubble icon as the site-wide assistant
  // launcher (.btn-ai-chat in assistant_partial.blade.php, teal-500 #14b8a6), so a writer who has used
  // Ask AI anywhere else recognises it here. On desktop it FLOATS at the reading's bottom-right — where
  // the chat opens from and where the page's own launcher sits — instead of hiding in the header's
  // top-right corner, and it takes no row of its own. Gone while the chat is open (the dock's ▾ closes it).
  const ChatIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      <path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 4v-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm3 6.2a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6zm5 0a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6zm5 0a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6z" />
    </svg>
  );
  // While the writer scrolls DOWN through a reading, the floating pill shrinks to its round icon so it
  // covers as little text as possible; scrolling up, or reaching the top or the end, brings the words
  // back (the extended-FAB pattern). Codex G_launcher_overlap.
  const [askCompact, setAskCompact] = useState(false);
  const readScrollRef = useRef({ top: 0 });
  useEffect(() => { setAskCompact(false); readScrollRef.current.top = 0; }, [right?.node?.id]);   // a new reading starts at its top
  const onReadingScroll = (e) => {
    const el = e.currentTarget; const top = el.scrollTop; const prev = readScrollRef.current.top;
    readScrollRef.current.top = top;
    const atEnd = el.scrollHeight - top - el.clientHeight < 8;
    const next = top > 80 && !atEnd && top > prev;
    if (top < prev - 4 || top <= 80 || atEnd) { if (askCompact) setAskCompact(false); }
    else if (next && !askCompact) setAskCompact(true);
  };
  const dockButton = (dc, { phone = false, float = false } = {}) => {
    if (!dc || !chatAllowed) return null;
    if (float && dockOpen) return null;
    const grain = dc.type === 'movie' ? 'script' : (dc.type || 'unit');
    const iconOnly = float && askCompact;
    return (
      <button type="button" aria-expanded={dockOpen} onClick={() => setDockOpen(!dockOpen, float ? 'float' : 'header')}
        className="ic-askai" aria-label={iconOnly ? `Ask AI about this ${grain}` : undefined}
        {...tip(dockOpen ? 'Hide the chat' : `Ask the AI about this ${grain} — it knows this reading and these pages`)}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: iconOnly ? 0 : 8, flexShrink: 0, cursor: 'pointer', fontWeight: 700,
          fontSize: phone ? 13 : 15, padding: iconOnly ? 0 : (phone ? '5px 12px' : '10px 20px'), borderRadius: 999, border: 0,
          ...(iconOnly ? { width: 46, height: 46 } : {}), transition: 'padding .15s ease, width .15s ease',
          background: dockOpen ? '#0f766e' : '#14b8a6', color: '#fff', letterSpacing: '.01em',
          ...(float ? { position: 'absolute', right: 22, bottom: 18, zIndex: 3, boxShadow: '0 6px 18px rgba(15,23,42,.28)' } : {}) }}>
        <ChatIcon />{iconOnly ? null : (() => { const n = unitChatCount(dc); return phone ? (n ? `Chat (${n})` : 'Ask AI') : (n ? `Continue chat (${n})` : `Ask AI about this ${grain}`); })()}
      </button>
    );
  };
  // The keyboard is "up" when the visible viewport is meaningfully shorter than the layout viewport.
  // Full-height chat needs BOTH: the composer focused and the keyboard actually showing — dismissing
  // the keyboard with the textarea still focused returns to the 60% split (review I4).
  const kbUp = isPhone && vvH != null && typeof window !== 'undefined' && (window.innerHeight - vvH) > 120;
  const phoneChatFull = dockTyping && kbUp;
  // Each view gets a small picture of what it looks like, so the three words read as three ways to see
  // the same script (owner, 2026-10-14: "people won't know to click that").
  const VIEW_ICONS = {
    timeline: <svg width="15" height="12" viewBox="0 0 15 12" aria-hidden="true"><rect x="0" y="0" width="15" height="3" rx="1" fill="currentColor"/><rect x="0" y="4.5" width="7" height="3" rx="1" fill="currentColor"/><rect x="8" y="4.5" width="7" height="3" rx="1" fill="currentColor"/><rect x="0" y="9" width="3" height="3" rx="1" fill="currentColor"/><rect x="4" y="9" width="3" height="3" rx="1" fill="currentColor"/><rect x="8" y="9" width="3" height="3" rx="1" fill="currentColor"/><rect x="12" y="9" width="3" height="3" rx="1" fill="currentColor"/></svg>,
    list: <svg width="14" height="12" viewBox="0 0 14 12" aria-hidden="true"><rect x="0" y="0.5" width="2.5" height="2.5" rx="1" fill="currentColor"/><rect x="4" y="0.5" width="10" height="2.5" rx="1" fill="currentColor"/><rect x="0" y="4.75" width="2.5" height="2.5" rx="1" fill="currentColor"/><rect x="4" y="4.75" width="10" height="2.5" rx="1" fill="currentColor"/><rect x="0" y="9" width="2.5" height="2.5" rx="1" fill="currentColor"/><rect x="4" y="9" width="10" height="2.5" rx="1" fill="currentColor"/></svg>,
    table: <svg width="14" height="12" viewBox="0 0 14 12" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x=".75" y=".75" width="12.5" height="10.5" rx="1.5"/><path d="M.75 4.25h12.5M.75 7.75h12.5M5 .75v10.5M9 .75v10.5"/></svg>,
  };
  // The PAGE chat's launcher: the site's teal Ask AI pill, like the reading modal's (owner, 2026-10-14/15:
  // "chat buttons look unique … more obvious"). Names what it will talk about in plain words, and says
  // "Continue chat (n)" when that thread already has questions in it.
  // Name what the page chat is about in its header, like the reading modal's "Ask about Scene 6".
  const pageChatHeading = effectiveSubject.axis === '_script' ? 'Ask about your script'
    : `Ask about ${effectiveSubject.unitId ? (effectiveSubject.title || 'this unit') : (effectiveSubject.axisLabel || 'this')}`;
  // `inline`: a smaller pill at the right of the tab row (owner, 2026-10-26: the floating one sat over the
  // reading and on top of the site's support bubble). The table view keeps the floating pill — its
  // grid has no tab row of its own.
  const renderChatLauncher = ({ z = 10, inline = false } = {}) => {
    if (!chatAllowed) return null;
    const sj = effectiveSubject;
    const c = chatConvos[chatKeyOf(sj)];
    const n = c ? (c.msgs || []).filter(m => m.me).length : 0;
    const what = sj.axis === '_script' ? null : (sj.unitId ? (sj.title || 'this unit') : (sj.axisLabel || METRIC_LABELS[sj.axis] || sj.axis));
    const label = n ? `Continue chat (${n})` : (what ? `Ask AI about ${what}` : 'Ask AI: where should I start?');
    return (
      <button type="button" id="ic-chat-pill" className="ic-askai"
        onClick={() => { setActivePanel('chat'); setChatOpenPersist(true, 'launcher'); }}
        {...tip(what ? `Ask the AI about ${what} — it knows this read` : 'Ask the AI about your whole script — it knows this read')}
        style={inline
          ? { alignSelf: 'center', marginLeft: (!embedded && viewportW < 900) ? 0 : 'auto', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 7,
              maxWidth: 'min(320px, 100%)', background: '#14b8a6', color: '#fff', border: 0, borderRadius: 999,
              padding: '6px 14px', fontWeight: 700, fontSize: 13.5, letterSpacing: '.01em', cursor: 'pointer', boxShadow: '0 1px 3px rgba(15,23,42,.18)' }
          : { position: 'absolute', right: 22, bottom: 18, zIndex: z, display: 'inline-flex', alignItems: 'center', gap: 8,
          maxWidth: 'min(360px, calc(100% - 44px))', background: '#14b8a6', color: '#fff', border: 0, borderRadius: 999,
          padding: '10px 20px', fontWeight: 700, fontSize: 15, letterSpacing: '.01em', cursor: 'pointer', boxShadow: '0 6px 18px rgba(15,23,42,.28)' }}>
        <ChatIcon /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </button>
    );
  };
  const renderViewToggle = ({ labelled = false } = {}) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {labelled && <span id="ic-view-label" style={{ fontWeight: 600, color: NAVY, fontSize: 13, whiteSpace: 'nowrap' }}>View as</span>}
      <div className="btn-group btn-group-sm" role="group" aria-label={labelled ? undefined : 'View'} aria-labelledby={labelled ? 'ic-view-label' : undefined}>
        {[['timeline', 'Timeline'], ['list', 'List'], ['table', 'Table']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => { track('ic_view_change', { view: v }); setView(v); }} aria-pressed={view === v}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, padding: '.22rem .8rem', cursor: 'pointer',
                     border: `1.5px solid ${view === v ? NAVY : '#cbd5e1'}`,
                     background: view === v ? NAVY : '#fff', color: view === v ? '#fff' : NAVY }}>{VIEW_ICONS[v]}{l}</button>
        ))}
      </div>
    </div>
  );

  const renderUnitDock = (dc, { phone = false, cramped = false, beside = false } = {}) => {
    if (!dc) return null;
    const title = unitDisplayLabel(dc, 'list') || dc.title || 'this unit';
    const grain = dc.type === 'movie' ? 'script' : (dc.type || 'unit');
    const unitId = dc.unitId || dc.unit_id || dc.id || null;
    const collapse = (from) => setDockOpen(false, from);
    if (!dockOpen || !chatAllowed) return null;   // closed = zero height; the header's dockButton is the way in
    const subject = { axis: dockAxis || '_unit', axisLabel: dockAxis ? (METRIC_LABELS[dockAxis] || dockAxis) : `The whole ${grain}`, unitId, title };
    return (
      <div style={{ height: '100%', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, ...(beside ? { borderLeft: '1px solid #e2e8f0' } : { borderTop: '1px solid #e2e8f0' }) }}
        onFocusCapture={(e) => { if (phone) setDockTyping(e.target.tagName === 'TEXTAREA'); }}
        onBlurCapture={(e) => { if (phone && e.target.tagName === 'TEXTAREA') setDockTyping(false); }}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); collapse('esc'); } }}>
        {dockAxis && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: '#eef4f8', fontSize: 12, color: '#3f5563' }}>
            <span>Topic: <b style={{ color: NAVY }}>{METRIC_LABELS[dockAxis] || dockAxis}</b> in {title}</span>
            <button type="button" onClick={() => setDockAxis(null)} style={{ all: 'unset', cursor: 'pointer', color: NAVY, textDecoration: 'underline', marginLeft: 'auto' }}>whole {grain}</button>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <AxisChat movieHash={movie.hash} subject={subject} queue={dockQueue} onClaim={(id) => setDockQueue(qu => qu.filter(r => r.id !== id))}
            starters={dockStarters(dc)} store={chatStore} full autoFocus={!phone} onSend={noteAxisSend}
            conversations={chatList} onOpenConversation={openConversation}
            heading={`Ask about ${title}`} closeGlyph="▾" closeLabel={cramped ? 'Back to the reading' : 'Collapse chat'}
            onClose={() => collapse('collapse')} />
        </div>
      </div>
    );
  };

  const hoverLabel = hNode ? `${unitDisplayLabel(hNode, 'list') || hNode.label}${(() => {
    const pg = +(hNode.detailedContent?.page_count); const s = scoreOf(hNode.detailedContent);
    return `${Number.isFinite(pg) && pg > 0 ? ` · ${pg % 1 ? pg.toFixed(1) : pg}pg` : ''}${s != null ? ` · ${fmtScore(s)}/10` : ''}`;
  })()}` : '';

  // which axis the Inspector should show: explicit (table header / colour-by) via right.axis, or the
  // timeline default when colouring by an axis and nothing else is drilled.
  const axisKey = right?.type === 'axis' ? right.axis : (right == null && AXES.includes(metric) ? metric : null);
  const showAxis = axisKey != null;
  const axisData = axisKey ? (movie.axis_inspectors?.[axisKey] || null) : null;
  // the back button returns to whichever axis we drilled from — ONLY when we actually drilled from
  // one (right.fromAxis, set in openNode/openAxis). Do NOT fall back to the colour-by metric: that
  // made the Patterns/rollup panel show a bogus "‹ <axis> Inspector" link you never came from.
  const fromAxis = right?.fromAxis || null;
  const backBar = fromAxis && (
    <button type="button" onClick={() => { track('ic_axis_inspector_open', { axis: fromAxis, from: 'back' }); setRight({ type: 'axis', axis: fromAxis }); setSelectedId(null); clearPin(); }}
      style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', cursor: 'pointer',
        border: 'none', borderBottom: '1px solid #e7edf1', background: '#f8fafc', color: NAVY, fontWeight: 700,
        fontSize: 13, padding: '8px 16px' }}>‹ {METRIC_LABELS[fromAxis] || fromAxis} Inspector</button>
  );

  // ---- rail (LEFT_RAIL_SPEC §1): a minimal index — what's available, never the answers ----
  const goHome = (via) => { track('ic_rail_nav', { item: 'home', ...(via ? { via } : {}) }); setActivePanel('pane'); setRight({ type: 'orient' }); setSelectedId(null); clearPin(); };
  const goFindings = (via) => { track('ic_rail_nav', { item: 'findings', ...(via ? { via } : {}) }); setActivePanel('pane'); setRight({ type: 'findings' }); setSelectedId(null); clearPin(); };
  const goAxes = (via) => { track('ic_rail_nav', { item: 'axes', ...(via ? { via } : {}) }); setActivePanel('pane'); setRight({ type: 'axes' }); setSelectedId(null); clearPin(); };
  const goPatterns = (via) => { track('ic_rail_nav', { item: 'patterns', ...(via ? { via } : {}) }); setActivePanel('pane'); setRight({ type: 'rollup', grain: 'overview' }); setSelectedId(null); clearPin(); };
  const goShape = (via) => { track('ic_rail_nav', { item: 'shape', ...(via ? { via } : {}) }); setActivePanel('pane'); setRight({ type: 'shape' }); setSelectedId(null); clearPin(); };
  // Shape tab shows when the public composite trajectory is present (everyone) OR the
  // admin-only 12×4 matrix is (admins). Both come from AnalysisController::icicle.
  const hasShape = !!(movie.shape_trajectory?.ok || movie.field_standing?.ok);
  // The unit last read, as a tab beside Findings / Axes / Patterns in List view (owner, 2026-10-19: "add
  // the specific unit as another tab highlighted so it's obvious what's what and they can click any tab
  // and go back to the unit looked at").
  const lastUnitTab = (view === 'list' && lastUnit && lastUnit.right?.node) ? {
    key: 'unit',
    label: unitDisplayLabel(lastUnit.right.node, 'list') || lastUnit.right.node.title || 'Reading',
    on: () => { track('ic_rail_nav', { item: 'unit' }); setActivePanel('pane'); setSelectedId(lastUnit.selectedId); setRight(lastUnit.right); },
  } : null;
  const railItems = [
    { key: 'findings', label: 'Findings', on: () => goFindings() },
    { key: 'axes', label: 'Axes', on: () => goAxes() },
    { key: 'patterns', label: 'Patterns', on: () => goPatterns() },
    ...(hasShape ? [{ key: 'shape', label: 'Shape', on: () => goShape() }] : []),
  ];
  const railSelected = right?.type === 'node' ? 'unit' : right?.type === 'findings' ? 'findings'
    : (right?.type === 'axes' || right?.type === 'axis') ? 'axes'
      : right?.type === 'rollup' ? 'patterns'
        : right?.type === 'shape' ? 'shape' : null;   // orient / node reading → nothing selected
  // Reading-mode tabs — calm text with an active underline, directly ABOVE the pane they control
  // (the old left rail sat in the same gutter as the map's row labels, so the gutter changed
  // meaning halfway down the page; and Findings/Axes/Patterns are ways of READING, not places).
  // Reading-mode tabs (Findings / Axes / Patterns) + Beta feedback + Take-a-tour pill.
  // ALL suppressed in publicView (LP demo): the LP surface should feel like a single
  // focused demonstration, not a full application. The bottom pane still shows whatever
  // node the visitor clicks — just without the mode-switching chrome above it.
  // ⚠️ `publicView && embedded`, NOT `publicView` alone (fixed 2026-08-21 — the SECOND half of the
  // same conflation as the Timeline/Table toggle, and I only fixed the first half at the time).
  // `AnalysisController::icicle()` sets `publicView = $isAnon || $isPublic || $viaShareLink`, so any
  // showcase/HoF/shared script suppressed these tabs on the FULL-SCREEN page too — for its own owner
  // — leaving Findings / Axes / Patterns / Shape with no way in at all. The suppression is meant for
  // the embedded LP demo ("a single focused demonstration, not a full application"); full screen is
  // the full application by definition.
  const modeTabs = (publicView && embedded) ? null : (
    /* ⚠️ WRAPS. Without `flexWrap` this row overflows its column instead of reflowing, and because
       the overlay chat sits on top of that column the overflow lands UNDERNEATH the chat — which is
       how "Take a tour" ended up hidden (measured at an 860px row: column content 0–440, pill
       720–838). Four tabs plus the feedback link plus the pill do not fit a ~440px column on one
       line, so the secondary items drop to a second row rather than disappearing. */
    <div id="ic-modes" style={{ display: 'flex', alignItems: 'baseline', gap: 6, rowGap: 6, flexWrap: 'wrap', padding: '.45rem 1.4rem 0 1rem', background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
      {/* ⚠️ SELECTED MUST LOOK SELECTED, AND NONE-SELECTED MUST LOOK LIKE NONE (owner, 2026-09-30:
          "make the selected tab more obvious so that when they click a unit they can see it no
          longer be selected"). The old rule painted EVERY tab navy when `railSelected === null`
          (a unit reading is open), so "nothing selected" was indistinguishable from "all selected",
          and a 2px underline was the only difference either way. Now: unselected is always the
          muted slate, and the selected tab is a tinted tab with a heavy underline — so clicking a
          unit visibly drops the tab back to plain. */}
      {(lastUnitTab ? [...railItems, lastUnitTab] : railItems).map(it => {
        const on = railSelected === it.key;
        return (
          <React.Fragment key={it.key}>
          {it.key === 'unit' && <span aria-hidden="true" style={{ alignSelf: 'center', width: 1, height: 18, background: '#cbd5e1', margin: '0 4px' }} />}
          <button type="button" onClick={it.on} className="ic-rail-btn" aria-pressed={on}
            {...(it.key === 'unit' ? tip(on ? 'The unit you are reading' : 'Back to the unit you were reading') : {})}
            style={{ all: 'unset', cursor: 'pointer', padding: '.35rem .75rem .45rem', borderRadius: '6px 6px 0 0',
              fontWeight: on ? 800 : 600, fontSize: 13.5,
              color: on ? NAVY : '#6b8390',
              background: on ? '#e3eef4' : 'transparent',
              boxShadow: on ? `inset 0 -3px 0 ${NAVY}` : 'none' }}>
            {it.label}
          </button>
          </React.Fragment>
        );
      })}
      {/* The tab row holds the reading-mode tabs only (owner, 2026-10-24). Tour, video and feedback live in
          the heading / title row: the video card + "Take a tour" by "What you're looking at", "Send feedback"
          beside "Early preview". Exception: full screen under 900px wide, where the title row has no room —
          the tour and video pills stay here. */}
      {(!embedded && viewportW < 900) && (<>
        <span style={{ marginLeft: 'auto' }} />
      <button type="button" id="ic-tour-btn" onClick={() => startTour('tabs')} {...tip('Take a quick guided tour')}
        style={{ alignSelf: 'center', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '.3rem .75rem', borderRadius: 999, border: `1.5px solid ${NAVY}`, background: '#eef4f8', color: NAVY,
          fontSize: 12.5, fontWeight: 700 }}>
        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16,
          borderRadius: '50%', background: NAVY, color: '#fff', fontSize: 11, fontWeight: 800, lineHeight: 1 }}>?</span>
        Take a tour
      </button>
      <button type="button" id="ic-howvideo-btn" onClick={() => { track('ic_rail_nav', { item: 'how_video' }); setHowVideo(true); }} {...tip('Watch how the Layered Read works (5 min)')}
        style={{ alignSelf: 'center', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '.3rem .75rem', borderRadius: 999, border: `1.5px solid ${NAVY}`, background: '#eef4f8', color: NAVY,
          fontSize: 12.5, fontWeight: 700 }}>
        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16,
          borderRadius: '50%', background: NAVY, color: '#fff', fontSize: 8, lineHeight: 1, paddingLeft: 1 }}>▶</span>
        How it works · 5 min
      </button>
      </>)}
      {view !== 'table' && (!publicView || !embedded) && !chatOpen && !unitModal && renderChatLauncher({ inline: true })}
    </div>
  );

  const synthesis = movie.der_synthesis || null;

  // ---- orientation (the default pane): what this map is + the two ways in. Short on purpose —
  // it tells the writer where to look and how, not what the answers are. ----
  const movieTitle = movie.movie?.title || 'your script';
  const hasFindings = !!synthesis?.writer_view?.tier_1_glance;
  // name only the grains THIS script actually has (a script without acts shouldn't read "every act")
  const grainPhrase = (() => {
    const names = rows.map(r => ({ act: 'act', sequence: 'sequence', scene: 'scene' })[r.key]).filter(Boolean);
    return names.length > 1 ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1] : (names[0] || 'scene');
  })();
  // Agreed mock (owner, 2026-07-07): headline · glyph legend · click-teaching FIRST · findings
  // button second ("or start with…") · one muted footer (triage default + tour). Every line is
  // clickable and PULSES its target on screen — the card teaches by pointing, not explaining.
  const glyphBtn = { all: 'unset', cursor: 'pointer', color: '#5b7a8c', fontSize: '.95rem' };
  // On desktop the orientation card teaches by PULSING the map in place; on a phone the map is a
  // separate tab, so the same cues switch to the Map view instead of pulsing an element that isn't shown.
  const mapCue = () => (isPhone ? setMView('map') : pulse('#ic-map'));
  const orientPane = (
    <div style={{ padding: '1.7rem 1.8rem 1.4rem', maxWidth: 720 }}>
      <h3 style={{ color: NAVY, fontWeight: 800, fontSize: '1.3rem', margin: 0 }}>
        This is {movieTitle}, on one screen.
      </h3>
      <div style={{ margin: '.75rem 0 0', display: 'flex', gap: 26, flexWrap: 'wrap' }}>
        <button type="button" onClick={mapCue} {...tip('Show me')} style={glyphBtn}>
          <span style={{ color: NAVY, fontWeight: 700 }}>→</span> story order
        </button>
        <button type="button" onClick={mapCue} {...tip('Show me')} style={glyphBtn}>
          <span style={{ color: NAVY, fontWeight: 700 }}>▂▄▆</span> wider = more pages
        </button>
      </div>
      <p style={{ margin: '1.15rem 0 0', fontSize: '1rem', color: '#33454f', lineHeight: 1.55 }}>
        <button type="button" onClick={mapCue} style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 700 }}>
          {isPhone ? '▸ Open the Map' : '▲ Click anything in the map'}
        </button>
        {' '}— every {grainPhrase} has its own reading: {isPhone ? 'tap a scene for' : ''} the grade, and how to lift it.
      </p>
      {hasFindings && (
        <p style={{ margin: '.85rem 0 0', fontSize: '1rem', color: '#33454f', lineHeight: 1.55 }}>
          <button type="button" onClick={() => { track('ic_orient_cta', { target: 'findings' }); goFindings('orient'); }}
            style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3 }}>
            Or start with the findings →
          </button>
          {' '}— {(() => {
            const cards = synthesis.writer_view.tier_1_glance.pattern_cards || [];
            const drags = cards.filter(c => c.role !== 'protect_signal').length;
            const protect = cards.some(c => c.role === 'protect_signal');
            const lead = drags > 1 ? `the ${drags} patterns dragging your script most` : 'the pattern dragging your script most';
            return protect ? `${lead}, and the strength to protect.` : `${lead}, biggest first.`;
          })()}
        </p>
      )}
      <div style={{ marginTop: '1.25rem', paddingTop: '.8rem', borderTop: '1px solid #eef2f5', fontSize: '.85rem', color: '#8ba0ac' }}>
        Viewing: <b style={{ color: '#5b7a8c' }}>Triage</b> —{' '}
        <button type="button" onClick={() => pulse('#ic-metric-select')} style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3 }}>
          recolour with Colour by
        </button>
        {' '}·{' '}
        <button type="button" onClick={() => startTour('orient')} style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3 }}>
          take the quick tour ▸
        </button>
        {' '}·{' '}
        <button type="button" onClick={() => { track('ic_orient_cta', { target: 'how_video' }); setHowVideo(true); }} style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3 }}>
          ▶ watch how it works (5 min)
        </button>
      </div>
    </div>
  );

  const findingsPane = synthesis?.writer_view?.tier_1_glance
    ? <div style={{ paddingTop: '1.1rem' }}>
        <SynthesisPanel synthesis={synthesis} surfaceLabel="timeline" layout="findings" activeCardIdx={activeFindingIdx} scriptBand={scriptBand}
          onOpenWiki={setWikiArt}
          onHighlight={(refs, idx, title, metaOverride) => { showOnTimeline(refs, null, title || 'this finding', metaOverride); setActiveFindingIdx(idx); track('ic_finding_open', { idx, title: title || null }); }}
          onClear={clearPin} />
      </div>
    : <div style={{ padding: '2rem 1.2rem', color: '#64748b', fontSize: '.95rem', lineHeight: 1.5 }}>
        No script-level findings for this script yet.{' '}
        <button type="button" onClick={() => { track('ic_rail_nav', { item: 'axes', via: 'findings_empty' }); setRight({ type: 'axes' }); }}
          style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3 }}>
          See the axes overview ›
        </button>
      </div>;
  // Axes tab = master-detail INSIDE the pane: the ranked list never leaves; the inspector opens
  // beside it. "‹ all axes" (or Esc) collapses back to the full-width sentence list.
  // The pane renders what it showed BEFORE a unit opened while the desktop unit modal is up.
  const paneR = unitModal ? paneRight : right;
  const axisSel = paneR?.type === 'axes' ? (paneR.axis || null) : null;
  const openAxisSplit = (ax, from) => {
    track('ic_axis_open', { axis: ax, from });
    setActivePanel('pane'); setMetric(ax); setSelectedId(null); clearPin();
    setRight({ type: 'axes', axis: ax });
  };
  // ⓘ for the CURRENT Colour-by selection — axis → its wiki article; lens → the lens article;
  // Triage / D+E → the framework overview (which explains bands + the three lenses).
  const metricWiki = () => {
    const art = AXES.includes(metric) ? wikiArticle('axis', metric)
      : ['design', 'execution', 'read'].includes(metric) ? wikiArticle('lens', metric)
        : wikiArticle('overview', 'framework');
    if (art) { track('ic_wiki_open', { kind: 'metric', id: metric }); setWikiArt(art); }
  };
  const metricInfoBtn = (
    <button type="button" onClick={metricWiki} {...tip('What does this colouring mean?')}
      style={{ all: 'unset', cursor: 'pointer', color: '#5b7a8c', fontSize: 14, padding: '0 2px' }}>ⓘ</button>
  );

  const axesPane = (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      <div style={{ width: axisSel ? 236 : '100%', flexShrink: 0, borderRight: axisSel ? '1px solid #e7edf1' : 'none', background: axisSel ? '#fbfcfd' : '#fff' }}>
        <AxesOverview view={view} movieNode={movieNode} inspectors={movie.axis_inspectors} chatted={chattedAxes} selected={axisSel} priorityAxis={movie.axis_priority?.axis || null} priorityBasis={movie.axis_priority?.basis || null}
          onOpenAxis={(ax) => openAxisSplit(ax, axisSel ? 'axes_list' : 'axes_overview')}
          onOpenWiki={() => { track('ic_wiki_open', { kind: 'overview', id: 'framework' }); setWikiArt(wikiArticle('overview', 'framework')); }} />
      </div>
      {axisSel && (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #e7edf1', background: '#f8fafc' }}>
            <button type="button" onClick={() => { track('ic_axis_open', { axis: null, from: 'collapse' }); setRight({ type: 'axes', axis: null }); }}
              style={{ flex: 1, textAlign: 'left', cursor: 'pointer', border: 'none', background: 'none', color: NAVY, fontWeight: 700, fontSize: 13, padding: '8px 16px' }}>
              ‹ all axes
            </button>
            <button type="button" onClick={() => { track('ic_wiki_open', { kind: 'axis', id: axisSel }); setWikiArt(wikiArticle('axis', axisSel)); }}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#5b7a8c', fontSize: 12.5, fontWeight: 600, padding: '8px 16px', whiteSpace: 'nowrap' }}>
              ⓘ what is {METRIC_LABELS[axisSel] || axisSel}?
            </button>
          </div>
          <AxisInspector axisLabel={METRIC_LABELS[axisSel] || axisSel} inspector={movie.axis_inspectors?.[axisSel] || null}
            explanation={movie.axis_explanations?.[axisSel] || null}
            preselectFirst={false}
            onShowOnTimeline={showOnTimeline}
            onAsk={(q, p) => {
              track('ic_chat_open', { axis: axisSel, candidate_id: p?.candidate_id || null, from: 'inspector_question' });
              setActivePanel('chat');
              setChatSubject({ axis: axisSel, axisLabel: METRIC_LABELS[axisSel] || axisSel, candidateId: p?.candidate_id || null, title: p?.title || null });
              setChatSeed({ question: q?.question || '', nonce: Date.now() });
              setChatOpenPersist(true, 'inspector_question');
            }} />
        </div>
      )}
    </div>
  );

  const shapePane = (
    <ShapeTab
      shape={movie.shape_trajectory}
      fieldStanding={movie.field_standing}
      nodes={tableNodes}
      script={movie.movie}
      articleUrl="/layered-read-case-study"
    />
  );

  const pane = (paneR == null || paneR.type === 'orient')
    ? orientPane
    : paneR.type === 'findings'
      ? findingsPane
      : paneR.type === 'shape'
        ? shapePane
      : paneR.type === 'axes'
        ? axesPane
        : showAxis
        ? <AxisInspector axisLabel={METRIC_LABELS[axisKey] || axisKey} inspector={axisData}
        explanation={axisKey ? (movie.axis_explanations?.[axisKey] || null) : null}
        preselectFirst={false}
        onShowOnTimeline={showOnTimeline}
        onAsk={(q, p) => {
          track('ic_chat_open', { axis: axisKey, candidate_id: p?.candidate_id || null, from: 'inspector_question' });
          setActivePanel('chat');
          setChatSubject({ axis: axisKey, axisLabel: METRIC_LABELS[axisKey] || axisKey, candidateId: p?.candidate_id || null, title: p?.title || null });
          setChatSeed({ question: q?.question || '', nonce: Date.now() });
          setChatOpenPersist(true, 'inspector_question');
        }} />
    : paneR?.type === 'node'
      ? <div>{backBar}<NodePanel node={paneR.node} peers={peersFor(paneR.node?.type === 'movie' ? 'movie' : paneR.node?.type)} movieHash={movie.hash} movieId={null} script={movie.movie} focusBlock="start" layout="twocol" toc onDiscussAxis={(ax, sd, lbl) => discussUnit(paneR.node, ax, sd, lbl)}
          /* null while the panel is ALREADY showing the zoomed unit — offering "zoom into this
             act" on the act you are looking at is a control that does nothing. */
          onFocusAct={(!canZoom(byId.get(paneR.node?.id)) || paneR.node.id === focusAct) ? null : ((id) => { enterFocus(id, 'reading'); })} /></div>
      : paneR?.type === 'rollup'
        ? <RollupPanel rollups={rollups} grain={paneR.grain} focus={paneR.focus} available={availableGrains} script={movie.movie} onGrainChange={g => setRight({ type: 'rollup', grain: g })}
            onShowUnits={(units, name) => { const refs = (units || []).map(u => u?.ref).filter(Boolean); if (refs.length) showOnTimeline(refs, null, name || 'this pattern', null, 'pattern'); }}
            onSelectUnit={(u) => { if (u?.ref) showOnTimeline([u.ref], null, u.label || u.short_label || 'this unit'); }} />
        : <div style={{ padding: '2rem 1.2rem', color: '#64748b', fontSize: '.95rem', lineHeight: 1.5 }}>Click any scene, sequence, or act for its grade and how to improve it — or <b>Patterns</b> in the left rail for what recurs at each level.</div>;

  // ── Per-AREA thumbs ──────────────────────────────────────────────────────────────────────────
  // The Layered Read is several independently-generated reads (findings, axis inspectors, per-unit
  // craft reads, pattern rollups, the recaps). Each gets its own div_id so thumb_feedback can tell us
  // WHICH of them is landing: `SELECT div_id, feedback_type, COUNT(*) … WHERE div_id LIKE 'lr:%'`.
  // No thumb on the orientation card — there's nothing generated there to rate.
  const unitThumb = (n, prefix) => {
    const g = n?.type === 'movie' ? 'script' : (n?.type || 'unit');
    return thumbKey(prefix, g, n?.number ?? null);
  };
  const thumbFor = (r) => {
    if (r == null || r.type === 'orient') return null;
    if (r.type === 'findings') return { key: 'lr:findings', label: 'Was this read useful?' };
    if (r.type === 'axes') return r.axis
      ? { key: thumbKey('lr:axis', r.axis), label: `Was the ${METRIC_LABELS[r.axis] || r.axis} read useful?` }
      : { key: 'lr:axes', label: 'Was this axis overview useful?' };
    if (r.type === 'axis') return { key: thumbKey('lr:axis', r.axis), label: `Was the ${METRIC_LABELS[r.axis] || r.axis} read useful?` };
    if (r.type === 'node') return { key: unitThumb(r.node, 'lr:read'), label: 'Was this read useful?' };
    if (r.type === 'rollup') return { key: thumbKey('lr:patterns', r.grain || 'overview'), label: 'Were these patterns useful?' };
    return null;
  };
  // Feedback sits with "Was this read useful?" at the end of each reading (owner, 2026-10-24: not as one more
  // link at the top). Public viewers get the thumbs only.
  const thumbStrip = (t) => (t ? (
    <div style={{ padding: '0 1.25rem 1.1rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 16px' }}>
      <Thumbs key={t.key} area={t.key} movieHash={movie.hash} label={t.label} onTrack={track} />
      {!publicView && <BetaFeedback movieHash={movie.hash} movieTitle={movie.movie?.title || null} label="Something off? Send feedback" />}
    </div>
  ) : null);
  const paneThumbs = thumbStrip(thumbFor(right));

  // ═══════════════════════════ MOBILE ═══════════════════════════
  if (isPhone) {
    const mChip = (active) => ({ border: 'none', borderRadius: 999, padding: '6px 13px', fontWeight: 700, fontSize: 13,
      cursor: 'pointer', background: active ? NAVY : '#eef2f5', color: active ? '#fff' : NAVY });
    const mGo = { findings: goFindings, axes: goAxes, patterns: goPatterns, shape: goShape };
    const mPane = (right == null || right.type === 'orient') ? orientPane
      : right.type === 'shape'
        ? shapePane
      : right.type === 'axes'
        ? <AxesOverview movieNode={movieNode} inspectors={movie.axis_inspectors} chatted={chattedAxes} priorityAxis={movie.axis_priority?.axis || null} priorityBasis={movie.axis_priority?.basis || null}
            onOpenAxis={(ax) => { track('ic_axis_open', { axis: ax, from: 'mobile_axes' }); setMetric(ax); setMAxis(ax); }} />
        : right.type === 'rollup'
          ? <RollupPanel rollups={rollups} grain={right.grain} focus={right.focus} available={availableGrains} script={movie.movie}
              onGrainChange={g => setRight({ type: 'rollup', grain: g })}
              onShowUnits={(units, name) => { const refs = (units || []).map(u => u?.ref).filter(Boolean); if (refs.length) { showOnTimeline(refs, null, name || 'this pattern', null, 'pattern'); setMView('map'); } }}
              onSelectUnit={(u) => { if (u?.ref) { showOnTimeline([u.ref], null, u.label || u.short_label || 'this unit'); setMView('map'); } }} />
          : findingsPane;
    // the mobile pane collapses node → findings and axes → overview (units/axes open in the modal),
    // so it needs its own thumb key rather than reusing paneThumbs.
    const mPaneThumbs = thumbStrip(thumbFor(
      (right == null || right.type === 'orient') ? right
        : right.type === 'axes' ? { type: 'axes' }
          : right.type === 'rollup' ? right
            : { type: 'findings' }
    ));
    const mModalThumbs = thumbStrip(
      mUnit ? { key: unitThumb(mUnit, 'lr:read'), label: 'Was this read useful?' }
        : mAxis ? { key: thumbKey('lr:axis', mAxis), label: `Was the ${METRIC_LABELS[mAxis] || mAxis} read useful?` }
          : null
    );
    return (
      <div data-sr-group="icicle" style={{ fontFamily: 'system-ui, sans-serif', color: INK, height: embedded ? '100%' : '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff' }}>
        {/* top bar: brand + compact colour-by */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.55rem .8rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ fontWeight: 800, color: NAVY, fontSize: '.98rem' }}>Layered Read</div>
          <select id="ic-metric-select" value={metric}
            onChange={e => { const v = e.target.value; metricSelfRef.current = true; track('ic_metric_change', { metric: v, mobile: true }); setMetric(v); clearPin(); }}
            className="form-select form-select-sm" style={{ marginLeft: 'auto', width: 'auto', borderColor: NAVY, color: NAVY, fontWeight: 600, fontSize: 12.5 }}>
            <option value="overall">{METRIC_LABELS.overall || 'Triage'}</option>
            <optgroup label="Groups">{['de', 'design', 'execution', 'read'].map(k => <option key={k} value={k}>{METRIC_LABELS[k]}</option>)}</optgroup>
            <optgroup label="Axes">{AXES.map(k => <option key={k} value={k}>{METRIC_LABELS[k]}</option>)}</optgroup>
          </select>
          {metricInfoBtn}
        </div>
        {/* mode buttons — Map first, then a divider, then the three ways of reading.
            ENTIRE ROW hidden on the embedded LP demo: it shows only the map + a
            simple reading pane, no mode switching, no beta feedback.
            ⚠️ `(!publicView || !embedded)` — same conflation as the desktop gates; a phone visiting
            the FULL-SCREEN page still wants mode switching. */}
        {(!publicView || !embedded) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.55rem .8rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0, overflowX: 'auto' }}>
            <button type="button" style={mChip(mView === 'map')} onClick={() => { track('ic_rail_nav', { item: 'map', mobile: true }); setMView('map'); }}>Map</button>
            <span style={{ width: 1, alignSelf: 'stretch', background: '#d7e2ea', margin: '2px 2px' }} />
            {railItems.map(it => (
              <button key={it.key} type="button" style={mChip(mView === 'read' && railSelected === it.key)}
                onClick={() => { mGo[it.key]('mobile'); setMView('read'); }}>{it.label}</button>
            ))}
            <span style={{ marginLeft: 'auto', flexShrink: 0 }}>
              <BetaFeedback movieHash={movie.hash} movieTitle={movie.movie?.title || null} />
            </span>
          </div>
        )}
        {/* content: vertical map OR the reading pane */}
        {mView === 'map' ? (
          <div ref={mMapRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: '#f8fafc' }} onScroll={onMapScroll}>
            <div style={{ position: 'sticky', top: 0, zIndex: 5 }}>
              {pinnedLabel && (
                <div style={{ background: '#eef4f8', borderBottom: `1px solid ${NAVY}33`, padding: '6px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: NAVY, fontWeight: 600 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pinnedLabel.text} · {pinnedLabel.meta}</span>
                    <button type="button" onClick={clearPin} style={{ all: 'unset', marginLeft: 'auto', color: NAVY, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', flexShrink: 0 }}>show all</button>
                  </div>
                  {/* the whole-script footprint at a glance — tiny cells are useless to TAP but perfect as an overview */}
                  <div style={{ display: 'flex', gap: 1, height: 7, marginTop: 5 }}>
                    {mOrdered.map(s => (
                      <span key={s.id} style={{ flex: Math.max(0.2, +(s.detailedContent?.page_count) || 1),
                        background: pinned && pinned.has(s.id) ? NAVY : '#d5dfe6', borderRadius: 1 }} />
                    ))}
                  </div>
                </div>
              )}
              {mCtx && (mCtx.act || mCtx.seq) && (
                <div style={{ background: 'rgba(255,255,255,.94)', borderBottom: '1px solid #e2e8f0', padding: '4px 10px', fontSize: 12, fontWeight: 700, color: NAVY }}>
                  {mCtx.act}{mCtx.seq ? ` › ${mCtx.seq}` : ''}
                </div>
              )}
            </div>
            {/* Script row — the whole-script node. The desktop map has a full-width Script bar at the
                top; the phone map dropped it, so the script node was unreachable. Give it its own
                tappable full-width bar above the acts/sequences/scenes grid. */}
            {movieNode && (
              <button type="button" onClick={() => mOpenUnit(movieNode)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: 'calc(100% - 16px)', margin: '8px 8px 0',
                  border: 'none', borderRadius: 4, background: softFill(movieNode.color), color: textOn(softFill(movieNode.color)),
                  padding: '10px 12px', cursor: 'pointer', textAlign: 'left', fontWeight: 800, fontSize: 13 }}>
                <span style={{ flex: 1 }}>Whole script</span>
                <span style={{ fontWeight: 700, opacity: .85 }}>›</span>
              </button>
            )}
            {/* Column layout adapts to what the script actually has: TV pilots / shorts often have NO
                acts, so reserving an acts column just leaves a dead blank strip on the left (reported).
                Drop it when there are no act nodes and let sequences take column 1. */}
            {/* The shared vertical map — see verticalMap(). `rowH` keeps the phone's 44px
                tap-target floor and 12px-per-page multiplier; the desktop List passes its own.
                This block used to be ~50 lines of duplicate JSX; it is the SAME map the desktop
                List renders, and one copy is the point. */}
            {verticalMap({
              rowH: (sc) => Math.max(44, (+(sc.detailedContent?.page_count) || 1) * 12),
              stickyTop: 64, pad: '8px 8px 90px', onOpen: mOpenUnit,
            })}
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: '#f5f7f9' }}>
            <div style={{ background: '#fff', margin: '.6rem .6rem 5.5rem', border: '1px solid #e7edf1', borderRadius: 12, overflow: 'hidden' }}>{mPane}{mPaneThumbs}</div>
          </div>
        )}
        {/* full-screen reading modal (unit or axis) — the SoS2 pattern */}
        {(mUnit || mAxis) && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: (phoneChatFull && vvH) ? vvH : '100%', zIndex: 1000, background: '#fff', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.6rem .9rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
              <div style={{ fontWeight: 700, color: NAVY, fontSize: '.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                {mUnit ? (unitDisplayLabel(mUnit, 'list') || mUnit.title || 'Reading') : (METRIC_LABELS[mAxis] || mAxis)}
              </div>
              {mAxis && (
                <button type="button" onClick={() => { track('ic_wiki_open', { kind: 'axis', id: mAxis }); setWikiArt(wikiArticle('axis', mAxis)); }}
                  style={{ all: 'unset', cursor: 'pointer', color: '#5b7a8c', fontSize: 15, padding: '0 4px' }}>ⓘ</button>
              )}
              {mUnit && dockButton(mUnit, { phone: true })}
              {mSceneIdx >= 0 && (
                <>
                  <button type="button" disabled={mSceneIdx <= 0} onClick={() => mStepScene(-1)}
                    style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, color: mSceneIdx <= 0 ? '#cbd5e1' : NAVY, fontWeight: 700, padding: '2px 11px', fontSize: 15, cursor: 'pointer' }}>‹</button>
                  <button type="button" disabled={mSceneIdx >= mOrdered.length - 1} onClick={() => mStepScene(1)}
                    style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, color: mSceneIdx >= mOrdered.length - 1 ? '#cbd5e1' : NAVY, fontWeight: 700, padding: '2px 11px', fontSize: 15, cursor: 'pointer' }}>›</button>
                </>
              )}
              <button type="button" onClick={mCloseModal}
                style={{ all: 'unset', cursor: 'pointer', color: '#64748b', fontSize: 22, lineHeight: 1, padding: '0 4px' }}>✕</button>
            </div>
            {/* Phone: the same dock. Expanded ~60% (the reading still peeks above); while typing, the
                chat takes the whole visible height above the keyboard. Landscape/short screens use the
                Chat | Reading switch, as on desktop. */}
            <div style={{ flex: (mUnit && dockOpen && (phoneChatFull || shortView)) ? '0 0 0px' : '1 1 auto', minHeight: 0, overflowY: 'auto',
              visibility: (mUnit && dockOpen && (phoneChatFull || shortView)) ? 'hidden' : 'visible' }}>
              {mUnit
                ? <NodePanel node={mUnit} peers={peersFor(mUnit.type === 'movie' ? 'movie' : mUnit.type)} movieHash={movie.hash} movieId={null} script={movie.movie}
                    onDiscussAxis={chatAllowed ? askInDock : null} />
                : <AxisInspector axisLabel={METRIC_LABELS[mAxis] || mAxis} inspector={movie.axis_inspectors?.[mAxis] || null}
                    explanation={movie.axis_explanations?.[mAxis] || null}
                    preselectFirst={false}
                    onShowOnTimeline={(refs, cid, label) => { showOnTimeline(refs, cid, label); setMAxis(null); setMView('map'); }}
                    onAsk={(q, p) => {
                      setChatSubject({ axis: mAxis, axisLabel: METRIC_LABELS[mAxis] || mAxis, candidateId: p?.candidate_id || null, title: p?.title || null });
                      setChatSeed({ question: q?.question || '', nonce: Date.now() });
                      setMAxis(null); setChatOpenPersist(true, 'inspector_question');
                    }} />}
              {mModalThumbs}
            </div>
            {mUnit && (
              <div style={{ flex: dockOpen ? ((phoneChatFull || shortView) ? '1 1 auto' : '0 0 60%') : '0 0 auto', minHeight: 0 }}>
                {renderUnitDock(mUnit, { phone: true, cramped: shortView })}
              </div>
            )}
          </div>
        )}
        {/* chat: full-screen sheet + pill — both suppressed on the EMBEDDED LP demo only.
            ⚠️ `(!publicView || !embedded)`: bare `publicView` removed the chat from the full-screen
            page on a phone for any showcase/shared script. */}
        {(!publicView || !embedded) && chatOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: '#fff' }}>
            <AxisChat movieHash={movie.hash} subject={effectiveSubject} seed={chatSeed} starters={chatStarters} store={chatStore} onSeedConsumed={() => setChatSeed(null)} onSend={noteAxisSend} conversations={chatList} onOpenConversation={openConversation}
              full onClose={() => setChatOpenPersist(false, 'close')} />
          </div>
        )}
        {chatAllowed && !chatOpen && !mUnit && !mAxis && (
          <button type="button" onClick={() => setChatOpenPersist(true, 'launcher')}
            style={{ position: 'fixed', right: 12, bottom: 10, zIndex: 900, background: '#fff', color: NAVY, border: `1px solid ${NAVY}`,
              borderRadius: 8, padding: '5px 11px', fontWeight: 600, fontSize: 12.5, boxShadow: '0 1px 4px rgba(15,23,42,.12)', cursor: 'pointer' }}>
            💬 {effectiveSubject.axis === '_script' ? 'Where should I start?' : `Ask about ${effectiveSubject.axisLabel}`}
          </button>
        )}
        <WikiDrawer article={wikiArt} onClose={() => setWikiArt(null)} onNavigate={setWikiArt} />
      </div>
    );
  }
  // ═══════════════════════════ DESKTOP ═══════════════════════════

  // The map's own controls (history, Colour by, legend, pin chip): ONE full-width row under the title.
  // In List side-by-side the group is LEFT-aligned so Colour by starts over the list it colours (owner,
  // 2026-10-15: "color by would make more sense above the left"). Moving it INTO the list column was
  // tried and rejected the same day: the legend wrapped to three lines and cost the list its height.
  const toolbarRow = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '.6rem 1rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0, position: 'relative' }}>
        {/* In-app history — top-left, browser convention. Always visible; each arrow is enabled only when
            there's somewhere to go that way (disabled, not hidden, so the control never shifts around).
            HIDDEN on the embedded LP demo: a first-time visitor has no history, so the affordance is
            visual noise. ⚠️ `(!publicView || !embedded)` — the reasoning is about a first-time DEMO
            visitor, but `publicView` is true for any showcase/shared script, so a bare gate also took
            the arrows away from the owner on full screen, where history genuinely accumulates. */}
        {/* Not on the desktop timeline: there it is in the reading modal (owner, 2026-10-19). */}
        {(!publicView || !embedded) && (view !== 'timeline' || isPhone) && renderHistory()}
        {/* Timeline / Table view toggle.
            Hidden only on the EMBEDDED public demo — that surface is meant to showcase the
            hierarchical timeline (the differentiator) and the Table is a power-user affordance.
            ⚠️ It used to be gated on `publicView` alone, which broke full screen (owner, 2026-08-21):
            `AnalysisController::icicle()` sets `publicView = $isAnon || $isPublic || $viaShareLink`,
            so ANY showcase or shared script lost the Table toggle even for its own owner on the
            full-screen page. That flag's documented job is suppressing the Shepherd auto-tour; it
            had quietly acquired a second, unrelated one. Full screen always gets the toggle now. */}
        {/* Full screen: the toggle lives at the right of the title row (owner, 2026-10-14). */}
        {embedded && !publicView && renderViewToggle()}
        {/* Table view has no standing side pane (the grid takes the width), so the script-level
            panels need their own way in — otherwise Findings / Axes / Patterns / Shape are only
            reachable by selecting a unit first, which is exactly the regression this fixes. */}
        {view === 'table' && !tableDetail && (
          <button type="button" onClick={openTablePane}
            style={{ fontWeight: 600, fontSize: 13, padding: '.22rem .75rem', cursor: 'pointer',
              border: `1.5px solid ${NAVY}`, borderRadius: 7, background: '#fff', color: NAVY }}>
            Findings &amp; patterns
          </button>
        )}
        {/* right group — the COLOUR ENCODING (which signal paints the map, + its legend) is a different
            decision from the Timeline/Table view mode on the left, so it lives apart on the right. */}
        <div style={{ marginLeft: sideBySide ? 0 : 'auto', display: 'flex', alignItems: 'center', gap: '6px 16px', flexWrap: 'wrap', justifyContent: sideBySide ? 'flex-start' : 'flex-end', flex: '1 1 380px', minWidth: 0 }}>
        {view !== 'table' && pinnedLabel && (
          // ACTIVE-FILTER CHIP (owner, 2026-10-08): the timeline is in "show only this" mode — say so at the top,
          // name what is shown, and offer the way back. The outlined swatch doubles as the legend for the outline.
          <div id="ic-pin-chip" key={pinnedLabel.text}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: 520, padding: '4px 6px 4px 10px',
              border: `1.5px solid ${pinColor}`, borderRadius: 999, background: pinColor + '0f', animation: 'icPinIn .35s ease' }}>
            <span aria-hidden="true" style={{ width: 11, height: 11, border: `2.5px solid ${pinColor}`, borderRadius: 2, flex: 'none' }} />
            <button type="button" title={pinnedLabel.kind === 'pattern' ? 'Go to this pattern' : undefined}
              onClick={() => { const c = document.querySelector('[data-lr-habit-open="1"]'); if (pinnedLabel.kind === 'pattern' && c) c.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              style={{ all: 'unset', cursor: pinnedLabel.kind === 'pattern' ? 'pointer' : 'default', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, color: INK }}>
              <span style={{ fontWeight: 600, color: pinColor }}>{pinnedLabel.kind === 'pattern' ? 'Showing pattern:' : 'Showing:'}</span>{' '}
              <b>{pinnedLabel.text}</b><span style={{ color: '#64748b' }}> · {pinnedLabel.meta}</span>
            </button>
            <button type="button" onClick={clearPin} aria-label="Show all units" title="Show all"
              style={{ all: 'unset', cursor: 'pointer', flex: 'none', width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: pinColor, fontWeight: 800, fontSize: 13 }}>✕</button>
          </div>
        )}
        {view !== 'table' && (
          <label style={{ fontWeight: 600, color: NAVY, fontSize: 13 }}>Colour by{' '}
            <select id="ic-metric-select" value={metric} onChange={e => { const v = e.target.value; metricSelfRef.current = true; track('ic_metric_change', { metric: v }); setMetric(v); clearPin(); setSelectedId(null); setRight(prev => AXES.includes(v) ? { type: 'axes', axis: v } : ((prev?.type === 'axis' || prev?.type === 'axes') ? { type: 'findings' } : prev)); }} className="form-select form-select-sm d-inline-block w-auto" style={{ borderColor: NAVY, color: NAVY, fontWeight: 600 }}>
              <option value="overall">{METRIC_LABELS.overall || 'Triage'}</option>
              <optgroup label="Groups">{['de', 'design', 'execution', 'read'].map(k => <option key={k} value={k}>{METRIC_LABELS[k]}</option>)}</optgroup>
              <optgroup label="Axes">{AXES.map(k => <option key={k} value={k}>{METRIC_LABELS[k]}</option>)}</optgroup>
            </select>
            {' '}{metricInfoBtn}
          </label>
        )}
        {view !== 'table' && <div style={{ minWidth: 180 }}><SunburstLegend metric={metric} surface="timeline" /></div>}
        {/* Legend for the teal "you have a conversation here" mark on units — only once there is one to explain
            (owner, 2026-10-14: "you have an icon for chats in units but no legend for it"). */}
        {view !== 'table' && chattedUnits.size > 0 && (
          <span {...tip('Units you have asked the AI about. Open one to continue the conversation.')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: '#33454f', whiteSpace: 'nowrap' }}>
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: 999, background: ASK_TEAL, boxShadow: '0 0 0 1.5px #fff, 0 0 0 2.5px #cbd5e1' }} />
            Has a chat
          </span>
        )}
        {view !== 'table'
          ? (hoverLabel ? <div aria-live="polite" style={{ position: 'absolute', right: 16, top: '100%', zIndex: 6, pointerEvents: 'none', marginTop: 4, fontSize: 12.5, color: INK, fontWeight: 700, background: 'rgba(255,255,255,.92)', padding: '1px 6px', borderRadius: 5 }}>{hoverLabel}</div> : null)
          : <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'right' }}>Click any cell for that unit’s reading, focused on the axis.</div>}
        </div>
      </div>
  );

  return (
    <div ref={shellRef} data-sr-group="icicle" style={{ fontFamily: 'system-ui, sans-serif', color: INK,
        height: embedded ? '100%' : (shortView ? 'auto' : '100vh'), minHeight: (shortView && !embedded) ? '100vh' : undefined,
        display: 'flex', flexDirection: 'column', overflow: shortView ? 'visible' : 'hidden' }}>
      <style>{`.ic-cell{transition:transform .08s ease, box-shadow .08s ease;cursor:pointer;}
        .ic-cell:hover{transform:scaleY(1.4);box-shadow:0 3px 12px rgba(15,23,42,.4);position:relative;z-index:5;outline:2px solid ${NAVY};outline-offset:-1px;}
        @keyframes icPinIn{from{opacity:0;transform:translateY(-4px);}to{opacity:1;transform:translateY(0);}}
        @keyframes icPinPulse{0%{box-shadow:0 0 0 0 rgba(179,38,30,.55);}45%{box-shadow:0 0 0 6px rgba(179,38,30,.18);}100%{box-shadow:0 0 0 0 rgba(179,38,30,0);}}
        /* rail: a mouse click must not leave a focus ring that reads as "selected" (Esc-home made
           the last-clicked entry look active). Keyboard users still get a ring via focus-visible. */
        .ic-rail-btn:focus{outline:none;}
        .ic-rail-btn[aria-pressed="false"]:hover{color:${NAVY} !important;background:#f1f6f9 !important;}
        .ic-rail-btn:focus-visible{outline:2px solid ${NAVY};outline-offset:-2px;}
        /* orientation card's "show me" pulse — points at the element the clicked line describes */
        @keyframes icPulse{0%,100%{box-shadow:0 0 0 0 rgba(20,83,110,0);}35%{box-shadow:0 0 0 5px rgba(20,83,110,.45);}70%{box-shadow:0 0 0 2px rgba(20,83,110,.25);}}
        .ic-pulse{animation:icPulse 1s ease 2;}
        @media (prefers-reduced-motion: reduce){ .ic-pulse{animation:icPulse .5s ease 1;} }
        /* Persistent zoom CUE on act/sequence cells. Decorative only — pointer-events:none, so the
           click always lands on the cell button behind it. Visible at rest (that is the entire
           point) and brightening on hover. This is the replacement for the old hover-only control:
           a hint you can see, over a target you cannot miss. */
        /* .72, not .55: measured against the band fills rather than eyeballed. The cue inherits the
           cell's own text colour (#0f172a on the light fills), so at .55 over the green #22c55e it
           blended to ~3.3:1 — clearing the 3:1 non-text minimum and nothing more, on a 6x10px mark
           whose entire job is to be noticed. .72 puts it near 4.6:1. */
        .ic-zoomhint{position:absolute;top:3px;right:3px;display:inline-flex;white-space:nowrap;z-index:6;align-items:center;gap:4px;font-size:11px;font-weight:800;line-height:1;
          opacity:.72;pointer-events:auto;cursor:zoom-in;padding:3px 5px;border-radius:5px;transition:opacity .12s ease, background .12s ease;}
        .ic-zoomhint__txt{display:none;}
        .ic-cell:hover .ic-zoomhint{background:rgba(255,255,255,.95);color:#0f2430;box-shadow:0 1px 3px rgba(15,23,42,.25);}
        .ic-cell:hover .ic-zoomhint__txt{display:inline;}
        /* Zoom out: same spot as Zoom in, but always shown with its words — it is the way back. */
        .ic-zoomhint--out{opacity:1;cursor:zoom-out;background:rgba(255,255,255,.95);color:#0f2430;box-shadow:0 1px 3px rgba(15,23,42,.3);}
        .ic-zoomhint--out .ic-zoomhint__txt{display:inline;}
        .ic-zoomhint:hover{background:#fff;outline:1.5px solid ${NAVY};}
        .ic-sliver{cursor:zoom-in !important;}
        .ic-zoomhint--hover{opacity:0;}
        .ic-zoomhint--left{left:3px;right:auto;}
        .ic-askai:hover{filter:brightness(.93);} .ic-askai:focus-visible{outline:3px solid #0f766e;outline-offset:2px;}
        /* Labels sit out the zoom tween — see the note at the .ic-celllabel span. */
        .ic-celllabel{transition:opacity .13s ease;}
        .ic-zooming .ic-celllabel,.ic-zooming .ic-zoomhint{opacity:0;}
        @media (prefers-reduced-motion: reduce){ .ic-celllabel{transition:none;} }
        .ic-cell:hover .ic-zoomhint,.ic-cell:focus-visible .ic-zoomhint{opacity:1;}
        @media (prefers-reduced-motion: reduce){ .ic-zoomhint{transition:none;} }
        /* The act "⤢ Focus" control and its .ic-actcell/.ic-focus-btn rules were DELETED 2026-09-04.
           It was a 20x15px button held at opacity:0 until hover, pinned to the act cell's right
           edge — up to 321px from the act's own label on a wide act — and it was the ONLY way to
           zoom. Zoom now lives on the act (and sequence) cell itself, which is what every icicle,
           treemap and flame graph does, and the permanently-visible act strip advertises it.
           Do not reintroduce a hover-only control as the sole route to a primary action. */
        /* Shepherd theme — same navy look as the wheel tour (rules mirrored from grades.css,
           inlined so the icicle bundle doesn't import the whole SoS stylesheet) */
        .shepherd-element.sos-shepherd{border-radius:12px;box-shadow:0 12px 40px rgba(15,23,42,.28);max-width:380px;}
        /* beat shepherd.css's own ".shepherd-has-title .shepherd-content .shepherd-header" (3 classes)
           — without the extra specificity the default grey header wins and the white title vanishes */
        .shepherd-element.sos-shepherd.shepherd-has-title .shepherd-content .shepherd-header{background:${NAVY};border-top-left-radius:12px;border-top-right-radius:12px;padding:12px 16px;}
        .shepherd-element.sos-shepherd .shepherd-title{color:#fff;font-weight:700;font-size:1rem;}
        .sos-shepherd .shepherd-cancel-icon{color:#cfe0ea;}
        .sos-shepherd .shepherd-text{color:#26323a;font-size:.92rem;line-height:1.5;padding:14px 16px;}
        .sos-shepherd .shepherd-footer{padding:0 16px 14px;display:flex;gap:8px;justify-content:flex-end;}
        .sos-shepherd .shepherd-button{background:${NAVY};color:#fff;border:none;border-radius:8px;padding:7px 14px;font-weight:600;cursor:pointer;}
        .sos-shepherd .shepherd-button.shepherd-button-secondary{background:#eef2f5;color:${NAVY};}
        /* LP-demo cell pulse: applied via the transient .ic-pulsing class when the landing page
           fires layered-read:pulse-units. Two short outline pulses at ~0.85s each = a ~1.7s
           attention-grab that then fully clears (no residual state). Position:relative + z-index
           lift the pulsed cell above neighbours so the outline isn't clipped. */
        .ic-pulsing{position:relative;z-index:6;animation:lr-cell-pulse .85s ease-out 2;}
        @keyframes lr-cell-pulse{
          0%   { outline: 0 solid rgba(20,83,110,.75); outline-offset: 0; }
          55%  { outline: 4px solid rgba(20,83,110,.75); outline-offset: 4px; }
          100% { outline: 4px solid rgba(20,83,110,0);   outline-offset: 10px; }
        }`}</style>

      {/* TITLE ROW (owner, 2026-10-14: "a row of its own for script title and Layered Read — Heathers is in
          the middle of it all"). Full screen only: the analysis-page embed already sits under that page's
          own big title. The controls row below keeps the full width for view, Colour by and the legend. */}
      {!embedded && (
        <div style={{ display: 'grid', gridTemplateColumns: viewportW >= 900 ? 'minmax(0,1fr) auto minmax(0,1fr)' : 'minmax(0,1fr) auto', alignItems: 'center', columnGap: 16, padding: '.6rem 1rem .4rem', flexShrink: 0, minWidth: 0 }}>
          <div className="d-flex align-items-center" style={{ gap: 12, minWidth: 0 }}>
          <LayeredReadWordmark />
          {movie.movie?.title && <span aria-hidden="true" style={{ width: 1, alignSelf: 'stretch', margin: '3px 2px', background: '#cbd5e1', flexShrink: 0 }} />}
          {movie.movie?.title && (
            <h1 title={movie.movie.title} style={{ margin: 0, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontSize: 22, fontWeight: 800, color: NAVY, letterSpacing: '-.01em', lineHeight: 1.2 }}>{movie.movie.title}</h1>
          )}
          </div>
          {/* View toggle in the title row (owner, 2026-10-14: "what would belong there … is the view by";
              then "people won't know to click that … top right people see last? center?"). It switches the
              whole page, so it is CENTRED like a calendar's Day / Week / Month, labelled "View as", with a
              small picture per view. Under 900px there is no room to centre: it sits at the right. */}
          <div style={{ justifySelf: viewportW >= 900 ? 'center' : 'end' }}>{renderViewToggle({ labelled: true })}</div>
          {/* The 5-min video card, as on the analysis page's Layered Read tab (view_icicle_react.blade.php —
              keep the two in step). Right end of the title row; under 900px the row has no third column and
              the "How it works" pill in the tab row is the way in. */}
          {viewportW >= 900 && (
            <div className="d-flex align-items-center" style={{ justifySelf: 'end', gap: 8 }}>
            <button type="button" onClick={() => { track('ic_rail_nav', { item: 'how_video', via: 'title_row' }); setHowVideo(true); }}
              aria-label="How the Layered Read works: 5-minute video"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '3px 12px 3px 3px',
                border: '1px solid #d9e6ec', borderRadius: 10, background: '#fff', cursor: 'pointer', textAlign: 'left', lineHeight: 1.2 }}>
              <span style={{ position: 'relative', display: 'block', width: 62, height: 35, borderRadius: 7, overflow: 'hidden', flex: 'none',
                background: "#14536e url('/video/layered-read/thumb-lr-tab.jpg?v=2') center/cover no-repeat" }}>
                {/* Frame: the LR mark on navy, left; the play circle sits right so it doesn't cover the mark (owner, 2026-10-26). */}
                <span aria-hidden="true" style={{ position: 'absolute', left: '76%', top: '50%', transform: 'translate(-50%,-50%)', width: 18, height: 18,
                  borderRadius: '50%', background: '#fff', color: NAVY, fontSize: 7, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', paddingLeft: 2, boxShadow: '0 1px 4px rgba(0,0,0,.3)' }}>▶</span>
              </span>
              <span>
                <span style={{ display: 'block', fontWeight: 700, color: NAVY, fontSize: 13.5 }}>How the Layered Read works</span>
                <span style={{ display: 'block', color: '#6b8390', fontSize: 12 }}>Video · 5 min</span>
              </span>
            </button>
            </div>
          )}
        </div>
      )}
      {/* top bar (fixed) */}
      {/* ONE ROW (owner, 2026-10-14): view controls left, Colour by + legend right. The right group SHRINKS
          (flex 1 1) and wraps INSIDE itself, so on a narrower window the legend drops under Colour by
          instead of the whole group falling to a second, oddly centred line. The hover read-out no
          longer reserves 220px in the row — it floats under the bar's right edge. */}
      {toolbarRow}

      {/* SIDE-BY-SIDE SHELL (owner, 2026-09-04): in List view the reading sits to the RIGHT of the
          map rather than under it, which buys the list the full window height — roughly double the
          rows — and spends horizontal space the reading pane did not need.
          ⚠️ When NOT side-by-side this wrapper is `display: contents`, i.e. it has NO layout effect
          at all, so Timeline / Table / landscape-phone render exactly as before. That is deliberate:
          a wrapper that always laid out would put every existing view at risk for one new one. */}
      {/* List side-by-side: the mode tabs sit above the RIGHT pane only (owner, 2026-10-15: "the tabs
          should be above the right pane as they affect that"). This reverses the 2026-09-30 full-width
          placement, which put Findings / Axes / Patterns over the list they do not change. */}
      <div style={sideBySide
        ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden' }
        : { display: 'contents' }}>

      {/* LIST — the vertical map on desktop (owner, 2026-09-04). Same map as the timeline, rotated:
          story order runs down, every scene carries its NAME, and acts/sequences are the columns
          their scenes span. It sits in the timeline's slot with its own scroller and feeds the same
          reading pane below, so switching views changes what you can SEE, never where things are.
          ⚠️ Row height is NOT the phone's `max(44, pages*12)`. That floor makes a 0.5-page scene and
          a 3-page scene identical — a scene has to clear 3.7 pages before height means anything —
          which silently throws away the page-length metaphor the timeline is built on. With no tap
          target to protect, desktop drops the floor to 30 and raises the multiplier to 30, so a
          3-page scene really is ~3x a 1-page one and length is readable again.
          Bounded height, not `flex:1`: the reading pane below has to keep its room, and the list is
          a scroller by nature (60 scenes is ~2,000px) — the same relationship the timeline has with
          the pane, just taller. */}
      {view === 'list' && (
        <div id="ic-list" ref={listRef}
          style={sideBySide
            ? { flex: listNarrow ? '0 0 clamp(300px, 26%, 400px)' : '0 0 clamp(340px, 34%, 560px)', minWidth: 0, minHeight: 0,
                display: 'flex', flexDirection: 'column', borderRight: '1px solid #e2e8f0' }
            : { flex: '0 0 auto', height: shortView ? 420 : 'min(48vh, 560px)',
                display: 'flex', flexDirection: 'column', borderBottom: '1px solid #e2e8f0' }}>
          {pinnedLabel && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 1rem', background: '#eef4f8', borderBottom: `1px solid ${NAVY}33`, fontSize: 13, color: NAVY, fontWeight: 600 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Highlighted: {pinnedLabel.text} · {pinnedLabel.meta}</span>
              <button type="button" onClick={clearPin} style={{ all: 'unset', marginLeft: 'auto', color: NAVY, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', flexShrink: 0 }}>show all</button>
            </div>
          )}
          {/* Name the right-hand columns. Three bare numbers per row are noise until you know what
              they are, and a hover tooltip does not teach — it answers, once, to whoever thinks to
              ask. Naming the lenses here states the framework on a surface the reader is already
              scanning, which is the same reason the row carries them at all. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 18px 4px 0',
            borderBottom: '1px solid #e7edf1', background: '#fff', fontSize: 11, fontWeight: 700, color: '#5b7683' }}>
            <span style={{ flex: 1 }} />
            {listW >= 700 && <span style={{ flexShrink: 0, width: 34, textAlign: 'right' }}>pages</span>}
            {listW >= 570 && LENSES.map(L => (
              <span key={L.key} {...tip(`${L.label} — ${L.axes.length} axes`)}
                style={{ flexShrink: 0, width: 26, textAlign: 'center' }}>{L.label[0]}</span>
            ))}
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: '#f8fafc' }}>
            {movieNode && (
              <button type="button" onClick={() => openNode(movieNode)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: 'calc(100% - 16px)', margin: '8px 8px 0',
                  border: 'none', borderRadius: 4, background: softFill(movieNode.color), color: textOn(softFill(movieNode.color)),
                  padding: '9px 12px', cursor: 'pointer', textAlign: 'left', fontWeight: 800, fontSize: 13,
                  outline: selectedId === movieNode.id ? `3px solid ${NAVY}` : 'none', outlineOffset: -2 }}>
                <span style={{ flex: 1 }}>Whole script</span>
                <span style={{ fontWeight: 700, opacity: .85 }}>›</span>
              </button>
            )}
            {/* Measured, not assumed. At the side-by-side width (490px) the three lens chips left
                the title 234px and truncated 12 of 61 of them — a fifth of the scene names, in the
                view whose entire purpose is showing scene names. So the thresholds sit ABOVE the
                column's own 560px cap: side-by-side gets titles only, stacked (~1,400px) gets
                everything. That is not a loss — side-by-side has the full lens spotlight open
                beside it, so per-row chips were repeating what the pane already says. */}
            {verticalMap({
              rowH: (sc) => Math.max(30, (+(sc.detailedContent?.page_count) || 1) * 30),
              stickyTop: 0, pad: '8px 8px 24px', onOpen: openNode,
              rich: listW >= 700 ? 2 : (listW >= 570 ? 1 : 0),
              markSelected: true,
            })}
          </div>
        </div>
      )}

      {/* TIMELINE — full width, stays fixed while the content below scrolls */}
      {view === 'timeline' && (
      <div ref={leftRef} id="ic-map" className={zooming ? 'ic-zooming' : ''} style={{ padding: '1rem', flexShrink: 0 }}>
        <div style={{ position: 'relative', paddingBottom: 10 }}>
          {rows.map(row => (
            <div key={row.key} className="d-flex align-items-stretch" style={{ gap: 0, marginBottom: 6 }}>
              {/* Layer names as TABS joined to their row (owner, 2026-10-14: the pale grey labels read as
                  decoration, not as the key to what each row is). Same height as the row, flush against it,
                  and darkened while you hover a unit in that row — so the name and the row read as one thing. */}
              {(() => {
                const on = hover?.node?.nodeType === row.key;
                return (
                  <div style={{ width: LABEL_W, flexShrink: 0, height: row.h, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    padding: '0 8px', borderRadius: '5px 0 0 5px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                    background: on ? NAVY : '#dfe7ec', color: on ? '#fff' : '#0f172a', transition: 'background .12s ease, color .12s ease' }}>
                    {row.label}
                  </div>
                );
              })()}
              {/* overflow hidden so the un-zoomed content, momentarily scaled past the frame,
                  cannot spill over the row label gutter mid-tween. */}
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: colTemplate }}>
                {/* SLIVER FILLERS: below a squeezed act/sequence, its sequences/scenes collapse into one
                    plain block per row in the act's colour, so the sliver reads as one column. */}
                {(() => {
                  const heads = [];
                  row.cells.forEach(c => { const h = groupOf[c.id]; if (h && h !== c.id && !heads.includes(h)) heads.push(h); });
                  return heads.map(h => {
                    const g = byId.get(h);
                    if (!g) return null;
                    return (
                      <button key={`fill-${h}`} type="button" tabIndex={-1} aria-hidden className="ic-cell ic-sliver"
                        onClick={() => sliverClick(g)} {...tip(`Zoom in on ${g.formattedLabel || g.label}`)}
                        style={{ gridColumn: `${startCol[h] || 1} / span ${span[h] || 1}`, gridRow: 1, height: row.h, padding: 0,
                          background: softFill(g.color), opacity: 0.5, border: 'none',
                          borderTop: '1px solid rgba(15,23,42,.1)', borderBottom: '1px solid rgba(15,23,42,.1)' }} />
                    );
                  });
                })()}
                {row.cells.map(cell => {
                  const head = groupOf[cell.id];
                  if (head && head !== cell.id) return null;   // drawn as part of its sliver's filler
                  const sliver = head === cell.id;
                  const de = metric === 'de' ? deFill(cell.detailedContent) : null;
                  const baseFill = de ? s10(cell.detailedContent.execution) : softFill(cell.color);
                  const onEnter = (e) => { const r = e.currentTarget.getBoundingClientRect(); setHover({ node: cell, x: r.left + r.width / 2, y: r.top, yb: r.bottom }); };
                  const cellStyle = {
                    // gridRow 1 explicitly: with the sliver fillers in the same grid, auto-placement
                    // could otherwise push cells onto a second line.
                    gridColumn: `${startCol[cell.id] || 1} / span ${span[cell.id] || 1}`, gridRow: 1, height: row.h,
                    background: de || softFill(cell.color),
                    borderTop: '1px solid rgba(15,23,42,.1)',
                    borderBottom: '1px solid rgba(15,23,42,.1)',
                    borderLeft: 'none', borderRight: 'none',   // kill the <button> UA default 2px outset side-borders
                                                               // (those were the stray grey/black vertical lines between scenes)
                    padding: '0 2px', overflow: 'hidden', whiteSpace: 'nowrap',
                    fontSize: row.key === 'movie' ? 14 : (row.key === 'act' ? 13 : 11), fontWeight: 700,
                    color: textOn(baseFill), textAlign: 'center',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    // when an Axis-Inspector issue is pinned, light its footprint and dim the rest;
                    // otherwise fall back to the hover ancestor/descendant relation.
                    // Pinned: the lit units keep their colour and get an outline in the pin colour; the rest fade only
                    // part-way. (Fading to 0.13 read as the legend's "Not fully read" grey — owner, 2026-10-08.)
                    opacity: pinned ? (pinned.has(cell.id) ? 1 : 0.38) : (related(cell) ? 1 : 0.18),
                    ...(pinned && pinned.has(cell.id) && selectedId !== cell.id ? {
                      outline: `2.5px solid ${pinColor}`, outlineOffset: -2, position: 'relative', zIndex: 3,
                      ...(pinFlash ? { animation: 'icPinPulse 1.4s ease 1' } : {}),
                    } : {}),
                    // Selected state: the cell only bolds its label. The visible marker (frame + column
                    // band + notch) is drawn on the boundary overlay below. ⚠️ It used to be an outline
                    // + scaleY(1.45) + shadow HERE, but this row's wrapper is overflow:hidden (for the
                    // zoom tween), which clipped the ring's top/bottom, the growth and the shadow; the
                    // surviving side edges merged with the black scene rules. Owner, 2026-10-02: "it's
                    // not obvious what unit is selected".
                    ...(selectedId === cell.id ? { fontWeight: 800 } : {}),
                    transition: 'transform .1s ease, box-shadow .1s ease, opacity .12s ease',
                  };
                  // ACT and SEQUENCE cells zoom. Every cell is now a real <button>: the act row used
                  // to be a <div role="button" tabIndex={0}> purely so the ⤢ could nest inside it,
                  // and it carried NO onKeyDown — so it announced as a button, took focus, and did
                  // nothing on Enter or Space, on the one row where that mattered most. Deleting the
                  // glyph deletes that whole problem; <button> gives Enter/Space back for free.
                  const zoomable = canZoom(cell);
                  const isFocused = cell.id === focusAct;
                  // A squeezed unit: its name on its side, and a click zooms to it (swap the zoom).
                  if (sliver) {
                    const full = cell.formattedLabel || cell.label || '';
                    const sl = cell.nodeType === 'act' ? (full.length <= 7 ? full : (unitDisplayLabel(cell, 'compact') || full))
                      : cell.nodeType === 'sequence' ? seqLabelShort(cell) : `${cell.detailedContent?.number ?? ''}`;
                    const st = zoomable ? `Zoom in on ${full}` : `Open ${full}`;
                    return (
                      <button key={cell.id} type="button" className={`ic-cell${zoomable ? ' ic-sliver' : ''}`} aria-label={st} {...tip(st)}
                        onClick={() => sliverClick(cell)} onMouseEnter={onEnter} onMouseLeave={() => setHover(null)}
                        style={{ ...cellStyle, padding: 0, fontSize: 10.5 }}>
                        <span className="ic-celllabel" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', lineHeight: 1 }}>{sl}</span>
                      </button>
                    );
                  }
                  // CLICK OPENS, always (owner, 2026-10-14). Zoom is the magnifier pill: "Zoom in" on
                  // hover, and on the zoomed unit a permanent "Zoom out" in the same spot.
                  const isAncestor = !isFocused && focusAnc.has(cell.id);
                  const nm = cell.nodeType === 'sequence' ? seqLabelFull(cell) : (cell.formattedLabel || cell.label);
                  const zoomTip = !zoomable ? null
                    : isFocused ? `Open ${nm} · Zoom out (or Esc) shows the whole script again`
                      : `Open ${nm} · Zoom in widens it on the map`;
                  // The ⤢ is a CUE, not the target — the whole cell is. It is always visible (at
                  // 55%), never opacity:0, and `pointer-events:none` so it can never swallow a
                  // click the way its 20x15px ancestor did. Only drawn when the cell is wide
                  // enough that it isn't competing with the label for the last few pixels.
                  // At rest the magnifier shows only where it can't crowd the label; on narrower
                  // cells it appears on hover.
                  const cw = cellWidth(cell);
                  // Every act gets it, however short (a 3-page Act 3 is exactly the one you want to
                  // enlarge). On a narrow act the pill floats over its neighbours on hover — the cell
                  // lets it overflow while the label clips itself.
                  const showHint = zoomable && !isFocused && !isAncestor;
                  const hintAtRest = cw >= 120;
                  // A right-anchored pill grows LEFTWARD; on a narrow block near the map's left edge that runs
                  // off the grid and is clipped by the row wrapper (Codex Z3). Anchor those on the left.
                  const c0 = (startCol[cell.id] || 1) - 1;
                  const leftPx = ((cumPages[c0] ?? 0) / totalPages) * gridW;
                  const pillLeft = cw < 120 && leftPx < 96;
                  const showOut = zoomable && isFocused;
                  return (
                    <button key={cell.id} type="button" className={`ic-cell${pulsedIds.has(cell.id) ? ' ic-pulsing' : ''}`}
                      aria-label={zoomable ? zoomTip : undefined}
                      {...(zoomable ? tip(zoomTip) : {})}
                      onClick={() => openNode(cell)}
                      onMouseEnter={onEnter} onMouseLeave={() => setHover(null)}
                      style={{ ...cellStyle, position: 'relative', ...((showHint || showOut) ? { overflow: 'visible' } : {}) }}>
                      {/* wrapped so the zoom tween can fade it: a scaleX'd text node is a
                          distorted text node, and there is no way to counter-scale a value that is
                          itself animating. Out for the tween, back in crisp at the new layout. */}
                      <span className="ic-celllabel" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>{cellLabel(cell)}</span>
                      {/* You have a conversation about this unit: a small teal dot (the Ask AI colour) in the
                          top-left corner — the zoom pill owns the top-right. Click opens the reading as always,
                          and its button then says "Continue chat". */}
                      {(() => { const d = cell.detailedContent; const u = d && (d.unitId || d.unit_id || d.id); return u && chattedUnits.has(String(u)); })() && (
                        <span aria-hidden title="You have a conversation here" className="ic-chatdot"
                          style={{ position: 'absolute', top: 3, left: 3, width: 9, height: 9, borderRadius: 999, background: ASK_TEAL,
                            boxShadow: '0 0 0 1.5px #fff', pointerEvents: 'none' }} />
                      )}
                      {/* Mouse shortcut only (a span: a <button> cannot nest in the cell's <button>). The keyboard
                          path is the reading's "See this act's scenes" button. */}
                      {showHint && <span aria-hidden className={`ic-zoomhint${hintAtRest ? '' : ' ic-zoomhint--hover'}${pillLeft ? ' ic-zoomhint--left' : ''}`}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); enterFocus(cell.id, 'cell_pill'); }}>
                        <MagIcon /><span className="ic-zoomhint__txt">Zoom in</span></span>}
                      {showOut && <span aria-hidden className="ic-zoomhint ic-zoomhint--out"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); exitFocus('cell_pill'); }}>
                        <MagIcon minus /><span className="ic-zoomhint__txt">Zoom out</span></span>}
                    </button>
                  );
                })}
              </div>
              </div>
            </div>
          ))}
          {/* per-row ▸ patterns buttons removed (owner, 2026-07-07): the rail's Patterns entry +
              RollupPanel's own grain switcher cover them, and the timeline gets the width back. */}
          {/* ONE boundary overlay, exactly over the grid content box (LABEL_W on the left;
              flush right now that the per-row patterns buttons are gone). Every line is positioned by
              scene fraction in the same coordinate space as the grid columns, so they align perfectly;
              each is centred on its boundary and uniform width. Level = how far DOWN it runs. */}
          <div ref={railRef} style={{ position: 'absolute', top: 0, bottom: 0, left: LABEL_W, right: 0, pointerEvents: 'none', zIndex: 4, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0 }}>
            {boundaryLines.map(({ k, level, pos }) => {
              const top = rowTop[level] || 0;
              // snap to whole pixels so a 2px line never straddles a pixel (which makes it blur/look
              // thinner in the white gaps between rows); fall back to % until the rail width is measured.
              const leftStyle = railW ? { left: Math.round(pos * railW) - 1 } : { left: `${pos * 100}%`, marginLeft: -1 };
              return (
                <div key={k} style={{
                  position: 'absolute', top, height: bottomY - top,   // runs down to the scenes-row bottom
                  ...leftStyle,
                  width: level === 'scene' ? 1 : 2,                    // scene = 1px, structural = 2px
                  background: INK,                                     // all separators solid black — no grey bevel
                }} />
              );
            })}
            {/* SELECTED UNIT — three cues that read at a glance without changing the cell's colour (the
                fill IS the verdict): a navy frame with a white keyline so it holds on any fill; a light
                band through the rows above, so you see which sequence/act holds it; and a notch under
                the timeline pointing down at the reading pane. Hidden mid-zoom (it would scaleX). */}
            {selFrame && !zooming && (
              <>
                {selFrame.top > 0 && (
                  <div style={{ position: 'absolute', top: 0, height: selFrame.top - 3, left: selFrame.left, width: selFrame.width,
                    background: 'rgba(20,83,110,.16)', borderLeft: `1px solid ${NAVY}55`, borderRight: `1px solid ${NAVY}55` }} />
                )}
                <div style={{ position: 'absolute', top: Math.max(0, selFrame.top - 3), height: selFrame.h + 6 - (selFrame.top < 3 ? 3 - selFrame.top : 0),
                  left: Math.max(0, selFrame.left - 3), width: selFrame.width + 6,
                  border: `3px solid ${NAVY}`, borderRadius: 4,
                  boxShadow: 'inset 0 0 0 1.5px #fff, 0 0 0 1.5px #fff, 0 3px 12px rgba(15,23,42,.35)' }} />
                <div style={{ position: 'absolute', top: bottomY + 3, left: selFrame.left + selFrame.width / 2 - 7, width: 0, height: 0,
                  borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: `7px solid ${NAVY}` }} />
              </>
            )}
          </div>
          </div>
        </div>
        {/* (The pinned caption that sat here moved up to the active-filter chip beside Colour by — owner, 2026-10-08.) */}
      </div>
      )}

      {/* TIMELINE detail — below the (short) map, which stays fixed; only this scrolls. The Axis-Inspector
          chat docks as a THIRD pane on the right (the inspector stays fully visible — you chat about the
          pattern while looking at it). Closed → a launcher bottom-right. */}
      {view !== 'table' && (
        <div ref={paneRowRef} style={{ flex: shortView ? '0 0 auto' : 1, minWidth: 0, minHeight: shortView ? undefined : 0, display: 'flex', borderTop: sideBySide ? 'none' : '1px solid #e2e8f0', position: 'relative' }}>
          {/* ⚠️ RESERVE THE OVERLAY CHAT'S WIDTH (owner, 2026-08-24: "there's a gap between chat and
              left pane and it looks bad… take the tour goes too far right"). Both complaints were
              the same cause: in overlay mode the chat is `position:absolute; right:0` inside this
              row, so it sits ON TOP of this column and everything under it simply disappears.
              Measured at a 860px row: chat 440–860, the Take-a-tour pill 720–838 and the pane card
              20–844 — the pill fully hidden, the card's right edge hidden, and the sliver of grey
              still showing beside the chat reading as a broken gutter rather than a seam.
              Padding here makes the tabs row and the card END where the chat begins. Docked mode
              needs nothing: the chat is in flow and takes its own space. */}
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column',
            paddingRight: (chatOpen && !chatDockable) ? chatOverlayW : 0,
            transition: 'padding-right .12s ease' }}>
            {modeTabs}
            {/* shortView (landscape phone): flow in the page so the reading isn't trapped under the map. */}
            <div ref={contentRef} style={{ flex: shortView ? '0 0 auto' : 1, minHeight: 0, overflowY: shortView ? 'visible' : 'auto', background: '#f5f7f9' }}>
              {/* left-anchored (NOT centered): constrained text measure belongs under the tabs that
                  opened it — the freed right-hand space is the chat's, not dead margin. */}
              <div ref={cardRef} id="ic-pane" onMouseDownCapture={() => setActivePanel('pane')}
                /* Right margin drops to 0 while the chat is DOCKED: 16px of page-grey between two
                   white panels reads as a mistake, where the chat's own left border reads as a
                   seam. Kept when the chat is closed or overlaying, so the card is not flush
                   against nothing. */
                style={{ margin: `1.2rem ${(chatOpen && chatDockable) ? 0 : '1rem'} 1.2rem 1.25rem`, background: '#fff', border: '1px solid #e7edf1',
                  borderTop: `3px solid ${activePanel === 'pane' ? NAVY : '#e7edf1'}`,
                  // overflow CLIP, not hidden: hidden makes the card a scroll container, which silently
                  // breaks every position:sticky inside it (the Patterns / reading "On this page" TOC).
                  // clip still rounds the corners; flow-root keeps the margin containment hidden gave.
                  borderRadius: 12, boxShadow: '0 1px 3px rgba(15,23,42,.06)', overflow: 'clip', display: 'flow-root' }}>{pane}{paneThumbs}</div>
            </div>
          </div>
          {/* AxisChat panel + launcher — suppressed in publicView (LP demo).
              The chat covers ~half the widget when open and clutters the corner
              when closed; both wrong for a first-impression marketing surface. */}
          {/* ⚠️ `(!publicView || !embedded)`, not `!publicView`. THIRD instance of the same
              conflation (after the Timeline/Table toggle and `modeTabs`): `publicView` is set for any
              anonymous/showcase/shared script, so the full-screen page lost its chat entirely — the
              symptom being that chat worked in Table view (that branch has no such guard) but not in
              Timeline. The suppression is for the EMBEDDED demo, where the chat covers half the widget.
              See the audit rule in CODEBASE_MAP: a bare `publicView` gate on in-app chrome is a bug. */}
          {(!publicView || !embedded) && chatOpen && <AxisChat heading={pageChatHeading} movieHash={movie.hash} subject={effectiveSubject} seed={chatSeed} starters={chatStarters} store={chatStore} onSeedConsumed={() => setChatSeed(null)} onSend={noteAxisSend} conversations={chatList} onOpenConversation={openConversation} dockWidth={chatDockW} overlay={!chatDockable}
            active={activePanel === 'chat'} onInteract={() => setActivePanel('chat')}
            onClose={() => setChatOpenPersist(false, 'close')} />}
          {/* The launcher sits in the tab row now (modeTabs), not floating here. */}
        </div>
      )}

      </div>

      {/* TABLE — the grid gets the FULL width and the reading opens OVER it (owner, 2026-08-21).
          It used to be master/detail with a permanently-open right drawer at clamp(620px, 55%, 880px).
          That drawer was present even with nothing selected, so the grid never had more than ~45% of
          the window: the sticky unit column collapsed to a few pixels and the row labels — the scene
          number and title, i.e. the only way to tell WHICH row you are reading — were invisible.
          A table with 18 score columns needs the width more than a persistent empty drawer does.

          The overlay keeps `drawerRef` on its scroll container, because that ref is what the
          detail-open effect resets to the top, and NodePanel scrolls ITSELF to a clicked axis inside
          its nearest scroller — which must therefore be this element. */}
      {view === 'table' && (
        <div style={{ flex: 1, minHeight: 0, position: 'relative', borderTop: '1px solid #e2e8f0' }}>
          {/* bottom padding: the floating Ask AI pill must never sit on the last rows */}
          <div style={{ position: 'absolute', inset: 0, overflow: 'auto', background: '#f5f7f9', padding: '1rem 1rem 5.5rem' }}>
            <TableView nodes={tableNodes} onSelectAxis={openAxis} onSelectAxisHeader={openAxisInspector} selectedId={selectedId} scoreScale={SCORE_SCALE} script={movie.movie}
              isChatted={(n) => { const d = n && n.detailedContent; const u = d && (d.unitId || d.unit_id || d.id); return !!(u && chattedUnits.has(String(u))); }} />
          </div>

          {tableDetail && (
            <>
              {/* Scrim: click anywhere off the panel to get the whole table back. */}
              {/* z-index 10/11 deliberately UNDER AxisChat's own 20: the chat is summoned FROM this
                  overlay ("ask about this question"), so it has to dock on top of it, not behind. */}
              <div onClick={closeTableDetail}
                style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,.28)', zIndex: 10 }} />
              <div role="dialog" aria-modal="true" aria-label="Unit reading"
                style={{ position: 'absolute', inset: '16px', zIndex: 11, display: 'flex', justifyContent: 'center',
                  pointerEvents: 'none' }}>
                <div ref={drawerRef} onMouseDownCapture={() => setActivePanel('pane')}
                  style={{ pointerEvents: 'auto', width: 'min(1100px, 100%)', maxHeight: '100%', overflowY: 'auto',
                    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                    borderTop: `3px solid ${activePanel === 'pane' ? NAVY : '#e2e8f0'}`,
                    boxShadow: '0 18px 50px rgba(15,23,42,.24)', position: 'relative' }}>
                  <button type="button" onClick={closeTableDetail} aria-label="Close and show the full table"
                    style={{ position: 'sticky', top: 8, float: 'right', marginRight: 10, zIndex: 3,
                      background: '#fff', border: '1px solid #dbe3ea', borderRadius: 8, color: NAVY,
                      fontWeight: 700, fontSize: 13, padding: '3px 10px', cursor: 'pointer' }}>✕ Esc</button>
                  {/* Script-level panels need their switcher INSIDE the overlay — the table view has
                      no standing pane to host it. Not shown for a unit reading, which has its own
                      instrument switcher on the unit header. */}
                  {right?.type !== 'node' && modeTabs}
                  {pane}{paneThumbs}
                </div>
              </div>
            </>
          )}

          {/* `overlay` is REQUIRED here now. This container used to be display:flex, so an in-flow
              chat docked beside the grid; it is position:relative since the table went full width,
              and an in-flow chat would drop below the fold instead of docking. */}
          {/* above the Findings & patterns overlay (z 10/11) too, so the script chat is reachable from it */}
          {!chatOpen && !unitModal && renderChatLauncher({ z: tableDetail ? 12 : 10 })}
          {chatOpen && <AxisChat heading={pageChatHeading} movieHash={movie.hash} subject={effectiveSubject} seed={chatSeed} starters={chatStarters} store={chatStore} onSeedConsumed={() => setChatSeed(null)} onSend={noteAxisSend} conversations={chatList} onOpenConversation={openConversation} overlay
            active={activePanel === 'chat'} onInteract={() => setActivePanel('chat')}
            onClose={() => setChatOpenPersist(false, 'close')} />}
        </div>
      )}

      <WikiDrawer article={wikiArt} onClose={() => setWikiArt(null)} onNavigate={setWikiArt} />

      {/* Floating tooltip — the exact SoS2 hover (white, band + lens statuses + biggest lever).
          ⚠️ TWO THINGS HERE ARE LOAD-BEARING; BOTH FIX THE SAME BUG (owner screenshot, 2026-09-04:
          the Act 3 hover rendered as a ~90px column, one word per line).
          1. `width: max-content`. A `position: fixed` box with only `left` set is SHRINK-TO-FIT, and
             its available width is `viewportWidth - left`. Hovering the last act put `left` at
             ~1860 on a 1920 viewport, so the box was offered ~60px and wrapped every word.
             `translateX(-50%)` cannot rescue it: transforms are applied AFTER layout, so the width
             was already decided. `max-content` makes the box size to its text regardless of where
             it sits, and `maxWidth` still caps it.
          2. The clamp. Once it is full width it would simply hang off the screen edge instead, so
             the centre is clamped to keep it fully visible. That needs the RENDERED width, which is
             only knowable after layout — hence the measure below. `useLayoutEffect` runs before
             paint, so the corrected position is the first thing drawn; there is no flash. Until the
             first measure `tipW` is 0 and the clamp is a no-op, which is the old centred behaviour. */}
      {hover && (() => {
        const baseHtml = createCustomData(hover.node, movie, metric, true);
        if (!baseHtml) return null;
        // A unit with a conversation says how much is in it (owner, 2026-10-14) — same teal as its map dot.
        const nQ = unitChatCount(hover.node.detailedContent);
        const html = nQ > 0
          ? `${baseHtml}<div style="margin-top:5px;padding-top:5px;border-top:1px solid #e2e8f0;color:#0f766e;font-weight:700;display:flex;align-items:center;gap:6px"><span style="width:9px;height:9px;border-radius:999px;background:${ASK_TEAL};display:inline-block"></span>Your chat here: ${nQ} question${nQ === 1 ? '' : 's'} asked</div>`
          : baseHtml;
        // Scenes (the bottom row): the card opens BELOW the cell (owner, 2026-10-24), so it never covers
        // the act and sequence rows the eye reads it against. Flips above only when there is no room
        // under it. Other rows keep the old rule: below only near the top of the screen.
        const isScene = (hover.node?.nodeType || hover.node?.detailedContent?.type) === 'scene';
        const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
        const roomBelow = !vh || hover.yb + 8 + (tipH || 160) < vh;
        const below = (isScene && roomBelow) || hover.y < 220;
        const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
        const half = tipW / 2;
        const left = (vw && tipW)
          ? Math.min(Math.max(hover.x, half + 8), vw - half - 8)
          : hover.x;
        return (
          <div ref={tipRef}
               style={{ position: 'fixed', left, top: below ? hover.yb + 8 : hover.y - 8,
                        transform: below ? 'translate(-50%,0)' : 'translate(-50%,-100%)',
                        background: '#fdfdfb', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 11px',
                        borderRadius: 8, fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'normal',
                        width: 'max-content', maxWidth: 'min(340px, calc(100vw - 24px))',
                        pointerEvents: 'none', zIndex: 2000, boxShadow: '0 6px 20px rgba(15,23,42,.22)' }}
               dangerouslySetInnerHTML={{ __html: html }} />
        );
      })()}
      <BetaFeedback trigger={false} movieHash={movie.hash} movieTitle={movie.movie?.title || null} />
      <HowItWorksVideo open={howVideo} onClose={() => setHowVideo(false)} surface={publicView ? 'full-demo' : embedded ? 'full-lrtab' : 'full-icicle'} />
      {unitModal && typeof document !== 'undefined' && createPortal((() => {
        const dc = right.node || {};
        const sIdx = dc.type === 'scene' ? mOrdered.findIndex(n => n.detailedContent?.number === dc.number) : -1;
        const step = (d) => { const nx = mOrdered[sIdx + d]; if (nx && nx.detailedContent) { track('ic_unit_open', { grain: 'scene', unit: nx.detailedContent.number, from: 'modal_prevnext' }); openNode(nx); } };
        const navBtn = (dis) => ({ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, color: dis ? '#cbd5e1' : NAVY, fontWeight: 700, padding: '2px 11px', fontSize: 15, cursor: dis ? 'default' : 'pointer' });
        return (
          <div role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) closeUnitModal('backdrop'); }}
            style={{ position: 'fixed', inset: 0, zIndex: 1040, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3vh 16px' }}>
            <div ref={unitDialogRef} tabIndex={-1} onKeyDown={trapTab} role="dialog" aria-modal="true" aria-label={unitDisplayLabel(dc, 'list') || dc.title || 'Reading'}
              style={{ background: '#fff', borderRadius: 12, width: 'min(1200px, 96vw)', height: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(15,23,42,.35)', overflow: 'hidden', outline: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.7rem 1rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                {renderUnitHistory()}
                <span aria-hidden="true" style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0', margin: '2px 4px' }} />
                <div style={{ fontWeight: 700, color: NAVY, fontSize: '1.02rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                  {unitDisplayLabel(dc, 'list') || dc.title || 'Reading'}
                </div>
                {sIdx >= 0 && (
                  <>
                    {/* Labelled with the scene they go to, so they read differently from Back / Forward on the left. */}
                    <button type="button" aria-label="Previous scene" disabled={sIdx <= 0} onClick={() => step(-1)} style={{ ...navBtn(sIdx <= 0), fontSize: 13 }}>‹ {sIdx > 0 ? `Scene ${mOrdered[sIdx - 1]?.detailedContent?.number ?? ''}` : 'Previous'}</button>
                    <button type="button" aria-label="Next scene" disabled={sIdx >= mOrdered.length - 1} onClick={() => step(1)} style={{ ...navBtn(sIdx >= mOrdered.length - 1), fontSize: 13 }}>{sIdx < mOrdered.length - 1 ? `Scene ${mOrdered[sIdx + 1]?.detailedContent?.number ?? ''}` : 'Next'} ›</button>
                  </>
                )}
                <button type="button" aria-label="Close" onClick={() => closeUnitModal('button')}
                  style={{ all: 'unset', cursor: 'pointer', color: '#64748b', fontSize: 22, lineHeight: 1, padding: '0 6px' }}>✕</button>
              </div>
              {/* Reading above, chat dock below. Expanded, the dock SHRINKS the reading (it never covers
                  it). In a short window the two cannot share the height, so the dock becomes a
                  Chat | Reading switch: chat takes the body and the reading stays mounted at zero
                  height, so its scroll position is still there when the writer switches back. */}
              {/* Owner, 2026-10-14: "the chat is still too small vertically". On a wide window the open
                  chat sits BESIDE the reading at the modal's full height; stacked (narrower windows) it
                  takes most of the height and the reading keeps a strip above it. */}
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: (dockOpen && !shortView && viewportW >= 1100) ? 'row' : 'column', position: 'relative' }}>
                {dockButton(dc, { float: true })}
                {/* paddingBottom: the floating Ask AI pill must never sit on the reading's last lines */}
                <div onScroll={onReadingScroll} style={{ flex: (dockOpen && shortView) ? '0 0 0px' : '1 1 0', minWidth: 0, minHeight: (dockOpen && !shortView) ? 140 : 0, overflowY: 'auto', paddingBottom: dockOpen ? 0 : 72, visibility: (dockOpen && shortView) ? 'hidden' : 'visible' }}>
                  {backBar}
                  <NodePanel node={dc} peers={peersFor(dc.type === 'movie' ? 'movie' : dc.type)} movieHash={movie.hash} movieId={null} script={movie.movie} focusBlock="start" layout="twocol" measure toc
                    onDiscussAxis={askInDock}
                    onFocusAct={(view !== 'timeline' || !canZoom(byId.get(dc.id)) || dc.id === focusAct) ? null : ((id) => { closeUnitModal('zoom_in'); enterFocus(id, 'reading'); })} />
                </div>
                <div style={{ flex: dockOpen ? (shortView ? '1 1 auto' : (viewportW >= 1100 ? '0 0 min(480px, 42%)' : '0 0 68%')) : '0 0 auto', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  {renderUnitDock(dc, { cramped: shortView, beside: dockOpen && !shortView && viewportW >= 1100 })}
                </div>
              </div>
            </div>
          </div>
        );
      })(), document.body)}
    </div>
  );
}
Icicle.propTypes = { movie: PropTypes.object, embedded: PropTypes.bool, publicView: PropTypes.bool, initialMetric: PropTypes.string, initialSelection: PropTypes.object, initialTarget: PropTypes.string, listenNavigate: PropTypes.bool };
